/* =====================================================================
   THE ROOM ENGINE — E6: THE DAY, AND THE CARD IT IS WRITTEN ON

   Tommy, 2026-09-13, on v32's day panel: the black ring-and-dot point
   "doesn't fit the theme", and the ruled panel in the margin is "just a
   ruled bit of paper — it should be a physical panel, a physical element
   in the room that perhaps intersects with the map or sits over it".

   So there is no panel and no ring. There is A TYPED INDEX CARD lying
   askew over the plate's south-east corner — a real object in the scene,
   with its own stock, its own edge and its own shadow struck from the
   same sun as the books — and the day's particulars are TYPED on it, in
   the sheet's own typewriter face, as DOM over the canvas so the type
   stays crisp and selectable (the Google-Maps split). The card holds its
   last day 600 ms after the pointer leaves it, and the essays open ONLY
   from the card's own control.

   ==== N2: THERE IS NO LOCK ON THE DAY ==============================
   E6 let a click drive a DRAWING PIN through the card and hold one day
   on it; R24 made that pin a real object (a head in the card's own
   units, its shadow, a corner riding up the shaft, a marking pin in the
   cell it named, a red thread between the two behind `?thread=1`) and
   left the day riding in the hash as `,d<date>`. Tommy, 2026-09-20:
   "i've never loved the red pin for locking a date in ... what if you
   just pan about, it stays locked? how then would you see the voyage and
   singularity content?"

   So the lock is gone, and the whole of that machinery with it: the pin,
   its shadow, its lift, the thread, the marking pin on the plate, the
   program the pencil cross was drawn by (the cross itself went at R24),
   `Room.pin` / `Room.pinned`, the terminal's `day` word, and the `,d`
   the hash used to write.

   THE CARD FOLLOWS THE POINTER, and nothing else. It holds its day 600
   ms after the pointer leaves a cell — which is the corridor from a day
   to the card's own control — and it holds it for as long as the hand is
   ON the card, so the reach for "read the post" never loses the card
   under the hand. A typed link may still carry `,d<date>`: that is the
   author's day, shown until the reader's first move over the map, and
   the hash never writes one back.

   NOTHING IS SIGNPOSTED. There is no readout and no chrome: the card is
   never empty (at rest it carries the next thing to find), so it teaches
   what it is by being what it is.

   THE PICK (ROOM-PLAN §3, E1 §3) — no readback, no pick pass.
     · at tilt 0 the ground is flat (reliefOf(0) is 0) and the whole
       chain screen -> sheet -> map is ONE affine inverse: arithmetic.
     · tilted, the same ray is marched against the FIELD'S OWN LATTICE,
       in the map's own rigid-body frame, and bisected. The field is
       bilinear and cannot hide a crossing inside a cell.
     · the day is then the one whose OWN CELL the map point falls in —
       and the plough turns every other column, so the row off the axis
       is not the day (the v32 A fault: it named the day reversed about
       the middle of the week on every turned column).

   Switches: `?day=0` nothing at all · `?card=0` the card's mesh and type
   off (the pick stays) · `?picksteps=N` the march's step count, for the
   accuracy measurement · `?daygrid=0` no cell ruling.
   ===================================================================== */
(function (root) {
'use strict';

var Q = new URLSearchParams(location.search);
var CARD_REST_CLEAR = Q.get('cardrest') === 'clear';   /* LK10 */
var R7 = '#define R7 1\n';
/* how far the card's corners stand off the paper, in sheet units, in the
   card's own uv: (0,0) head-west, (1,0) head-east, (0,1) foot-west,
   (1,1) foot-east — which is the corner away from the plate, and the one
   a card that has been carried actually lifts. */
var CARD_LIFT = [0.0, 0.35, 0.55, 2.6];
var CARD_STOCK_Z = 1.4;       // the card face above its supporting surface
var DESK_CARD_PX = 0.5;       // visible stock clears the oak by half a css pixel
var HOLD_MS = 600;                 // v32 A: "yes to short hold"
var PHONE = /(^|\s)phone(\s|$)/.test((typeof document !== "undefined" && document.documentElement ? document.documentElement.className : ""));
var CARD_TILT = -3.1 * Math.PI / 180;   // askew, and only just
/* the card's width in SHEET UNITS (E9): 346 css px at the 1920 x 1080
   layout, where its foot was measured clear of the sheet's marginalia */
var CARD_W = 448.9;
var DAY_MS = 86400000;

/* ==== CF1 (2026-09-21): THE CARD AND THE PAPER UNDER IT ==============
   Tommy, on the motion R47 wrote: "When you fold the paper, the file card
   ends up on the desk, and I really like the way it does that! The way it
   moves is really slick, and I like where it ends up! But its interaction
   with the paper during the folding process is just not realistic. Of
   course, you kind of move the paper out from underneath it in a way that
   just could never happen."

   He is right and it is the one line R47 did not write down: the card
   slides WEST to the crease while the very paper it is lying on is
   rising EAST of the crease. The paper is pulled out from under it.

   Two ways out were built behind `?card=`, and he picked.

     slip   the card RIDES the leaf it is lying on, on the leaf's own
            angle, until the leaf is too steep to hold it — and then it
            slips down the face, off the foot, and lands on the oak just
            east of the crease. Nothing is ever pulled out from under it.
     north  CF2: gone. The card was taken off the map before the map
            moved and stood on the bare oak north of the sheet.

   ==== CF2 (2026-09-21): SLIP IS THE MOTION, AND IT IS THE ONLY ONE ====
   Tommy, on the two: "I love Slip, whereas North sucks!"

   So the switch is gone with the loser. THE SLIP IS WHAT THE FOLD DOES,
   with no query at all: `?card=slip` and `?card=north` are nothing now,
   and `?card=0` — which is older than CF1 and never was a variant — is
   still the card's mesh and type off. Deleted outright and by name:
   `foldNorth`, `lerpQuad` (the errand's blend, and nothing else used
   it), `CARD_NORTH`, `CARD_NORTH_T`, `CARD_CARRY`, `CARD_MODE`, and
   R47's own slide with `slidFrom`, which is the motion the complaint
   above is about. CF1's prose under each stays as history, marked.

   The numbers are all in SHEET UNITS. The sheet is 840 mm across
   (objects.js deskRect: `mm = sheet.w * k / 840`), so one sheet unit is
   0.355 mm and the card is 159 x 85 mm of index stock.
   ==================================================================== */
var CARD_SLIP_A0 = 55 * Math.PI / 180;  // the leaf's angle where the card lets go
/* ==== AND WHERE IT IS DOWN, WHICH IS NOT THE TEN DEGREES IT LOOKS LIKE.
   The brief asked for the slip to run over "the next ~10 degrees of the
   turn". MEASURED: `Room.foldTo` is 1100 ms with its own ease-in-out
   cubic, `foldTurns` puts its own smoothstep inside that, and the first
   turn's whole half circle is 481 ms of the 1100. Fifty-five degrees is
   reached at t = 0.122 and sixty-five at t = 0.133 — eleven
   milliseconds, which at sixty frames a second is LESS THAN ONE FRAME.
   The card would not slip; it would teleport.

   So the TRIGGER is the angle the brief names and the SPAN is what the
   eye needs: 55 to 125 degrees, which is 0.09 of the gesture, 83 ms,
   five frames. The band is symmetric about the vertical, because
   `foldTurns`' smoothstep is (55 to 90 is t 0.1224 to 0.1667 and 90 to
   125 is 0.1667 to 0.2107, to three figures the same). Nothing about
   R47's easing is touched: this reads the angle that easing makes. ==== */
var CARD_SLIP_A1 = 125 * Math.PI / 180;
var CARD_SKID = 30;                     // 10.7 mm: the skid east of the crease line
var CARD_RET_A = 15 * Math.PI / 180;    // the returning leaf's angle where it comes home
var CARD_HOP = 6;                       // 2.1 mm: it lifts off the wood by itself
/* ==== CF3 (2026-09-23): THE FLOP, THE SQUEEZE AND THE DEAL ==============
   Tommy: "I really like the idea about the card flipping! ... Yes, have
   the memorandum on the back. ... when you're folding and the card slides
   down, it should flip over as it hits the bottom, maybe." And of R58's
   unfold: "It just magically slides back into place like time is running
   backwards!"

   THE FLOP. The ride and the slide down the leaf's face are CF1's to the
   bit. Where CF1's card then TIPPED about its west edge and lay down face
   up, it keeps turning the way the tip was turning: a half turn and a bit
   (from the 76 degrees it left the foot at, through flat, to face down)
   about the axis parallel to the crease, which is the tip's own axis, so
   the spin is the momentum it already had. A card cannot turn over on the
   spot without clearing half its own width (231 units, 82 mm), and this
   one is already standing on its edge that high when it leaves the foot,
   so the centre stays where it is and rises only what the half turn
   needs, a few mm, while the skid carries it east. It lands ON ITS BACK
   short of the rest by FLOP_SKID and skids the rest, decelerating: the
   west edge CARD_SKID east of c1, the y it always had. Off the foot the
   card is not on the paper any more, so the flop runs on its own clock
   (FLOP_MS, then FLOP_SKID_MS), not on the leaf's angle: at the fold's
   own speed the leaf turns from 76 to 180 degrees in 105 ms.
     76 -> -180 degrees in 150 ms (smoothstep: it leaves the foot turning
     slowly and lands flat), the centre never under half the card's own
     extent in z plus a clearance and never nearer the crease than half
     its extent in x, then 16 units of skid in 70 ms.

   THE SQUEEZE. On the way back the last leaf floats down its last few
   degrees on the air under it (objects.js UNF_FALL), and the closing
   wedge between leaf and oak squirts the card out EAST along the wood,
   on its back and flat, the way a slick card shoots out of a closing
   book: from rest, accelerating (distance as time squared), over SQ_MS,
   the cushion's own length, and out past the window's east edge, at
   which point it is out of the picture (no stock, no type). It starts as
   the leaf passes SQ_A, and it is never slower than the least that keeps
   every corner under the leaf's face (the leaf's height over each corner
   is the corner's distance from the hinge times tan of the angle).

   THE DEAL. DEAL_WAIT after the leaf has landed, which is when the lie
   is coming back into the paper, the card comes back in from the east
   edge already in the second half of a flip (from 110 degrees through
   the vertical to flat) with its low edge skimming the sheet, touches
   down FACE UP DEAL_SKID east of its home and skids home, decelerating:
   DEAL_MS in all, 0.72 of it the flight. That is the card being put back
   on the map, and the day is on it the instant it lands, because the
   day is its face.

   WHICH FACE IS UP decides what is read (`card.faceUp`, the pose's own
   normal): the day on the front, the memorandum on the back (D.attach,
   THE MEMORANDUM). Nothing is rewritten when the paper folds. ==== */
/* ==== FU1 (2026-09-27): IT LANDS FACE UP, AND THE HAND TURNS IT OVER ====
   Tommy: "the file card aka hint card in the folded view, landing normal
   side up under the folded map (so it shows the normal map-based text)
   and then you can flip it over to find the hints".

   So the flop stops at flat: off the foot it falls back on to the oak
   face up (76 degrees to nought, the way it was already turning), skids
   the same FLOP_SKID and lies there with the day on it. A press on it
   (engine.js x2Press, D.card `turn`) turns it over on the spot, a half
   turn about the axis along its height in TURN_MS with the centre lifted
   TURN_RISE over what the turn needs; another press turns it back. The
   squeeze takes it out on whichever face it lies on, and the deal always
   brings it home face up, as before.
   FU3 (2026-09-27): "why can't we manually turn it over like the
   unfolding map?" — a drag on it turns it WITH the hand, one card's width
   of travel to the half turn, and let go it falls to the nearer face. ==== */
var TURN_MS = 260, TURN_RISE = 10;       // 3.5 mm over what the half turn needs
/* PK1: the peek's lift and its way back, and how much of the glass the
   card held up to the eye takes (width, height: whichever binds) */
var PEEK_MS = 300, PEEK_FW = 0.86, PEEK_FH = 0.78;
var FLOP_MS = 150, FLOP_SKID_MS = 70, FLOP_SKID = 16;
var FLOP_RISE = 4;                      // 1.4 mm over what the half turn needs, at the top of the arch
var SQ_A = 6 * Math.PI / 180, SQ_MS = 170;
var SQ_CLEAR = 1.5;                     // a leaf's thickness (0.9) and a little, over every corner
var DEAL_WAIT = 400, DEAL_MS = 350, DEAL_FLIGHT = 0.72, DEAL_SKID = 24;
var DEAL_PSI0 = -110 * Math.PI / 180;
/* CF2: `CARD_CARRY` (the errand's 14 mm arc), `CARD_NORTH_T` (it was over
   in the first eighth of the turn) and `CARD_NORTH` (its place on the bare
   oak, at x 1500, y -253) were the north variant's three numbers and went
   with it. CF1 measured that place twice and wrote the measurement down;
   the finding it left behind is worth keeping even though the card that
   stood there is gone: AT 1537 x 865 THE VISIBLE OAK NORTH OF THE SHEET IS
   138 SHEET UNITS — 49 mm of the 220 mm that are actually there. The
   sheet's head projects to screen y = 64 at 0.464 px a unit, and the camera
   does not open out for the fold (`Room.foldAt` holds the pose, and the
   landed standing pose measures the same to the pixel). Anything put north
   of the sheet's head is half off the top of the window. ==== */
/* where in the slip the SLIDE ends and the TIP begins, and the leaf's
   angle at that moment: 76 degrees. Not the vertical, though the vertical
   is where a face stops holding anything — a card standing at 90 degrees
   on the crease is 449 units of stock edge-on to this camera, and for
   the frame it would last that is a black line across the picture and
   not a card. 76 is steep enough to read as a card about to go. */
var SLIP_TIP = 0.30;
/* CF1: the card's shadow is a CONTACT shadow. Below SHADOW_H off the
   wood it is thrown at its own height as it always was; between there
   and SHADOW_GONE it goes, because a card in the air over a folding
   sheet has nothing to be in contact with. */
var SHADOW_H = 12, SHADOW_GONE = 70;
var SLIP_HOLD_A = CARD_SLIP_A0 + (CARD_SLIP_A1 - CARD_SLIP_A0) * SLIP_TIP;

var DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
              'August', 'September', 'October', 'November', 'December'];

/* =================================================================
   THE SHADER

   ONE program now. N2 took the pin, its shadow and its thread off the
   card, and the pencil cross had gone at R24 — so the device-pixel mark
   program that drew all four (`day-mark`, its two stages and `setMark`)
   is gone with them. What is left is the flat quad lying on the desk:
   the card, its cut edges and its shadow, through the one camera, so
   the card lies on the desk in the room pose too.
   ================================================================= */

/* the card: a quad lying on the desk, given as an origin and two edges in
   sheet units, so it is rigid with the desk in any camera pose.

   ==== R7: AND IT IS A CARD, NOT A RECTANGLE ==========================
   E6's card was one flat quad with a painted corner shade, and at the
   room pose it read as a decal. Three things, all of them geometry:
     · A CORNER LIFTS. The four corners carry their own height (uLift,
       bilinear across the quad) — the south-east one 2.6 sheet units off
       the paper, which is what a 5 x 3 card that has been in a pocket
       does. The surface normal comes off that lift, so the card catches
       the light unevenly the way a curled card does.
     · IT HAS A THICKNESS. The four edges are drawn as bands from the
       lifted surface down to the paper: at 1.4 sheet units the card was
       already a millimetre off the desk and nothing showed it.
     · THE SHADOW FOLLOWS BOTH. Each corner's shadow is thrown at ITS OWN
       height, so the quad under the card is a general quadrilateral and
       not the card's own rectangle shifted — uW is the bilinear term
       that lets one quad draw it.
   ================================================================= */
var CARD_VS = `#version 300 es
precision highp float;
` + R7 + `
layout(location = 0) in vec2 a;
out vec2 vUv; out vec2 vP;
uniform vec3 uO, uU, uV;
uniform mat4 uVP; uniform vec3 uSheet;
uniform vec3 uPkAt;        // PK1: where a peeked card lay (xy), and how far it is up (z)
#ifdef R7
out vec3 vN; out vec3 vW;
out float vAcross;         /* CE1: down the cut edge, 0 at the face, 1 at the foot */
uniform vec3 uW;           // the bilinear corner of a warped quad
uniform vec4 uLift;        // the four corners' height, in sheet units
uniform vec4 uBand;        // the cut edge: from (x,y) to (z,w) in card uv
uniform vec3 uBandN;       // and its outward normal
uniform float uMode;       // 0 the face or the shadow, 1 the cut edge
/* CF1: the cut edge is a band from the card's face DOWN TO WHAT IS
   HOLDING THE CARD UP, and until now that was written as z = 0 — the
   desk. A card lying on the desk is a millimetre off it and the band was
   a millimetre tall; a card riding the lifting leaf is five hundred
   units off it and the band was a five-hundred-unit black curtain
   hanging from the card to the wood. So the foot is taken DOWN THE
   CARD'S OWN NORMAL by the stock's height over its support: uNrm is that
   normal and uLo that height. For a card lying flat (uNrm = +z, uLo =
   its own o.z) this is p.z -> 0 to the last bit, as it was. */
uniform vec3 uNrm;
uniform float uLo;
#endif
void main(){
  vUv = a;
#ifdef R7
  vec2 g = a;
  float side = 0.0;
  if (uMode > 0.5) { g = mix(uBand.xy, uBand.zw, a.x); side = a.y; vUv = g; }
  vAcross = side;
  float lz = mix(mix(uLift.x, uLift.y, g.x), mix(uLift.z, uLift.w, g.x), g.y);
  vec3 p = uO + uU*g.x + uV*g.y + uW*(g.x*g.y);
  p.z += lz;
  if (uMode > 0.5) {
    vN = uBandN;
    p -= uNrm * ((uLo + lz) * side);
  } else {
    /* the surface normal off the lift: the two edge tangents, crossed */
    vec3 Tu = uU + uW*g.y; Tu.z += mix(uLift.y - uLift.x, uLift.w - uLift.z, g.y);
    vec3 Tv = uV + uW*g.x; Tv.z += mix(uLift.z - uLift.x, uLift.w - uLift.y, g.x);
    vN = normalize(cross(Tu, Tv));
  }
  vW = p;
#else
  vec3 p = uO + uU*a.x + uV*a.y;
#endif
  vP = p.xy;                 /* R42 (a): where this is on the sheet, for the lamp */
  /* PK1: a card held up to the eye keeps the lamp's light it lay in: at
     night the middle of the glass is out of the pool, and the memorandum
     held there read grey */
  vP = mix(vP, uPkAt.xy, uPkAt.z);
  gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), p.z*uSheet.z, 1.0);
}`;

var CARD_FS = `#version 300 es
precision highp float;
` + R7 + `
in vec2 vUv; in vec2 vP;
out vec4 o;
uniform float uKind;       // 0 the shadow, 1 the card, 2 its cut edge
uniform vec4 uLampS;       // R42 (a): the lantern in sheet units: x, y, and the pool's two radii
uniform float uLampN;      // and the night, which is nought by day
uniform vec2 uPx;          // the card in device px
/* CF1: how much shadow there is at all. One by default and by day, and
   taken down to nothing while the card is in the air on the folding
   leaf — a contact shadow is a contact, and a card five hundred units
   up under a low sun was throwing its shadow three thousand units
   across the oak and out of the room. */
uniform float uShade;
uniform vec3 uLight;       // the sun in sheet space
uniform vec4 uSun;         // dx, dy, len, cast
uniform float uSoft;
#ifdef R7
in vec3 vN; in vec3 vW; in float vAcross;
uniform vec3 uEye; uniform float uSpec; uniform float uSide;
/* ==== CF3 (b): THE PAPER OVER THE CARD HIDES IT ========================
   The card pass carries no depth (it lies OVER the lamp, the books and the
   sheet, E6), so a card that has gone UNDER the paper — on its back on the
   oak with the first leaf falling over it, and squirted out from under
   that leaf's free edge — was drawn over the print. Tommy's "time running
   backwards" is exactly a card seen through the paper. So each fragment
   asks the one question a depth buffer would: does the line from it to the
   eye pass through the first leaf? The leaf is the rectangle hinged on the
   centre crease (uLeaf.x) at its angle (uLeaf.y), uLeaf.z long and uLeaf.w
   deep; at nought it IS the east half of the flat sheet. A card lying on
   the paper is on the eye's side of the leaf's plane and is never asked
   again; a card under it is, and only what shows past the free edge is
   drawn. uLeafOn is nought unless the day card has a fold to be under
   (never for the note, which has its own scissor). DOM: layoutDom's clip. */
uniform vec4 uLeaf;
uniform float uLeafOn;
bool underLeaf(vec3 P){
  if (uLeafOn < 0.5) return false;
  float sa = sin(uLeaf.y), ca = cos(uLeaf.y);
  vec3 nL = vec3(-sa, 0.0, ca), H0 = vec3(uLeaf.x, 0.0, 0.0);
  float fP = dot(nL, P - H0), fE = dot(nL, uEye - H0);
  if (fP*fE >= 0.0) return false;
  vec3 X = P + (uEye - P)*(fP/(fP - fE));
  float d = (X.x - uLeaf.x)*ca + X.z*sa;
  return d >= 0.0 && d <= uLeaf.z && X.y >= 0.0 && X.y <= uLeaf.w;
}
uniform vec4 uRule;        // NL1: the feint blue ruling — first rule, pitch, how many, and the height nothing is ruled above
uniform vec4 uRuleB;       // CF3: and the BACK's, which carries the memorandum's list
#endif
float h21(vec2 p){ p = fract(p*vec2(0.1031,0.1030)); p += dot(p, p.yx+33.33); return fract((p.x+p.y)*p.x); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
  return mix(mix(h21(i), h21(i+vec2(1,0)), f.x), mix(h21(i+vec2(0,1)), h21(i+vec2(1,1)), f.x), f.y);
}
/* ==== R42 (a): THE LANTERN'S OWN COLOUR, ON THE CARD ================
   The card is paper on the same desk under the same lamp, and it went
   grey at night for the same reason the sheet did: nothing on it knew
   the light had a temperature. Tommy, at R23's merge: "why is the card
   now grey?" — measured 202/199/188 at 23:00 against 239/234/221 by day.

   Five lines, the same five as objects.js's SHEET_FS (and engine.js's
   frame-1 sheet, and R48's ground fragment): the lantern is a point a lamp's height over the top,
   its share here falls off as h^2/(d^2 + h^2), the paper is warmed by
   that share and cooled by what is left, and the night scales the whole
   thing — so by day this is the identity to the last bit. ============ */
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  /* LK1: and the lamp is the light. The paper outside the pool falls into
     shadow at night (to half), and into a dusk dimness before it. */
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
void main(){
  vec2 q = vUv;
#ifdef R7
  if (underLeaf(vW)) discard;          /* CF3 (b): under the paper, and hidden by it */
#endif
  if (uKind < 0.5) {
    /* THE SHADOW THE CARD THROWS, on whatever is under it — oak, paper
       or the plate's own corner — because it is drawn in the card's pass
       and not baked into the desk's. A contact shadow that is never not
       there, and a cast one that is entirely the light's doing. */
    vec2 d = abs(q - 0.5) - 0.5;
    float e = max(d.x*uPx.x, d.y*uPx.y);
    float blur = 7.0*uSoft;
    float a = (1.0 - smoothstep(-blur, blur*0.85, e))*0.40;
    o = vec4(0.0, 0.0, 0.0, a*0.86*uShade);
    return;
  }
#ifdef R7
  if (uKind > 1.5) {
    /* THE CUT EDGE. Card stock is a sandwich: two sized faces and a
       coarser core, and the core is what a cut shows. */
    vec3 e = vec3(0.906, 0.878, 0.812);
    e *= 0.94 + 0.12*vnoise(vec2((q.x + q.y)*uPx.x*0.6, uSide*7.0));
    float lam = clamp(dot(vN, uLight), 0.0, 1.0);
    /* CE1 (2026-10-01): THE SHADED EDGE OF WHITE STOCK IS GREY, NOT BLACK,
       AND AN EDGE THINNER THAN A PIXEL IS A FRACTION OF ONE. Tommy, of the
       card: "it's kind of got black dashes". The west band stands nearly
       edge-on to this camera, a third of a pixel across, and was shaded to
       a tenth of the stock (an ambient floor of 0.42, then 0.34 for its
       side), so the rows the rasteriser happened to light came out as black
       dashes down the card. The floor is the room's bounce off white card
       now and the sides differ by a breath; and the band is drawn at the
       share of a pixel it covers (1 / its width across, in px), so a
       sliver is a faint even line. The blend is premultiplied. */
    e *= 0.70 + 0.45*lam;
    e *= mix(1.0, 0.80, smoothstep(0.10, 1.0, uSide));
    vec2 gA = vec2(dFdx(vAcross), dFdy(vAcross));
    float cov = clamp(1.0/max(length(gA), 1e-4), 0.0, 1.0);
    o = vec4(lampTemp(e, vP)*cov, cov);
    return;
  }
#endif
  /* THE STOCK. An index card is not the survey's paper: it is whiter,
     thicker and it takes a rule in red at the head and feint blue below.
     Card stock #f6f1e4 against the sheet's #f1e7d1. */
  vec3 col = vec3(0.965, 0.945, 0.894);
  /* the fibre */
  float g = vnoise(q*uPx*0.85);
  col *= 0.975 + 0.05*g;
  col *= 0.985 + 0.03*h21(floor(q*uPx));
  /* CF3: WHICH FACE IS THIS. The quad is the card's face and a card lying
     on its back is the same quad turned over, so the side the eye is on
     is the side being seen: its normal is the face normal turned toward
     the eye, and its ruling is that face's. The back is ruled in the same
     card height (it is turned about the axis along its height, so its
     head is still at the head), with the memorandum's pitch. */
#ifdef R7
  bool frontF = dot(vN, uEye - vW) >= 0.0;
  vec3 nF = frontF ? vN : -vN;
  vec4 uRl = frontF ? uRule : uRuleB;
#else
  vec4 uRl = uRule;
#endif
  /* the red head rule, and the feint blue rules under it */
  float head = abs(q.y - 0.205)*uPx.y;
  col = mix(col, vec3(0.706, 0.286, 0.243), clamp(1.25 - head, 0.0, 1.0)*0.62);
  /* NL1 (2026-09-21): AND THE RULING IS TOLD WHERE TO GO. It was six
     rules from the red one down, every 0.74/5 of the card — the day's
     card, and the only card there was. The note under the map is a LIST
     now, one rule an errand, and a ruling that does not fall where the
     list is written is two rulings on one card. So the places are a
     uniform in the card's own height (first rule, pitch, how many) and
     the day's card passes the numbers it always had. */
  float rv = (q.y - uRl.x)/uRl.y;
  float k = floor(rv + 0.5);
  if (q.y > uRl.w && k > -0.5 && k < uRl.z - 0.5) {
    float dr = abs(rv - k)*uRl.y*uPx.y;
    col = mix(col, vec3(0.549, 0.639, 0.702), clamp(1.0 - dr, 0.0, 1.0)*0.34);
  }
  /* the cut edge, and the card's own thickness catching the light */
  float bx = min(q.x, 1.0 - q.x)*uPx.x, by = min(q.y, 1.0 - q.y)*uPx.y;
  float b = min(bx, by);
  col *= 0.80 + 0.20*smoothstep(0.0, 2.2, b);
#ifdef R7
  /* it is lying on the desk with a corner up, so the light it takes is
     its own normal's and not the flat sheet's. MEASURED FROM FLAT, as the
     sheet's bare paper is (objects.js): uLight.z is what a flat face
     returns, so the flat of the card is its own stock colour at any hour
     and only the lifted corner carries shading. Until 2026-09-20 the
     datum was a constant 0.62, a 34-degree sun, and the card fell to
     three quarters of its colour as the sun came down while the sheet
     beside it did not: Tommy, at dusk, "did we deliberately change the
     colour of the file card to be much greyer?" */
  float lam = clamp(dot(nF, uLight), 0.0, 1.0);
  col *= 1.0 + 0.55*(lam - clamp(uLight.z, 0.0, 1.0));
  vec3 hv = normalize(uLight + normalize(uEye - vW));
  col += uSpec*pow(clamp(dot(nF, hv), 0.0, 1.0), 20.0);
#else
  /* it is lying flat, so it takes the sun the way the paper does: bare
     paper measured from flat is its own colour, whatever the hour */
  /* a corner lifts a little off the desk: the shade runs under the
     south-west of it */
  col *= 1.0 - 0.10*smoothstep(0.55, 1.0, q.y)*smoothstep(0.55, 0.0, q.x);
#endif
  o = vec4(lampTemp(col, vP), 1.0);
}`;

/* =================================================================
   THE MODULE
   ================================================================= */
var D = { ready: false };
root.RoomDay = D;

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
function smst(a, b, v) { var t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }
function isoOf(ms) { return new Date(ms).toISOString().slice(0, 10); }

/* R4: the day's programs, issued by the engine after the first frame and
   collected when the record lands (marks.js). R12: `day-card` is made at
   frame 1 instead; `prog` memoises, so naming it here is a no-op. */
D.precompile = function (H) {
  return [H.prog(H.gl, CARD_VS, CARD_FS, 'day-card')];
};

/* =================================================================
   THE CARD'S STOCK — R4's OPEN 2, closed by R12

   The card is two things. Its PAPER — the stock, the cut edges, the
   lifted corner, the shadow — is FURNITURE: placed from the layout
   alone, knowing nothing of the record, so it is drawn in FRAME 1 with
   the desk and the books and frame 1 shows a blank index card. Its TYPE
   is the record's and arrives in frame 2, gated by `html.room-map` as
   it already was. Nothing is drawn and thrown away.

   `D.attach` is handed this module as `H.card`: one program, one quad.
   ================================================================= */
D.card = function (H) {
  var gl = H.gl, S = H.S;
  var NO_CARD = Q.get('card') === '0';
  /* CF1 read a mode here (`CARD_MODE`, slip or north, anything else R47's
     slide). CF2: he picked slip, so there is one motion and nothing to
     read — `?card` is the E6 on/off switch again and only '0' means
     anything. */
  /* compiled HERE, not in `D.precompile`: the engine calls this before
     frame 1, so `day-card` is a frame-1 program like the desk's. */
  var pCard = H.prog(gl, CARD_VS, CARD_FS, 'day-card');
  var quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0, 1,0, 0,1, 1,1]), gl.STATIC_DRAW);
  var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  /* ================================================================
     THE CARD — where it lies

     A physical object at the plate's south-east corner: its north-west
     corner over the corner of the window, its body running south-east
     across the paper's foot and its east end out over the oak. It never
     covers the plate's ground beyond that corner, and it is never
     empty.
     ================================================================ */
  /* NL1: `rule` is the stock's feint ruling in the card's own height —
     the first rule's place, the pitch, how many, and the height nothing
     is ruled above. These four are exactly what the shader carried as
     constants before the note's list needed its own ruling, so every card
     but the note is ruled to the same three-hundredth of a pixel it was
     (D.note sets them off its list; nothing else does). */
  var card = { w: 0, h: 0, o: null, u: null, v: null, px: 0, py: 0,
               rule: [0.205, 0.74 / 5, 6, 0.205] };
  var dom = {};
  /* CF3: and its BACK, a second face of the same stock size carrying the
     memorandum (D.attach, THE MEMORANDUM); the note's instance has none */
  function grabDom() { dom.el = document.getElementById('day-card');
                       dom.back = document.getElementById('day-card-back'); }
  /* CF3: the corners' lift is a card curled UP off whatever it lies on,
     so it belongs to the face that is up; a card turned over lies on its
     curl and is flat. Nought everywhere for a face-down pose. */
  function lifts() { return card.faceUp === false ? [0, 0, 0, 0] : CARD_LIFT; }

  /* the four cut edges, each as a run in the card's own uv — head, east,
     foot, west — walked so the band's outward normal is the run turned a
     quarter turn in the sheet's plane */
  var CARD_BANDS = [[0,0, 1,0], [1,0, 1,1], [1,1, 0,1], [0,1, 0,0]];
  /* CF1: and the card is not always lying flat now — riding the leaf it
     stands at the leaf's own angle, so the run, the surface normal and
     the quarter turn between them are all taken in THREE dimensions. For
     a flat card (u.z = v.z = 0, normal +z) this is the old two lines to
     the last bit: cross(run, +z) is (dy, -dx, 0) and the 0.20 up-tilt is
     0.20 of the normal. */
  function bandNormal(bd) {
    var f = (bd[2] - bd[0]), g = (bd[3] - bd[1]);
    var r = [f * card.u[0] + g * card.v[0],
             f * card.u[1] + g * card.v[1],
             f * (card.u[2] || 0) + g * (card.v[2] || 0)];
    var n = [card.u[1] * (card.v[2] || 0) - (card.u[2] || 0) * card.v[1],
             (card.u[2] || 0) * card.v[0] - card.u[0] * (card.v[2] || 0),
             card.u[0] * card.v[1] - card.u[1] * card.v[0]];
    var nm = Math.hypot(n[0], n[1], n[2]) || 1;
    n = [n[0] / nm, n[1] / nm, n[2] / nm];
    var o = [r[1] * n[2] - r[2] * n[1], r[2] * n[0] - r[0] * n[2], r[0] * n[1] - r[1] * n[0]];
    var m = Math.hypot(o[0], o[1], o[2]) || 1;
    /* CF3: the run crossed with the FACE normal is outward either way up
       (a card turned over walks its edges the other way round and its
       face normal points down, and the two flips cancel); the up-tilt is
       toward the sky, so it takes the normal of whichever face is up */
    var up = n[2] < 0 ? -1 : 1;
    return [o[0] / m + 0.20 * up * n[0], o[1] / m + 0.20 * up * n[1], o[2] / m + 0.20 * up * n[2]];
  }
  /* the four corners of the shadow, each thrown from its own height */
  /* CF1: the card's unit normal and the height of its face over whatever
     is holding it up. Flat on the desk both are what they always were
     (+z, and the origin's own z); on the lifting leaf the normal is the
     leaf's and the height is the stock's 1.4 over the PAPER, not the
     five hundred units over the wood. `card.lo` is set by the variants
     and left alone by R47's slide, so the default motion reads o.z. */
  function cardNormal() {
    var uz = card.u[2] || 0, vz = card.v[2] || 0;
    var n = [card.u[1] * vz - uz * card.v[1],
             uz * card.v[0] - card.u[0] * vz,
             card.u[0] * card.v[1] - card.u[1] * card.v[0]];
    var m = Math.hypot(n[0], n[1], n[2]) || 1;
    return [n[0] / m, n[1] / m, n[2] / m];
  }
  function cardLo() { return card.lo === undefined ? card.o[2] : card.lo; }
  function shadowQuad(s, off) {
    var out = [], uv = [[0,0],[1,0],[0,1],[1,1]];
    for (var i = 0; i < 4; i++) {
      var a = uv[i][0], b = uv[i][1];
      /* CF1: the corner's OWN height, which on a card standing on the
         lifting leaf is mostly the edge vectors' z and not the origin's */
      var z = Math.max(0, card.o[2] + (card.u[2] || 0) * a + (card.v[2] || 0) * b + lifts()[i]);
      /* `off` was calibrated at the card's normal 1.4-unit stock height.
         During foldSlide the card descends toward the oak; dividing by
         that moving height made the lifted corner's projection singular
         near zero and produced a room-wide diagonal band. Cast distance
         is proportional to actual height over the receiver, referenced
         to the fixed calibration height, so landing shortens the shadow. */
      /* CF1: AND THE HEIGHT IT IS THROWN FROM IS CAPPED. A card riding
         the leaf is hundreds of units off the oak and the throw below is
         linear in that height: measured at t = 0.10, 3,570 sheet units,
         which was a black band across the whole room. Contact is what
         this shadow is; past SHADOW_H it is no longer contact and
         `uShade` has taken it out altogether, so the cap is only there
         to keep the quad finite while it fades. The line under it is
         untouched, denominator and all — the calibration height is
         still the fixed one and never the card's own moving z, which is
         what test-room-measurements.js:399 is standing guard over. */
      z = Math.min(z, SHADOW_H);
      var k = off * z / CARD_STOCK_Z;
      out.push([card.o[0] + card.u[0]*a + card.v[0]*b + s.dx*k,
                card.o[1] + card.u[1]*a + card.v[1]*b + s.dy*k]);
    }
    return out;
  }
  function shadowFigures() {
    if (!card.o || !S.layout) return null;
    var s = sunOf(), cst = s.cast === undefined ? 1 : s.cast;
    var off = 6.0 * (0.25 + 0.75 * cst) * (s.len || 1) / S.layout.k;
    var q = shadowQuad(s, off), xs = q.map(function (p) { return p[0]; });
    var ys = q.map(function (p) { return p[1]; });
    return {
      baseZ:card.o[2], referenceZ:CARD_STOCK_Z,
      cornerZ:[[0,0],[1,0],[0,1],[1,1]].map(function (uv, i) {
        return Math.max(0, card.o[2] + (card.u[2] || 0) * uv[0] + (card.v[2] || 0) * uv[1]
                           + lifts()[i]); }),
      quad:q,
      extents:{x0:Math.min.apply(Math,xs),x1:Math.max.apply(Math,xs),
               y0:Math.min.apply(Math,ys),y1:Math.max.apply(Math,ys)},
      finite:q.every(function (p) { return isFinite(p[0]) && isFinite(p[1]); })
    };
  }
  /* Read-only review evidence for the ordinary day card. The note reuses
     this factory later, so retain the first instance rather than letting
     its separate placement replace the diagnostic. */
  if (!D.cardFigures) D.cardFigures = shadowFigures;

  function place() {
    var L = S.layout;
    /* THE CARD IS SIZED IN THE SHEET'S UNITS, NOT THE WINDOW'S (E9).
       It was `L.cw * 0.235` clamped to 346, which is the SAME 346 css px
       at 1920 x 1080 and at 1536 x 864 — but the paper at 1536 is 0.78 of
       the paper at 1920, so the same card ate a fifth more of the foot
       margin and its tail came down on the notes about sea level. Measured
       in the sheet's own units the card was 449 x 270 at 1920 and 578 x
       347 at 1366; it is 449 x 270 at every window now, so the rectangle
       of paper it lies on is the same rectangle at every window and what
       it clears at one size it clears at all of them.

       AND IT IS SHALLOWER: 0.60 of its width put the card's south-west
       corner at 961 sheet units, and the section's month figures are
       lettered across the whole paper at 947, so the card's foot came
       down on DEC at every window. 0.53 stands it at 929 — eight css px
       clear of that row at 1920 x 1080, which is the tightest of the
       three — and what it gives up is the blank ruled foot of the card,
       not a line of the typing (five lines and the essay tab need 126 of
       the 183 css px it has at 1920). */
    /* SB3 (2026-09-24): NO CAP ABOVE. It was clamped to 346 css px as well,
       so on a window wider than 1920 (Tommy's, 2194 css) the card came out
       smaller in the sheet's units than the paragraph above says it is —
       393 units, its foot at ~899 instead of 929 — and the section's lower
       third showed below it. The section's east end is drawn to be hidden
       under the card (handle.js SB3), so the card is the same rectangle of
       paper at every window, as E9 meant; its type follows (`--dck`). */
    var w = Math.max(210, Math.round(CARD_W * L.k));
    var h = Math.round(w * 0.53);
    var ax = S.portal.l + S.portal.w, ay = S.portal.t + S.portal.h;
    /* its head lies just over the plate's own south-east corner: ten css
       px of the window and no more (the ruling: never beyond the corner) */
    var x = ax - w * 0.62, y = ay - 10;
    /* LK10 (audit A21): A VARIANT FOR TOMMY TO PICK. At rest the card's
       head covers the plate's south-east corner: the neat line, the
       border's NOV and DEC and the section's last months. `?cardrest=clear`
       sets it down just below the border instead (an eighth of its width,
       which is the border band at every window, since both are sheet
       units), so the neat line and the months show and its tail lies over
       the section's empty future, which SB3 draws to be covered. The
       default is the ruling as it stands. */
    if (CARD_REST_CLEAR) y = ay + w * 0.125;
    /* CS1: where the desk's card lies, kept for the phone's slide (below) */
    var yDesk = y, wDesk = Math.round(w * 0.85), hDesk = Math.round(h * 0.85);
    /* ==== PH10 (2026-10-02): ON THE PHONE THE CARD IS NOT LYING THERE.
       Tommy: "think about what we render and portray at all when the book
       is shut", and the design's "nothing covers the map at rest". So at
       rest the card is not drawn at all (draw, `phoneAway`); a tapped day
       brings it, readable and clear of the plate's middle, and the next
       tap off the record puts it away. On its side (the only way the
       phone shows the map, PO1) it lies at the top corner of the glass,
       its head over the plate's end and nowhere near its middle.
       CC1 (2026-10-02): ONE CORNER. It jumped to whichever top corner was
       away from the day tapped, so it moved left and right as the map was
       tapped; Tommy: "The day card should sit in one corner". It lies at
       the top right (over the unsurveyed months) and goes to the top left
       only when the day tapped would be under it there. */
    /* EG3: folded, the card lies beside the packet as it does at a desk
       (FU1), not in the corner of a map that is put away */
    var folded = !!(root.RoomObjects && RoomObjects.foldOn && RoomObjects.foldT > 0.5);
    /* CS1 (2026-10-04): ON THE PHONE THE CARD'S PLACE IS ALWAYS ITS CORNER,
       and the fold moves it as the desk's fold moves the desk's card.
       Tommy: "Why have we not just got our similar action to what we have
       on desktop, where it slides down? And then when we fold back up, it
       shoots off." FJ1 had swapped its place for the desk's at the fold's
       first degree and hidden it while it travelled, so it vanished in the
       air and came back from nowhere. Now it rides the leaf from its
       corner, slides down its face and skids to the desk's own rest row
       beside the packet (`restY`, foldSlip); the unfold squeezes it off the
       glass and deals it back into its corner. A card not up when the fold
       starts is dealt in once the packet lands. */
    if (PHONE && L.side) folded = false;
    if (PHONE && L.side && !folded) {
      /* LY1: the plate now starts at the glass's head, so the card's corner
         lies over it: at 667 px the 200 px card reached the plate's middle.
         CS1: and never bigger than the desk's own card (178 px), since it
         now goes to the desk with the fold and comes back: one card, one size */
      /* MP2: and the corner is the GLASS's, as it lies in the world: the
         camera stands back by frameK (a pinch out to the whole sheet takes
         it further back), so the glass's top right is `gl`'s, the card's
         size on the glass is kept by laying it frameK larger, and the tapped
         point (glass px) is taken into the world to be kept clear of */
      var fG = S.frameK || 1, G = L.gl || { x0: 0, y0: 0, x1: L.cw, y1: L.ch };
      w = Math.min(wDesk, Math.round(clamp(Math.round(L.cw * 0.26), 170, 260) * fG)); h = Math.round(w * 0.53);
      x = G.x1 - (8 + 6) * fG - w; y = G.y0 + 8 * fG;
      var pa = card.phoneAt, bx = Math.abs(Math.cos(CARD_TILT)) * w + Math.abs(Math.sin(CARD_TILT)) * h,
          by = Math.abs(Math.sin(CARD_TILT)) * w + Math.abs(Math.cos(CARD_TILT)) * h;
      if (pa) pa = [L.cw / 2 + (pa[0] - L.cw / 2) * fG, L.ch / 2 + (pa[1] - L.ch / 2) * fG];
      /* NC2 (2026-10-07): AND IT NO LONGER MOVES ASIDE for a day under it. Tommy: "people need to click under the
         card. If they want to click under the card, they can move the bloody map" (a tap on the card read the day
         under it and the card jumped to the top left). A tap on the card is the card (engine.js, hitCard) */
      void pa; void bx; void by;
    }
    var mgn = 8;
    var ex = Math.abs(Math.cos(CARD_TILT)) * w + Math.abs(Math.sin(CARD_TILT)) * h;
    var ey = Math.abs(Math.sin(CARD_TILT)) * w + Math.abs(Math.cos(CARD_TILT)) * h;
    /* RU3: upright in the room, inside the sideways glass it lay on */
    var ax0 = L.ah ? (L.cw - L.aw) / 2 : 0, ay0 = L.ah ? (L.ch - L.ah) / 2 : 0;
    var aw0 = L.aw || L.cw, ah0 = L.ah || L.ch;
    /* MP2: the glass as it lies in the world (the camera stands back by frameK) */
    if (L.gl) { ax0 = L.gl.x0; ay0 = L.gl.y0; aw0 = L.gl.x1 - L.gl.x0; ah0 = L.gl.y1 - L.gl.y0; }
    x = clamp(x, ax0 + mgn, ax0 + Math.max(mgn, aw0 - ex - mgn));
    y = clamp(y, ay0 + mgn, ay0 + Math.max(mgn, ah0 - ey - mgn));
    card.w = w; card.h = h; card.x = x; card.y = y;
    /* in sheet units, with the card's own thickness off the desk */
    var k = L.k, ct = Math.cos(CARD_TILT), st = Math.sin(CARD_TILT);
    var z = CARD_STOCK_Z;              // a card is about a millimetre thick
    card.o = [(x - L.ox) / k, (y - L.oy) / k, z];
    card.u = [w * ct / k, w * st / k, 0];
    card.v = [-h * st / k, h * ct / k, 0];
    /* CS1: the row (sheet units) the phone's card skids to beside the packet */
    card.restY = PHONE && L.side ? (yDesk - L.oy) / k + (wDesk * st + hDesk * ct) / (2 * k) : null;
    /* CF1: THE FLAT CARD, KEPT. Every pose below is taken from this and
       never from the card as it stands, so a pose is a pure function of
       the turn's angle and the gesture reverses by running the angle
       back down — which is what `Room.foldAt` does on the unfold. */
    flat = { o: card.o.slice(), u: card.u.slice(), v: card.v.slice() };
    card.lo = undefined;               /* CF1: flat on the paper, so o.z is the height */
    /* PK3 (2026-10-04): THE TYPE IS SET AT THE SIZE IT IS READ AT. Held up
       (PK1) the card is three or four times its size on the desk, and the
       DOM over it was a box of the desk size scaled up by its matrix: iOS
       draws a box with a perspective matrix once and scales the picture,
       so the memorandum read blurred (Tommy, on his iPhone). The box is
       laid at the size it is held up at and scaled DOWN on the desk, which
       every browser draws sharp. `dw`/`dh` are the box; `w`/`h` the card. */
    var gw = Math.max(L.cw, L.ch);
    var dsc = Math.max(1, Math.min(4, 0.9 * gw / w));
    card.dw = Math.round(w * dsc); card.dh = Math.round(h * dsc);
    /* TC1: ON THE PHONE ON ITS SIDE THE TYPE IS 1.6 TIMES ITS SIZE. Tommy: "In landscape on mobile, it's basically
       unreadable". At 852 x 393 it was 6 css px. Every day's card was measured at the bigger size (scratch
       cardfit.js) and fits, now that the settled line (SL1), "read the post" (RP1) and "Bench mark" (RP2) are off
       it; the two that do not fit go on two cards (TC1, the second card). The back (the memorandum, read held up)
       keeps its size. */
    var tk = PHONE && L.side ? (dom.el && dom.el.getAttribute('data-two') === 'voy' ? 1.45 : 1.6) : 1;   /* VL1 */
    if (dom.el) { dom.el.style.width = card.dw + 'px'; dom.el.style.height = card.dh + 'px';
                  dom.el.style.setProperty('--dck', (card.dw / 320 * tk).toFixed(4)); }
    if (dom.back) { dom.back.style.width = card.dw + 'px'; dom.back.style.height = card.dh + 'px';
                    dom.back.style.setProperty('--dck', (card.dw / 320).toFixed(4)); }
  }

  /* ==== R47: THE CARD SLIDES OFF THE MAP; IT IS NOT TAKEN OFF IT =======
     ==== CF2: AND THE CODE THIS DESCRIBES IS GONE. This is the motion
     Tommy called not realistic — the card sliding WEST to the crease
     while the paper under it rises EAST of it — and the slip replaced it
     outright, so `slidFrom` and the eight lines that read it are deleted.
     It is kept here because it is where the PLACE came from, and the
     place is the part he likes: the slip finishes at the same y and the
     same z, 30 units (CARD_SKID) further east than the line below says.
     Read on as history. ====
     X2's OPEN 1: the day's card "stops being drawn the moment the sheet
     starts to move … it reads as 'you take the card off the map before
     you fold it', which is what a person does, but it is a cut and not a
     move." So here is the move.

     THE CARD IS ON THE FLAP. It lies over the plate's south-east corner,
     which is 1,899 sheet units east — well past the middle crease at
     1,182 — so the first fold picks up the very paper it is resting on.
     A card on a rising flap slides DOWN it, which is west, and it stops
     where the paper stops moving: at the crease, with its west edge
     against the fold and the rest of it lying on the oak the map has
     just left. Nothing afterwards reaches it — the second fold takes the
     sheet in to 591 and the third takes it in two — so the card is on
     the desk beside the packet for the rest of the fold, and it slides
     back up on to the paper as the sheet opens.

     It goes on the FIRST TURN's own angle and is down by the time the
     flap is vertical (0.55 of the half turn), because that is when the
     paper is steepest and a card on it has already gone. And it comes
     down the paper's own thickness as it goes: the card was a millimetre
     off the sheet and ends a millimetre off the wood, which is the half
     css pixel its own shadow is struck from.
     ==================================================================== */
  var flat = null;
  /* CF1: the card has left the leaf and is lying on the oak. The ONE bit
     of memory in the slip, and it is what makes the fold and the unfold
     different motions: going up, the card lets go at 55 degrees; coming
     down, it stays on the wood until the leaf is nearly home. */
  var slip = { off: false };
  /* CF3: and what the card is doing on its own once it has left the leaf:
     home (on the sheet, riding the leaf), flop, back (on the oak on its
     back), squeeze, gone (out of the frame), deal. `t0` is when the
     current move began, `land` when the leaf last touched down with the
     card out of the picture, `x0` where the squeeze started from. */
  var cf = { st: 'home', t0: 0, land: 0, x0: 0, side: 0, turn: null, held: null };
  /* CF3: the card as a centre and a turn about the axis along its height
     (the crease's direction, turnQuad's rotation), from the flat card */
  function spinQuad(C, psi) {
    var sa = Math.sin(psi), ka = Math.cos(psi);
    var u = [flat.u[0] * ka, flat.u[1], flat.u[0] * sa];
    var v = [flat.v[0] * ka, flat.v[1], flat.v[0] * sa];
    return { o: [C[0] - (u[0] + v[0]) / 2, C[1] - (u[1] + v[1]) / 2, C[2] - (u[2] + v[2]) / 2],
             u: u, v: v };
  }
  function centreOf(q) {
    return [q.o[0] + (q.u[0] + q.v[0]) / 2, q.o[1] + (q.u[1] + q.v[1]) / 2,
            q.o[2] + (q.u[2] + q.v[2]) / 2];
  }
  /* a cubic Hermite from 0 to 1 with its two end slopes */
  function herm(r, m0, m1) {
    var r2 = r * r, r3 = r2 * r;
    return m0 * (r3 - 2 * r2 + r) + (3 * r2 - 2 * r3) + m1 * (r3 - r2);
  }
  /* is the card wholly beyond the east edge of the glass? */
  function outEast() {
    var L = S.layout, k = L.k;
    for (var i = 0; i < 4; i++) {
      var a = (i & 1), b = (i >> 1);
      var p = [card.o[0] + card.u[0] * a + card.v[0] * b, card.o[1] + card.u[1] * a + card.v[1] * b,
               card.o[2] + (card.u[2] || 0) * a + (card.v[2] || 0) * b];
      var P = Room.scenePoint ? Room.scenePoint(L.ox + p[0] * k, -(L.oy + p[1] * k), p[2] * k) : null;
      if (!P || !(P[0] > L.cw)) return false;
    }
    return true;
  }

  /* ==== CF1: THE FIRST TURN'S OWN ROTATION, FOR A RIGID CARD ==========
     objects.js `foldRoll` turns a point of the sheet about the centre
     crease at desk level and rolls the paper round its own bight on the
     way (r = leaf/2, about 0.45 sheet units — a quarter of a css pixel
     at this layout). A card is not paper and has no bight, so it takes
     the plain turn: the same rotation, without the roll.
       c     the crease, in sheet units
       ang   the card's OWN angle, which is the leaf's until it slips
       dOff  how far the card has slid down the leaf's face toward the
             hinge (positive is west, toward the crease)
       lo    the card's face above whatever is holding it up
     The turn is affine in (d, lo) and lo is one number across the card,
     so the two edge vectors are the linear part and nothing else. ==== */
  function turnPt(p, c, ang, dOff, lo) {
    var d = (p[0] - c) - dOff, sa = Math.sin(ang), ka = Math.cos(ang);
    return [c + d * ka - lo * sa, p[1], d * sa + lo * ka];
  }
  function turnQuad(c, ang, dOff, lo) {
    var sa = Math.sin(ang), ka = Math.cos(ang);
    return { o: turnPt(flat.o, c, ang, dOff, lo),
             u: [flat.u[0] * ka, flat.u[1], flat.u[0] * sa],
             v: [flat.v[0] * ka, flat.v[1], flat.v[0] * sa] };
  }
  /* CF2: `lerpQuad` stood here — the straight blend from one quad to
     another that carried the north errand off the leaf and on to the oak.
     Nothing but `foldNorth` ever called it, so it went with it. The slip
     never blends between two poses: it is the one turn all the way down. */
  function setQuad(q, lo) {
    card.lo = lo;                      /* CF1: the stock over its SUPPORT, for the cut edges */
    card.o[0] = q.o[0]; card.o[1] = q.o[1]; card.o[2] = q.o[2];
    card.u[0] = q.u[0]; card.u[1] = q.u[1]; card.u[2] = q.u[2];
    card.v[0] = q.v[0]; card.v[1] = q.v[1]; card.v[2] = q.v[2];
    /* CF3: which face is up is the pose's own: the face normal's z */
    card.faceUp = (card.u[0] * card.v[1] - card.u[1] * card.v[0]) >= 0;
  }

  /* ==== CF1 `?card=slip`, CF2 THE FOLD: THE CARD RIDES THE LEAF AND
     THEN SLIPS ==========================================================
     CF2: this is what the fold does now, with no query on the URL.
     It lies on the east half, so the first turn picks up the very paper
     it is resting on and the card goes with it, at the leaf's own tilt,
     drawn as a card lying on a sheet that is standing up. R47's easing
     is untouched — the character Tommy calls slick is `foldTurns`', not
     this function's, and this reads the angle that easing produces.

     AT 55 DEGREES IT LETS GO, and what follows is two movements in
     order, not one. FIRST IT SLIDES, still flat on the
     leaf's face at the leaf's own angle, down the slope to the foot —
     `dOff` runs the card's west edge all the way to the crease, and the
     card's angle is still the leaf's, so it is on the paper the whole
     way. THEN IT TIPS, about that west edge, which is on the crease: its
     angle comes off the leaf's and decays to nought, and it comes down
     flat on the oak east of the crease.

     THE ORDER IS THE WHOLE POINT, and it is the second thing this card
     has got wrong. A card that leaves the middle of the face has to pass
     THROUGH the leaf to get underneath it; a card that has reached the
     foot goes ROUND the leaf's own bottom edge, which is what a card
     sliding off a tilting board actually does. Once it is round, the
     card's plane and the leaf's plane are two half-planes hinged on the
     same line with the card's angle the lower of the two, and two
     half-planes off one line meet only on that line — so for the rest of
     the gesture the card is in the empty wedge between the leaf's
     underside and the wood, and never in the paper.

     WHERE IT COMES TO REST. The `dOff` term overshoots the hinge by
     CARD_SKID as the card goes flat, which is the skid: the card's west
     edge finishes 30 sheet units (10.7 mm) EAST of the centre line, at
     the same y it always had. R47's motion put that edge ON the line, so
     this is 30 units further east than the place Tommy likes and nothing
     else about the place has moved. It is well clear of both later
     turns: the second takes the sheet in to x = 591 and its free edge
     never reaches east of the centre line at all.

     THE UNFOLD. Not the slip run backwards — a card does not climb a
     leaf. It stays on the wood while the leaf comes back down over it,
     and once the leaf is within 15 degrees of the desk the card lifts
     CARD_HOP off the oak BY ITSELF, with no hand drawn, because the
     paper is folding by itself too. It goes home the same way it came:
     east along the wedge and up on to the face, with its own angle held
     under the leaf's at every step (ang = a*e, e at most one), so it
     meets the paper only at the last instant — and that instant is
     e = 1, which is a = 0, which is the sheet flat. It settles on to
     the sheet exactly as the sheet goes flat.
     ================================================================== */
  /* CF3: THE TIP AND THE RETURN BELOW ARE GONE, replaced by the flop,
     the squeeze and the deal (see CF3 at the head of this file). What
     stays is CF1's ride and slide, which is a pure function of the leaf's
     angle and so reverses under a hand that lets go short. The rest runs
     on the card's own clock once it has left the leaf, and the function
     says so: it returns true while the card is moving by itself (or
     waiting on the leaf's landing to be dealt back), so draw() asks for
     the next frame. `slip.off` is kept as "the card has left the leaf". */
  function foldSlip(g) {
    var c = g.c1, a = g.a[0], now = (window.performance || Date).now();
    /* the card's face where it comes to rest, a positive half css pixel
       over the oak and never through it. CF2: this line stood in R47's
       slide and `scripts/test-room-measurements.js:382` matches it by
       name ("sliding card lands on its positive desk support"); the slip
       is the sliding card now, so the line lives here. */
    var deskZ = DESK_CARD_PX / S.layout.k;
    /* the card's west edge, flat, east of the crease: the origin corner,
       which the -3.1 degree tilt leaves west of the foot-west one */
    var dMin = flat.o[0] - c;
    /* CF3: half the card's extent along the turn (x when flat, z when on
       edge), the y of its centre, and where its centre rests on the oak */
    var E = (Math.abs(flat.u[0]) + Math.abs(flat.v[0])) / 2;
    var yC = flat.o[1] + (flat.u[1] + flat.v[1]) / 2;
    var restX = c + CARD_SKID + E;
    /* CS1: the phone's card lies in its corner and skids to the desk's row */
    var yR = card.restY != null ? card.restY : yC;
    var ySkid = function (t) { t = clamp(t, 0, 1); return yC + (yR - yC) * t * t * (3 - 2 * t); };
    /* FC1: and on the phone it leaves the leaf over its NORTH edge (y below nought), clear of the paper by its own
       half height, so it never goes round the foot of the leaf the phone's camera is looking at the back of */
    var yN = card.restY != null ? -((Math.abs(flat.u[1]) + Math.abs(flat.v[1])) / 2 + 12) : yC;
    var sm = function (t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
    var xEdge = ((S.layout.gl ? S.layout.gl.x1 : S.layout.cw) - S.layout.ox) / S.layout.k;    /* the glass's east edge (MP2: in the world) */
    card.gone = false; card.sz = undefined;
    if (cf.st === 'squeeze' || cf.st === 'gone') {
      /* the touchdown: under half a degree is the leaf on the oak (the
         cushion's last frame is there, and the settle that follows lifts
         it 1.5 degrees, which is not a lift); over three it is a hand
         taking it up again */
      if (a <= 0.5 * Math.PI / 180) { if (!cf.land) cf.land = now; }
      else if (a > 3 * Math.PI / 180) cf.land = 0;
    }
    /* CS1: a card that was not up when the fold began is dealt in once it
       lands; one lying west of the crease (CC1's left corner) is not on the
       leaf, so it shoots off the glass and is dealt in the same way */
    if (cf.st === 'home' && card.phoneDealIn) { card.phoneDealIn = false; cf.st = 'gone'; cf.toRest = true; cf.land = 0; cf.landF = 0; }
    if (cf.st === 'home' && card.restY != null && dMin < 0 && a > 1e-4) {
      cf.st = 'squeeze'; cf.t0 = now; cf.x0 = centreOf(flat)[0]; cf.y0 = yC; cf.land = 0; cf.landF = 0; cf.toRest = true;
    }
    if (cf.st === 'home') {
      if (a < SLIP_HOLD_A) {
        /* CF1's ride and slide, exactly: on the leaf at its angle, and
           from 55 degrees down its face to the foot */
        var p = clamp((a - CARD_SLIP_A0) / (CARD_SLIP_A1 - CARD_SLIP_A0), 0, 1), sl = smst(0, SLIP_TIP, p);
        /* FC1 (2026-10-07): Tommy: "it falls through the paper in one place and lands on the desk in another". The
           phone looks at the BACK of the rising leaf, so a card going round its foot came through it. On the phone
           the card slides down the face and off the leaf's north edge as it goes, and falls clear of the paper */
        var tq = turnQuad(c, a, dMin * sl, CARD_STOCK_Z);
        tq.o[1] += (yN - yC) * sl;
        setQuad(tq, CARD_STOCK_Z);
        slip.off = false;
        return false;
      }
      cf.st = 'flop'; cf.t0 = now; slip.off = true;
    }
    if (cf.st === 'flop') {
      var tf = now - cf.t0;
      if (tf < FLOP_MS) {
        /* from where the slide left it: at the foot, on the leaf's face,
           at the leaf's 76 degrees */
        var s0 = turnQuad(c, SLIP_HOLD_A, dMin, CARD_STOCK_Z), C0 = centreOf(s0);
        var f = tf / FLOP_MS, e = f * f * (3 - 2 * f);
        /* FU1: it falls back flat, FACE UP — 76 degrees to nought, the
           tip's own way round, and no further */
        var psi = SLIP_HOLD_A * (1 - e);
        var x1 = restX - FLOP_SKID;
        /* x: fast off the foot, arriving at the skid's own speed */
        var mEnd = 2 * FLOP_SKID * FLOP_MS / (FLOP_SKID_MS * Math.max(1, x1 - C0[0]));
        var mS = C0[0] - c - E * Math.abs(Math.cos(SLIP_HOLD_A)), mE = CARD_SKID - FLOP_SKID;
        var x = Math.max(C0[0] + (x1 - C0[0]) * herm(f, 2.2, mEnd),
                         c + E * Math.abs(Math.cos(psi)) + mS + (mE - mS) * f);
        /* z: the low edge on the oak and the centre half its own extent
           over it at the card's angle, from where it stood (the clearance
           it left the leaf with dies away by the time it is flat). No
           arch: a card falling back on to its face does not jump. */
        var cS = C0[2] - deskZ - E * Math.abs(Math.sin(SLIP_HOLD_A));
        var kc = smst(0, 0.3, f), clr = (cS * (1 - kc) + 2 * kc) * (1 - f);
        var z = deskZ + E * Math.abs(Math.sin(psi)) + clr;
        /* AND WHEREVER THE LEAF HAS GOT TO. At the fold's own speed the
           leaf turns 17 degrees in a frame, and the card's foot-west corner
           is 14 units up the face when the slide ends (the card's -3.1
           degree tilt), so the frame the flop starts on can find the leaf
           already past the card's own edge. Every corner is kept on the
           leaf's back side by a leaf's thickness and a little (SQ_CLEAR):
           its height above the leaf's plane, -dx sin(a) + z cos(a), under
           -SQ_CLEAR. The card is only ever pushed EAST for it, and at most a
           few units in the first frame or two. */
        var yF = yN;                                                      /* CS1, FC1: off the north edge */
        var q0 = spinQuad([x, yF, z], psi), sa0 = Math.sin(a), ca0 = Math.cos(a), push = 0;
        if (sa0 > 0.05) for (var ci = 0; ci < 4; ci++) {
          var cu = ci & 1, cv = ci >> 1;
          var px = q0.o[0] + q0.u[0] * cu + q0.v[0] * cv, pz = q0.o[2] + q0.u[2] * cu + q0.v[2] * cv;
          push = Math.max(push, (pz * ca0 + SQ_CLEAR) / sa0 - (px - c));
        }
        setQuad(push > 0 ? spinQuad([x + push, yF, z], psi) : q0, deskZ);
        card.sz = z;
        return true;
      }
      cf.st = 'back'; cf.side = 0; cf.turn = null;
    }
    if (cf.st === 'back') {
      var k2 = clamp((now - cf.t0 - FLOP_MS) / FLOP_SKID_MS, 0, 1);
      var xb = restX - FLOP_SKID * (1 - k2) * (1 - k2);
      var yB = yN + (yR - yN) * sm((now - cf.t0 - FLOP_MS) / FLOP_SKID_MS);   /* CS1, FC1: and skids to its row */
      /* FU1: TURNED OVER BY HAND (D.card `turn`, a press on it). A half
         turn about the axis along its height, on the spot: the centre
         rises what the turn needs to keep both long edges off the oak,
         and a little over, and comes down on the other face. */
      var moving = k2 < 1;
      /* FU3: or IN THE HAND (`hold`), at whatever angle the drag has it,
         and let go to the nearer face (`release`) — the unfold's pull,
         for a card. The lift is the angle's own (|sin|), so a held card,
         a turn and a card let go mid-turn all stand at the same height
         for the same angle, and it is flat on the oak at both faces. */
      /* PK1: or HELD UP TO THE EYE while the hand is on it (`peek`) */
      cf.pkC = [xb, yB];
      var pkA = peekStep(now);
      if (pkA > 0) {
        var pq = peekQuad([xb, yB, deskZ], pkA);
        if (pq) { setQuad(pq, deskZ); card.sz = pq.c[2]; }
        else { card.sz = undefined; setQuad(spinQuad([xb, yB, deskZ], 0), deskZ); }
        if (pkA < 1 || cf.pkTo !== 1) moving = true;
      } else {
        card.sz = undefined;
        setQuad(spinQuad([xb, yB, deskZ], 0), deskZ);
        if (cf.pkTo === 1) moving = true;
      }
      if (a >= SQ_A) return moving;
      cf.pk = 0; cf.pkTo = 0; cf.pkS = null; cf.pkNudge = 0; cf.pkH = null;
      cf.st = 'squeeze'; cf.t0 = now; cf.x0 = xb; cf.y0 = yB; cf.toRest = false; cf.land = a <= 1e-6 ? now : 0;
    }
    if (cf.st === 'squeeze') {
      var q = (now - cf.t0) / SQ_MS;
      /* the centre half a card past the edge of the glass, and never
         short of the paper's own east edge, whichever is further */
      var need = Math.max(xEdge, g.w) + E + 40 - cf.x0;
      var dx = need * q * q;
      /* and never slower than the leaf closing over it: every corner under
         the leaf's face by a leaf's thickness and a little */
      if (cf.toRest) { /* CS1: off the sheet on its way up, no leaf over it */ }
      else if (a > 1e-6 && a < Math.PI / 2) dx = Math.max(dx, SQ_CLEAR / Math.tan(a) + deskZ / Math.tan(a) - (cf.x0 - E - c));
      else if (a <= 1e-6) dx = Math.max(dx, need);
      setQuad(spinQuad([cf.x0 + dx, cf.y0 != null ? cf.y0 : yC, deskZ], cf.side || 0), deskZ);
      if ((dx >= need && outEast()) || q >= 3) cf.st = 'gone';
      else return true;
    }
    if (cf.st === 'gone') {
      card.gone = true;
      var O = root.RoomObjects;
      /* CS1: dealt in beside the packet once the fold has landed; an
         unfold that comes first takes it home as the desk's does */
      if (cf.toRest && O) cf.fmax = Math.max(cf.fmax || 0, O.foldT);
      if (cf.toRest && O && O.foldT <= 0.0005 && cf.fmax > 0.5) { cf.toRest = false; cf.fmax = 0; }
      if (cf.toRest) {
        if (O && O.foldT >= 0.9995) { if (!cf.landF) cf.landF = now; } else cf.landF = 0;
        if (!(cf.landF && now - cf.landF >= DEAL_WAIT)) return !!cf.landF;
        cf.st = 'deal'; cf.t0 = now; cf.landF = 0; card.gone = false; cf.side = 0;
      } else {
      if (!(cf.land && now - cf.land >= DEAL_WAIT && !(O && O.foldT > 0.0005))) return !!cf.land;
      cf.st = 'deal'; cf.t0 = now; cf.land = 0; card.gone = false; cf.side = 0;
      }
    }
    /* the deal (CS1: to the desk's rest beside the packet, or home) */
    var hC = cf.toRest ? [restX, yR, deskZ] : centreOf(flat), zB = cf.toRest ? deskZ : CARD_STOCK_Z;
    var qd = (now - cf.t0) / DEAL_MS;
    if (qd >= 1 && cf.toRest) {
      cf.toRest = false; cf.fmax = 0; cf.st = 'back'; cf.t0 = now - FLOP_MS - FLOP_SKID_MS; slip.off = true;
      cf.y0 = yR; setQuad(spinQuad([restX, yR, deskZ], 0), deskZ);
      return false;
    }
    if (qd >= 1) {
      cf.st = 'home'; slip.off = false;
      setQuad(turnQuad(c, 0, 0, CARD_STOCK_Z), CARD_STOCK_Z);
      return false;
    }
    if (qd < DEAL_FLIGHT) {
      var r = qd / DEAL_FLIGHT;
      var ps = DEAL_PSI0 * Math.pow(1 - r, 1.5);          /* the spin dies as it lands flat */
      var X0 = xEdge + E * Math.abs(Math.cos(DEAL_PSI0)) + 30, Xl = hC[0] + DEAL_SKID;
      /* in fast, arriving at the skid's own speed */
      var mL = 2 * DEAL_SKID * DEAL_FLIGHT / ((1 - DEAL_FLIGHT) * Math.max(1, X0 - Xl));
      var xd = X0 + (Xl - X0) * herm(r, 1.8, mL);
      var zd = zB + E * Math.abs(Math.sin(ps)) + 12 * (1 - r);
      setQuad(spinQuad([xd, hC[1], zd], ps), zB);
      card.sz = zd;
    } else {
      var r2 = (qd - DEAL_FLIGHT) / (1 - DEAL_FLIGHT);
      setQuad(spinQuad([hC[0] + DEAL_SKID * (1 - r2) * (1 - r2), hC[1], zB], 0), zB);
    }
    return true;
  }

  /* ==== CF1 `?card=north`: THE CARD IS TAKEN OFF FIRST ================
     ==== CF2: GONE. "I love Slip, whereas North sucks!" `foldNorth` is
     deleted and so is the switch that reached it. CF1's argument for it
     is kept below, because it is the road not taken and the reason it was
     not taken is in it: nothing ever touched the card again, so there was
     no card-and-paper interaction left to judge — which is the very thing
     the complaint was about. It also never fitted the window (see the
     138-unit measurement at the top of this file). Read on as history. ====
     The other answer to the same complaint, and the one a person would
     give: you do not fold a map with a card on it. Over the first eighth
     of a tenth of the gesture the card lifts, carries north over the
     sheet's head and settles on the bare oak there, and it stays for the
     whole fold — nothing ever touches it again.

     THE TIMING IS A COMPROMISE AND IT IS WRITTEN DOWN. The three turns
     are `foldTurns`' and `foldTurns` is objects.js's, which this branch
     is not allowed to edit and does not: the paper starts at t = 0 with
     the card, instead of waiting for it. What keeps that honest is that
     the card's own pose is blended out of THE LEAF'S — `turnQuad` at the
     leaf's angle — so through the errand the card is on the paper it is
     leaving and rises off it, and on the way home it comes down on to a
     leaf that is still 26 degrees up and rides it flat. It is never on a
     plane the paper is not.
     ================================================================== */

  function foldSlide() {
    var O = root.RoomObjects;
    if (!O || !O.foldOn || !card.o || !S.layout) return false;
    if (!flat) return false;           /* nothing placed yet: no pose to take */
    /* CF2: ONE motion, and it is a pure function of the fold's own
       geometry (plus the slip's one latch), so `Room.foldAt` run
       downward IS the unfold and there is no second animation.
       CF3: READ AS HISTORY past the slide. Off the leaf the card moves
       on its own clock, and this says whether it is (see foldSlip). */
    return foldSlip(O.foldGeom());
  }

  /* ==== CF3 (b): THE SAME QUESTION IN JS, for the type ================
     `leafNow` is the first leaf as the shader is given it (null when the
     card has nothing to be under); `underLeaf` is the shader's test line
     for line; `occClip` is what the paper hides of a card that is under
     it: the leaf's four corners projected from the eye on to the card's
     own plane, in the card's own (a, b). That quad is the hidden part (a
     card under the leaf is wholly behind its plane, so every point of the
     card inside the leaf's shadow-from-the-eye is behind the leaf), and it
     is NOT one line: with the leaf standing high its south edge crosses
     the card as well as its free edge. The type is clipped to the card
     minus that quad, one even-odd polygon (setClip). null is "nothing
     hidden". */
  function leafNow() {
    var O = root.RoomObjects;
    if (card.onDesk || !O || !O.foldOn || !O.foldGeom || !flat) return null;
    if (!(O.foldT > 0) && cf.st === 'home' && !(O.foldSettle > 0)) return null;
    var g = O.foldGeom();
    return { c: g.c1, a: g.a[0], span: g.w - g.c1, H: g.h };
  }
  function underLeaf(P, lf, eye) {
    var sa = Math.sin(lf.a), ca = Math.cos(lf.a);
    var fP = -sa * (P[0] - lf.c) + ca * P[2], fE = -sa * (eye[0] - lf.c) + ca * eye[2];
    if (fP * fE >= 0) return false;
    var t = fP / (fP - fE);
    var X = [P[0] + (eye[0] - P[0]) * t, P[1] + (eye[1] - P[1]) * t, P[2] + (eye[2] - P[2]) * t];
    var d = (X[0] - lf.c) * ca + X[2] * sa;
    return d >= 0 && d <= lf.span && X[1] >= 0 && X[1] <= lf.H;
  }
  function cardAt(a, b) {
    return [card.o[0] + card.u[0] * a + card.v[0] * b, card.o[1] + card.u[1] * a + card.v[1] * b,
            card.o[2] + (card.u[2] || 0) * a + (card.v[2] || 0) * b];
  }
  function occClip(eye) {
    var lf = leafNow();
    if (!lf || !eye) return null;
    /* a card on the eye's side of the leaf's plane is on the paper, or
       clear of it, and nothing of it is hidden; one behind that plane is
       under the leaf (the flop never straddles it, CF3 (a)) */
    var sa = Math.sin(lf.a), ca = Math.cos(lf.a);
    var fE = -sa * (eye[0] - lf.c) + ca * eye[2], behind = false;
    for (var ci = 0; ci < 4; ci++) {
      var P = cardAt(ci & 1, ci >> 1);
      if ((-sa * (P[0] - lf.c) + ca * P[2]) * fE < 0) behind = true;
    }
    if (!behind) return null;
    /* the leaf's corners, from the eye on to the card's plane */
    var u = card.u, v = card.v, uz = u[2] || 0, vz = v[2] || 0;
    var n = [u[1] * vz - uz * v[1], uz * v[0] - u[0] * vz, u[0] * v[1] - u[1] * v[0]];
    var fx = lf.c + lf.span * Math.cos(lf.a), fz = lf.span * Math.sin(lf.a);
    var uu = u[0] * u[0] + u[1] * u[1] + uz * uz, vv = v[0] * v[0] + v[1] * v[1] + vz * vz;
    function onCard(F) {
      var den = n[0] * (F[0] - eye[0]) + n[1] * (F[1] - eye[1]) + n[2] * (F[2] - eye[2]);
      if (Math.abs(den) < 1e-9) return null;
      var sP = (n[0] * (card.o[0] - eye[0]) + n[1] * (card.o[1] - eye[1]) + n[2] * (card.o[2] - eye[2])) / den;
      var Q = [eye[0] + (F[0] - eye[0]) * sP - card.o[0], eye[1] + (F[1] - eye[1]) * sP - card.o[1],
               eye[2] + (F[2] - eye[2]) * sP - card.o[2]];
      return [(Q[0] * u[0] + Q[1] * u[1] + Q[2] * uz) / uu, (Q[0] * v[0] + Q[1] * v[1] + Q[2] * vz) / vv];
    }
    var quad = [onCard([lf.c, 0, 0]), onCard([fx, 0, fz]), onCard([fx, lf.H, fz]), onCard([lf.c, lf.H, 0])];
    if (quad.some(function (q) { return !q; })) return [[-9, -9], [9, -9], [9, 9], [-9, 9]];
    return quad;
  }
  /* write it on a face: the front's box runs with (a, b); the back's is
     mirrored left to right (layoutDom, THE BACK) */
  function setClip(el, poly, mirror) {
    if (!el) return;
    var v = '';
    if (poly) {
      /* the card's own box, then the hidden quad, even-odd: what is left
         is the card minus the paper over it (outside the box nothing is
         drawn, so the quad's own overhang is nobody's) */
      var W = card.dw || card.w, Hh = card.dh || card.h;     /* PK3: the box's own px */
      var qs = poly.map(function (p) {
        return ((mirror ? 1 - p[0] : p[0]) * W).toFixed(1) + 'px ' + (p[1] * Hh).toFixed(1) + 'px';
      });
      v = 'polygon(evenodd, 0px 0px, ' + W + 'px 0px, ' + W + 'px ' + Hh + 'px, 0px ' + Hh + 'px, 0px 0px, ' +
          qs.join(', ') + ', ' + qs[0] + ')';
    }
    if (el.__clip === v) return;
    el.__clip = v; el.style.clipPath = v;
  }
  var occNow = null;                   /* the last clip, for the harness */

  /* the DOM over the card: the same three corners the mesh is built
     from, projected through the same camera, as one affine transform.
     Nothing is measured and nothing is read back. */
  function layoutDom() {
    if (!dom.el || !card.o) return false;
    /* A discovery is the one time this card is put into the reader's
       hand. Its stock is painted by CSS while it is there (draw(),
       below, withholds the desk copy), so the old desk projection must
       not chase the moving chart underneath it. The ordinary date card
       still takes this exact projection on every other frame. */
    if (dom.el.getAttribute('data-discovery') === '1') { backShow(false); return true; }
    /* CF3: out of the frame is out of the picture, both faces */
    if (card.gone || card.phoneAway) { backShow(false); return false; }
    var L = S.layout, k = L.k;
    /* The ink belongs only to the face of the stock. A camera behind its
       plane must not see a room-facing copy of the date or note. */
    /* CF3: AND THE CARD HAS TWO FACES. The test is the face normal against
       the eye, which for a card lying flat face up is the old one exactly
       (the eye above the card's plane); the side the eye is on is the face
       that is read, the day's on the front and the memorandum on the back. */
    var eye = (window.RoomObjects && RoomObjects.eye) ? RoomObjects.eye(S) : null;
    var fz = (card.u[0] * (card.v[1]) - card.u[1] * card.v[0]);
    var fx = card.u[1] * (card.v[2] || 0) - (card.u[2] || 0) * card.v[1];
    var fy = (card.u[2] || 0) * card.v[0] - card.u[0] * (card.v[2] || 0);
    var fm = Math.hypot(fx, fy, fz) || 1;
    var cc = [card.o[0] + (card.u[0] + card.v[0]) / 2, card.o[1] + (card.u[1] + card.v[1]) / 2,
              card.o[2] + ((card.u[2] || 0) + (card.v[2] || 0)) / 2];
    var side = eye ? (fx * (eye[0] - cc[0]) + fy * (eye[1] - cc[1]) + fz * (eye[2] - cc[2])) / fm : 1;
    if (Math.abs(side) <= 0.001) { backShow(false); return false; }
    function proj(p) {
      var wx = L.ox + p[0] * k, wy = -(L.oy + p[1] * k), wz = p[2] * k;
      return Room.scenePoint(wx, wy, wz);
    }
    /* R7: THE TYPE RIDES THE LIFT. The card's corners are not coplanar any
       more, so the three points the affine is taken from are projected at
       THEIR OWN heights — the transform is still affine (a css matrix is)
       and the fourth corner is out by a fraction of a pixel at this lift,
       which is what the type on a lifted card does anyway. */
    var lf = lifts();                  /* CF3: the up face's curl */
    /* the ORIGIN corner takes no lift at all (CARD_LIFT[0] is 0): an
       affine cannot lift one corner of a DOM box, and lifting the origin
       translates the whole typed block instead. The PAPER takes what
       lift there is; the typing does not. */
    /* ==== R30: AND THE FOURTH CORNER. Three points are a complete affine
       for a plane parallel to the screen; at the desk's edge pose the
       card's plane is pitched 22 degrees with it, so the card on the
       screen is a TRAPEZOID and its foot-east corner is not where three
       of its corners say it is. `Room.quadMat` keeps the perspective row
       and takes the residual at all four to zero; below a quarter of a
       css pixel it hands the 2D matrix straight back. The foot-east
       corner takes CARD_LIFT[3], which is a constant. */
    var P0 = proj([card.o[0], card.o[1], card.o[2]]);
    /* CF1: + the edge vectors' own z. The typing is on the face of the
       stock and the face is a plane in the air while the card is on the
       leaf; without this the DOM stayed flat on the desk under a card
       standing at sixty degrees. Nought for a card lying flat. */
    var uz = card.u[2] || 0, vz = card.v[2] || 0;
    var P1 = proj([card.o[0] + card.u[0], card.o[1] + card.u[1], card.o[2] + uz + lf[1]]);
    var P2 = proj([card.o[0] + card.v[0], card.o[1] + card.v[1], card.o[2] + vz + lf[2]]);
    var P3 = proj([card.o[0] + card.u[0] + card.v[0], card.o[1] + card.u[1] + card.v[1],
                   card.o[2] + uz + vz + (lf[3] || 0)]);
    if (!P0 || !P1 || !P2 || !P3) { backShow(false); return false; }
    /* CF3 (b): and the paper over it hides the type as it hides the stock */
    occNow = occClip(eye);
    setClip(side < 0 ? dom.back : dom.el, occNow, side < 0);
    setClip(side < 0 ? dom.el : dom.back, null, false);
    if (side < 0) {
      /* CF3: THE BACK. Turned over about the axis along its height, the
         card's head is still at the head and its east edge is where its
         west edge was, so the back's own top-left is the face's top-right
         corner: the same four corners, mirrored left to right. */
      var tb = dom.back ? Room.quadMat([P1, P0, P2, P3], card.dw || card.w, card.dh || card.h) : null;
      if (tb && dom.back.__t !== tb) { dom.back.__t = tb; dom.back.style.transform = tb; }
      backShow(!!tb);
      return false;
    }
    backShow(false);
    var t = Room.quadMat([P0, P1, P3, P2], card.dw || card.w, card.dh || card.h);
    if (t && dom.el.__t !== t) { dom.el.__t = t; dom.el.style.transform = t; }
    return !!t;
  }
  /* CF3: the back's type shown or not, and the list told the first time
     (and every time) it comes up, so it is struck to the marks as found */
  function backShow(on) {
    if (!dom.back) return;
    var v = on ? '' : 'hidden';
    if (dom.back.style.visibility === v) return;
    dom.back.style.visibility = v;
    if (on && dom.onBack) dom.onBack();
  }
  function sunOf() {
    return (window.RoomObjects && RoomObjects.sun) ? RoomObjects.sun() :
      { dx: 0.7, dy: 0.7, len: 1, cast: 1, soft: 1, L: [-0.5, -0.5, 0.7] };
  }

  /* the paper, its four edges and its shadow — every frame, from frame
     1 on; it hands back the draws it made, which the day's pass counts */
  function draw() {
    if (NO_CARD) return 0;
    /* The presented card is a physical duplicate lifted into the hand,
       not two cards at once. CSS supplies the same ruled index stock in
       the middle of the glass; leave the desk copy out until it is put
       down. This branch is presentation-only: the normal card's six
       draws and placement are untouched. */
    if (dom.el && dom.el.getAttribute('data-discovery') === '1') {
      dom.__away = false;
      dom.el.style.visibility = '';
      backShow(false);
      return 0;
    }
    var draws = 0;
    var L = S.layout, s = sunOf();
    if (!card.o) place();
    /* R47: off the flap and on to the oak. CF3: and while the card is
       moving on its own clock, the next frame is asked for here. */
    if (foldSlide() && S.invalidate) S.invalidate();
    if (card.gone || card.phoneAway) {
      /* CF3: out past the edge of the glass: no stock, no shadow, no type */
      if (dom.el && !dom.__away) { dom.__away = true; dom.el.style.visibility = 'hidden'; }
      backShow(false);
      return 0;
    }
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    /* 1. the shadow the card throws, along the sun, at the card's own
          distance off the desk — the books' rule (objects.js) */
    var cst = s.cast === undefined ? 1 : s.cast;
    var off = 6.0 * (0.25 + 0.75 * cst) * (s.len || 1) / L.k;
    gl.useProgram(pCard);
    gl.bindVertexArray(vao);
    gl.uniformMatrix4fv(pCard.u.uVP, false, S.VP);
    gl.uniform3f(pCard.u.uSheet, L.ox, L.oy, L.k);
    gl.uniform2f(pCard.u.uPx, card.w * S.dpr, card.h * S.dpr);
    gl.uniform4f(pCard.u.uRule, card.rule[0], card.rule[1], card.rule[2], card.rule[3]);
    /* CF3: the back's ruling (the memorandum's pitch), the front's where there is no back */
    var rb = card.ruleB || card.rule;
    if (pCard.u.uRuleB) gl.uniform4f(pCard.u.uRuleB, rb[0], rb[1], rb[2], rb[3]);
    gl.uniform3f(pCard.u.uLight, s.L[0], s.L[1], s.L[2]);
    gl.uniform4f(pCard.u.uSun, s.dx, s.dy, s.len, cst);
    gl.uniform1f(pCard.u.uSoft, s.soft === undefined ? 1 : s.soft);
    /* CF3 (b): the eye before the shadow is drawn (the shadow is hidden by
       the paper as the stock is), and the first leaf, for underLeaf */
    var ey0 = (window.RoomObjects && RoomObjects.eye) ? RoomObjects.eye(S) : [0, 0, 900 / L.k];
    gl.uniform3f(pCard.u.uEye, ey0[0], ey0[1], ey0[2]);
    var lfN = leafNow();
    if (pCard.u.uLeaf) gl.uniform4f(pCard.u.uLeaf, lfN ? lfN.c : 0, lfN ? lfN.a : 0, lfN ? lfN.span : 0, lfN ? lfN.H : 0);
    if (pCard.u.uLeafOn) gl.uniform1f(pCard.u.uLeafOn, lfN ? 1 : 0);
    /* CF1: the card's own plane, for the cut edges' foot, and how much
       shadow a card this far off the wood still throws */
    var nrm = cardNormal();
    /* CF3: the cut edge hangs from the face DOWN to the support, so it
       takes the normal of the face that is up */
    if (nrm[2] < 0) nrm = [-nrm[0], -nrm[1], -nrm[2]];
    if (pCard.u.uNrm) gl.uniform3f(pCard.u.uNrm, nrm[0], nrm[1], nrm[2]);
    if (pCard.u.uLo) gl.uniform1f(pCard.u.uLo, cardLo());
    /* CF3: a card in the air off the leaf is as high as its CENTRE (card.sz) */
    if (pCard.u.uShade) gl.uniform1f(pCard.u.uShade,
      1 - smst(SHADOW_H, SHADOW_GONE, card.sz !== undefined ? card.sz : card.o[2]));
    /* R42 (a): the lantern, in the card's own units — the oak's own point
       (objects.js lampSheet), so the pool is ONE pool across the desk */
    if (pCard.u.uLampS) {
      var lsh = (window.RoomObjects && RoomObjects.lampSheet)
        ? RoomObjects.lampSheet(S, s) : [0, 0, 1, 1, 0];
      gl.uniform4f(pCard.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
      gl.uniform1f(pCard.u.uLampN, lsh[4]);
    }
    if (pCard.u.uPkAt) {
      var pkE = cf.pk || 0;
      var fc = centreOf(flat);
      gl.uniform3f(pCard.u.uPkAt, cf.pkC ? cf.pkC[0] : fc[0], cf.pkC ? cf.pkC[1] : fc[1], pkE);
    }
    /* TC1: the stock pass (shadow, cut edges, face) for whichever quad is in `card`; the second card's runs
       first, under this one, with its own corners swapped in and put back */
    function stockPass() {
    gl.uniform1f(pCard.u.uKind, 0);
    /* R7. 1. THE SHADOW, corner by corner. Every corner is thrown at
         its OWN height above the paper (E4's rule: a thing on the desk
         keeps its distance and takes the sun's direction and length), so
         the lifted corner's shadow runs further than the others' and the
         four of them make a quadrilateral. uW carries the bilinear term
         that a parallelogram cannot. */
      var sh = shadowQuad(s, off);
      gl.uniform1f(pCard.u.uMode, 0);
      gl.uniform4f(pCard.u.uLift, 0, 0, 0, 0);
      gl.uniform3f(pCard.u.uO, sh[0][0], sh[0][1], 0.02);
      gl.uniform3f(pCard.u.uU, sh[1][0] - sh[0][0], sh[1][1] - sh[0][1], 0);
      gl.uniform3f(pCard.u.uV, sh[2][0] - sh[0][0], sh[2][1] - sh[0][1], 0);
      gl.uniform3f(pCard.u.uW, sh[3][0] - sh[1][0] - sh[2][0] + sh[0][0],
                               sh[3][1] - sh[1][1] - sh[2][1] + sh[0][1], 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      /* 2. the card itself, and the four bands of its cut edge */
      var ey = (window.RoomObjects && RoomObjects.eye) ? RoomObjects.eye(S) : [0, 0, 900 / L.k];
      gl.uniform3f(pCard.u.uEye, ey[0], ey[1], ey[2]);
      gl.uniform1f(pCard.u.uSpec, (window.RoomObjects && RoomObjects.spec) ? RoomObjects.spec(s) : 0.04);
      var lfd = lifts();
      gl.uniform4f(pCard.u.uLift, lfd[0], lfd[1], lfd[2], lfd[3]);
      gl.uniform3f(pCard.u.uO, card.o[0], card.o[1], card.o[2]);
      gl.uniform3f(pCard.u.uU, card.u[0], card.u[1], card.u[2]);
      gl.uniform3f(pCard.u.uV, card.v[0], card.v[1], card.v[2]);
      gl.uniform3f(pCard.u.uW, 0, 0, 0);
      gl.uniform1f(pCard.u.uKind, 2);
      gl.uniform1f(pCard.u.uMode, 1);
      for (var bi = 0; bi < 4; bi++) {
        var bd = CARD_BANDS[bi], nn = bandNormal(bd);
        gl.uniform4f(pCard.u.uBand, bd[0], bd[1], bd[2], bd[3]);
        gl.uniform3f(pCard.u.uBandN, nn[0], nn[1], nn[2]);
        gl.uniform1f(pCard.u.uSide, bi / 3);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      gl.uniform1f(pCard.u.uKind, 1);
      gl.uniform1f(pCard.u.uMode, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    draws += 6;
    }
    if (card.second && !cf.pk) {
      var o1 = card.o, u1 = card.u, v1 = card.v, q2 = secondQuad();
      card.o = q2.o; card.u = q2.u; card.v = q2.v;
      stockPass();
      card.o = o1; card.u = u1; card.v = v1;
    }
    stockPass();
    /* Keep the writing on the card through a room move. The projected
       transform above follows the same four lifted corners as the stock;
       only a failed/back-face projection hides it. `__away` remains the
       shared "there is no readable DOM face" bit used by the note. */
    var away = !layoutDom();
    if (dom.el && away !== dom.__away) {
      dom.__away = away;
      dom.el.style.visibility = away ? 'hidden' : '';
    }
    return draws;
  }

  /* CF1: read-only evidence for the harness — the card's four corners in
     the sheet's own units, at their own heights, and which motion drew
     them. Nothing in the build reads it. The note under the map makes a
     second instance of this factory, so the DAY's card is the one kept
     (the same rule `D.cardFigures` is kept by). */
  /* ==== PK1 (2026-10-03): THE CARD IS PEEKED, NOT TURNED. Tommy, of
     the closer look at the folded map making the way out to the room less
     obvious and easier to take by accident: zoom only in the room, and
     for the card, "on mobile and desktop we remove card flip, instead we
     zoom/lift + flip the card ... so you can 'peek' the card". So FU1's
     press that turned it over and FU3/FU5's drag that turned it with the
     hand (both his asks, 2026-09-27) are gone: the card lifts towards the
     eye, turning over on the way, until it lies square to the glass with
     the memorandum filling most of it. It works from wherever the reader
     is (seated over the packet, or standing), because the pose is built
     from the camera itself.
     PK2 (same evening, his iPhone): held was too easy: "I do wonder if we
     change the hint card to have a physicality to it, like it used to in
     the desktop version. Rather than just holding it down, maybe you need
     to swipe it or drag it ... the way it worked, where it flips over and
     comes up, is really nice". So the HAND carries it (engine.js): `peekTo`
     puts it at once at a share of the way up (the drag follows the hand),
     and `peekSettle` lets it go on to up or down from wherever it is.
     `peek(on)` is the settle with the old test. ==== */
  /* FD3: `h` the hand's travel on the glass since it took the card, `g`
     where it was taken (0 lying, 1 up): the card's middle is kept that far
     from where it was, so it stays under the finger whichever way it goes,
     and the let-go eases it back on to its own line (`cf.pkHk`) */
  function peekTo(a, h, g) {
    if (cf.st !== 'back' || card.gone || !card.o) return false;
    cf.pkNudge = 0;
    a = clamp(a, 0, 1);
    cf.pk = a; cf.pkTo = a; cf.pkS = null;
    cf.pkH = h ? [h[0], h[1], g ? 1 : 0] : null; cf.pkHk = 1;
    if (S.invalidate) S.invalidate();
    return true;
  }
  /* FD2 (2026-10-04): which way it turns over. Tommy: "Can we say the hint
     card can flip in either direction, left or right? It used to do this."
     The drag sets it from the hand's left or right travel as the card leaves
     the oak (engine.js, FD3); the way down is the same turn backwards */
  function peekDir(d) { if (d) cf.pkDir = d > 0 ? 1 : -1; return cf.pkDir || 1; }
  function peekSettle(to) {
    if (cf.st !== 'back' || card.gone || !card.o) return false;
    cf.pkTo = to ? 1 : 0;
    var from = cf.pk || 0;
    /* FD3: a card let go away from its line eases back on to it, never jumps */
    cf.pkS = { from: from, to: cf.pkTo, t0: (window.performance || Date).now(),
               ms: Math.max(cf.pkH ? PEEK_MS : 80, PEEK_MS * Math.abs(cf.pkTo - from)) };
    if (S.invalidate) S.invalidate();
    return true;
  }
  function peek(on) {
    if (!on && cf.pkTo !== 1 && !(cf.pk > 0)) return false;
    return peekSettle(on);
  }
  /* where it lies on the glass, and how far it is up: what the hand reads */
  /* MP4: where it comes to rest beside the packet (sheet units): east of the
     first crease by the skid, at the phone's rest row. The upright phone's
     folded shot frames the packet and this (engine.js vp) */
  function restBox() {
    if (!flat || !root.RoomObjects || !RoomObjects.foldGeom) return null;
    var g = RoomObjects.foldGeom();
    if (!g || !(g.c1 > 0)) return null;
    var E = (Math.abs(flat.u[0]) + Math.abs(flat.v[0])) / 2, Ey = (Math.abs(flat.u[1]) + Math.abs(flat.v[1])) / 2;
    var yC = card.restY != null ? card.restY : flat.o[1] + (flat.u[1] + flat.v[1]) / 2;
    var x0 = g.c1 + CARD_SKID;
    return { x0: x0, x1: x0 + 2 * E, y0: yC - Ey, y1: yC + Ey };
  }
  function peekInfo() {
    if (cf.st !== 'back' || card.gone || !cf.pkC || !window.Room || !Room.deskPoint) return null;
    var r = Room.deskPoint(cf.pkC[0], cf.pkC[1], DESK_CARD_PX / S.layout.k);
    return r ? { rest: [r[0], r[1]], at: cf.pk || 0, up: cf.pkTo === 1 } : null;
  }
  /* a tap on it lying: a small lift and drop, the sign that it moves */
  function peekNudge() {
    if (cf.st !== 'back' || card.gone || cf.pkTo === 1 || cf.pk > 0) return false;
    cf.pkNudge = (window.performance || Date).now();
    if (S.invalidate) S.invalidate();
    return true;
  }
  function peekStep(now) {
    if (cf.pkNudge) {
      var nq = (now - cf.pkNudge) / 420;
      if (nq >= 1) { cf.pkNudge = 0; cf.pk = 0; cf.pkTo = 0; return 0; }
      cf.pk = 0.14 * Math.sin(Math.PI * nq); cf.pkTo = cf.pk;
      return cf.pk;
    }
    /* up, and the reader goes (stands, sits, turns to the machine): it is
       put down, not carried in front of the eye to the next place */
    var ck = (S.cam && S.cam.want > 0.5 ? 1 : 0) + (S.cam && S.cam.lapWant > 0.5 ? 2 : 0);
    if (cf.pkTo === 1) { if (cf.pkCk === undefined) cf.pkCk = ck; else if (ck !== cf.pkCk) peekSettle(false); }
    else cf.pkCk = undefined;
    /* PK2: the hand puts it exactly where it is (`peekTo`, so the card
       stays under the finger); let go, it eases on from there in the time
       the rest of the way takes at PEEK_MS for the whole */
    var pk = cf.pk || 0, ps = cf.pkS;
    if (ps) {
      var sq = Math.min(1, (now - ps.t0) / ps.ms);
      pk = ps.from + (ps.to - ps.from) * sq * sq * (3 - 2 * sq);
      if (sq >= 1) { pk = ps.to; cf.pkS = null; cf.pkH = null; }
      else if (cf.pkH) cf.pkHk = 1 - sq * sq * (3 - 2 * sq);
    }
    cf.pk = pk;
    /* and what is DOM over the canvas (the machine's screen) is under a card
       held up in front of it: styles.css `card-peek` */
    var H = document.documentElement, up = pk > 0;
    if (H.classList.contains('card-peek') !== up) H.classList.toggle('card-peek', up);
    /* CP1 (2026-10-04): and each of them goes only where the card is over
       it. Tommy: "When you pick up the hint card, the terminal changes to be
       solid dark blue?", then "the labels on the books disappear when the
       card is picked up. make sure you catch all similarly linked things".
       They went wherever the card was, though it is nowhere near them in
       most views */
    peekCover(up);
    return pk;
  }
  /* CP1: EVERYTHING THAT IS DOM OVER THE PICTURE, under a card held up.
     The card is drawn; these are laid over the drawing, so the card cannot
     cover them and they go instead: the machine's screen (resting or the
     terminal), the paper labels on the books' covers (the desk's, and the
     phone's copies). Each goes only while the card is over it (`under-card`,
     styles.css), tested box against box on the glass. */
  var UNDER = '.tm.is-laid, .lap-passive.is-laid, .book-cover .book-label, .desk-label';
  function peekCover(up) {
    var els = document.querySelectorAll(up ? UNDER : '.under-card'), b = null;   /* down: only what it hid */
    /* the card is both its faces (held up it shows the back, the memorandum) */
    if (up) [dom.el, dom.back].forEach(function (f) {
      if (!f || getComputedStyle(f).visibility === 'hidden') return;
      var q = f.getBoundingClientRect();
      if (!(q.width > 0)) return;
      b = b ? { left: Math.min(b.left, q.left), top: Math.min(b.top, q.top), right: Math.max(b.right, q.right), bottom: Math.max(b.bottom, q.bottom) } : q;
    });
    for (var i = 0; i < els.length; i++) {
      var e = els[i], on = false;
      if (b) {
        var r = e.getBoundingClientRect();
        on = r.width > 0 && r.right > b.left && r.left < b.right && r.bottom > b.top && r.top < b.bottom;
      }
      if (e.classList.contains('under-card') !== on) e.classList.toggle('under-card', on);
    }
  }
  /* the camera's own axes in the sheet's units, from the frame's matrix:
     the eye, the way it looks through the glass's middle, its right and
     its down, and how wide and high the glass is per unit of distance */
  function camAxes() {
    var L = S.layout, m = S.VP, k = L.k;
    if (!m || !window.DOMMatrix) return null;
    var inv;
    try { inv = new DOMMatrix(Array.prototype.slice.call(m)).inverse(); } catch (e) { return null; }
    function un(x, y, z) {
      var q = inv.transformPoint(new DOMPoint(x, y, z, 1));
      if (!q.w) return null;
      return [(q.x / q.w - L.ox) / k, (-q.y / q.w - L.oy) / k, q.z / q.w / k];
    }
    var n0 = un(0, 0, -0.5), n1 = un(0, 0, 0.5), r = un(1, 0, 0.5), l = un(-1, 0, 0.5),
        t = un(0, 1, 0.5), b = un(0, -1, 0.5);
    if (!n0 || !n1 || !r || !l || !t || !b) return null;
    function sub(a, c) { return [a[0] - c[0], a[1] - c[1], a[2] - c[2]]; }
    function len(a) { return Math.hypot(a[0], a[1], a[2]); }
    function nrm(a) { var d = len(a) || 1; return [a[0] / d, a[1] / d, a[2] / d]; }
    var eye = (window.RoomObjects && RoomObjects.eye) ? RoomObjects.eye(S) : null;
    if (!eye) return null;
    var dir = nrm(sub(n1, n0));
    var dd = len(sub(n1, eye)) || 1;
    return { eye: eye, dir: dir, right: nrm(sub(r, l)), down: nrm(sub(b, t)), un: un,
             kw: len(sub(r, l)) / dd, kh: len(sub(b, t)) / dd };
  }
  /* the card at `e` of the way from lying face up (centre C) to held up
     square to the glass, the memorandum to the eye: its frame swung from
     the desk's to the camera's, a half turn about its own height on the
     way, and its centre carried up to the eye's line of sight at the
     distance where it takes PEEK_FW of the glass's width (or PEEK_FH of
     its height, whichever binds) */
  function peekQuad(C, a) {
    var cam = camAxes();
    if (!cam) return null;
    var e = a;                         /* PK2: the share itself (the settle eases) */
    var W = Math.hypot(flat.u[0], flat.u[1]), Hh = Math.hypot(flat.v[0], flat.v[1]);
    var Ua = [flat.u[0] / W, flat.u[1] / W, 0], Na = [0, 0, 1];
    var Ub = cam.right, Nb = [-cam.dir[0], -cam.dir[1], -cam.dir[2]];
    function mix(p, q) { return [p[0] + (q[0] - p[0]) * e, p[1] + (q[1] - p[1]) * e, p[2] + (q[2] - p[2]) * e]; }
    function nrm(v) { var d = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / d, v[1] / d, v[2] / d]; }
    function cross(p, q) { return [p[1] * q[2] - p[2] * q[1], p[2] * q[0] - p[0] * q[2], p[0] * q[1] - p[1] * q[0]]; }
    var N = nrm(mix(Na, Nb)), U0 = nrm(mix(Ua, Ub));
    var V = nrm(cross(N, U0)), U = cross(V, N);
    /* the half turn about the card's height, the face going away. FD2: either
       way, as the hand goes (`cf.pkDir`, set by the drag); the two end the same */
    var sp = -Math.PI * e * (cf.pkDir || 1), cs = Math.cos(sp), sn = Math.sin(sp);
    var Ue = [U[0] * cs + N[0] * sn, U[1] * cs + N[1] * sn, U[2] * cs + N[2] * sn];
    var dist = Math.max(W / (PEEK_FW * cam.kw), Hh / (PEEK_FH * cam.kh));
    /* PK2: ITS MIDDLE IS WHERE THE HAND IS. On the glass it goes straight
       from where it lay to the glass's middle, `e` of the way, unless a hand
       holds it (FD3, below), and in depth from the oak to the held distance;
       it is put on the eye's ray through that point, so the finger stays on
       the card the whole way. A little nearer while it
       turns, so its edge never goes into the oak. */
    var L = S.layout, Rs = Room.deskPoint ? Room.deskPoint(C[0], C[1], C[2]) : null;
    if (!Rs) return null;
    var sx = Rs[0] + (L.cw / 2 - Rs[0]) * e, sy = Rs[1] + (L.ch / 2 - Rs[1]) * e;
    /* FD3: in the hand, under the hand (`peekTo`) */
    var ph = cf.pkH;
    if (ph) {
      var k = cf.pkHk == null ? 1 : cf.pkHk, ge = ph[2];
      sx += (Rs[0] + (L.cw / 2 - Rs[0]) * ge + ph[0] - sx) * k;
      sy += (Rs[1] + (L.ch / 2 - Rs[1]) * ge + ph[1] - sy) * k;
    }
    var nx = sx / L.cw * 2 - 1, ny = 1 - sy / L.ch * 2;
    var r0 = cam.un(nx, ny, -0.5), r1 = cam.un(nx, ny, 0.5);
    if (!r0 || !r1) return null;
    var rd = nrm([r1[0] - r0[0], r1[1] - r0[1], r1[2] - r0[2]]);
    var d0 = Math.hypot(C[0] - cam.eye[0], C[1] - cam.eye[1], C[2] - cam.eye[2]);
    var rise = (W / 2 + TURN_RISE) * Math.abs(sn) * (1 - e);
    var dd = d0 + (dist - d0) * e - rise;
    var c = [cam.eye[0] + rd[0] * dd, cam.eye[1] + rd[1] * dd, cam.eye[2] + rd[2] * dd];
    var u = [Ue[0] * W, Ue[1] * W, Ue[2] * W], v = [V[0] * Hh, V[1] * Hh, V[2] * Hh];
    return { o: [c[0] - (u[0] + v[0]) / 2, c[1] - (u[1] + v[1]) / 2, c[2] - (u[2] + v[2]) / 2],
             u: u, v: v, c: c };
  }
  function peeking() { return (cf.pk || 0) > 0 || cf.pkTo === 1; }
  function peekUp() { return cf.pkTo === 1 && cf.st === 'back' && !card.gone; }
  function hit(x, y) {
    if (cf.st !== 'back' || !card.o || !window.Room || !Room.deskPoint) return false;
    var o = card.o, u = card.u, v = card.v, z = card.sz !== undefined ? card.sz : o[2];
    var q = [Room.deskPoint(o[0], o[1], z), Room.deskPoint(o[0] + u[0], o[1] + u[1], z),
             Room.deskPoint(o[0] + u[0] + v[0], o[1] + u[1] + v[1], z),
             Room.deskPoint(o[0] + v[0], o[1] + v[1], z)];
    if (!q[0] || !q[1] || !q[2] || !q[3]) return false;
    var k = 0;
    for (var i = 0; i < 4; i++) {
      var A = q[i], B = q[(i + 1) & 3];
      var cr = (B[0] - A[0]) * (y - A[1]) - (B[1] - A[1]) * (x - A[0]);
      if (cr > 0) k++; else if (cr < 0) k--;
    }
    return k === 4 || k === -4;
  }

  /* ==== TC1 (2026-10-07): A SECOND CARD, CLIPPED BEHIND. Tommy, of the two cards whose words will not go on one
     card at the phone's reading size (the pit's and the sea's): "I don't mind a second card clipped behind. That's
     quite cool." So when the day's half says so (`card.second`, attach's twoCard) a second card of the same stock
     lies UNDER this one, turned a little and pushed down its height, so its foot shows below this card's foot; a
     tap on what shows of it brings it forward (attach turns the words over, the cards stay where they lie). Only
     the phone on its side asks for it; the desk never does. ==== */
  var SECOND_DV = 0.34, SECOND_DU = -0.05, SECOND_A = 3.4 * Math.PI / 180;
  function secondQuad() {
    var c = Math.cos(SECOND_A), sn = Math.sin(SECOND_A);
    var u = [card.u[0] * c - card.u[1] * sn, card.u[0] * sn + card.u[1] * c, card.u[2] || 0];
    var v = [card.v[0] * c - card.v[1] * sn, card.v[0] * sn + card.v[1] * c, card.v[2] || 0];
    return { o: [card.o[0] + card.u[0] * SECOND_DU + card.v[0] * SECOND_DV,
                 card.o[1] + card.u[1] * SECOND_DU + card.v[1] * SECOND_DV, Math.max(0.2, card.o[2] - 0.6)], u: u, v: v };
  }
  function inQuad(o, u, v, z, x, y) {
    var q = [Room.deskPoint(o[0], o[1], z), Room.deskPoint(o[0] + u[0], o[1] + u[1], z),
             Room.deskPoint(o[0] + u[0] + v[0], o[1] + u[1] + v[1], z), Room.deskPoint(o[0] + v[0], o[1] + v[1], z)];
    if (!q[0] || !q[1] || !q[2] || !q[3]) return false;
    var k = 0;
    for (var i = 0; i < 4; i++) {
      var A = q[i], B = q[(i + 1) & 3];
      var cr = (B[0] - A[0]) * (y - A[1]) - (B[1] - A[1]) * (x - A[0]);
      if (cr > 0) k++; else if (cr < 0) k--;
    }
    return k === 4 || k === -4;
  }
  /* what shows of the second card: inside it and not under this one */
  function hitSecond(x, y) {
    if (!card.second || !card.o || card.gone || card.phoneAway || !window.Room || !Room.deskPoint) return false;
    var q = secondQuad();
    return inQuad(q.o, q.u, q.v, q.o[2], x, y) && !inQuad(card.o, card.u, card.v, card.o[2], x, y);
  }
  /* TP1, NC2: the card in its corner, or either card of a pair: a tap there is the card, never the map under it */
  function hitCard(x, y) {
    if (cf.st !== "home" || !card.o || card.gone || card.phoneAway || !window.Room || !Room.deskPoint) return false;
    if (inQuad(card.o, card.u, card.v, card.o[2], x, y)) return true;
    if (!card.second) return false;
    var q = secondQuad();
    return inQuad(q.o, q.u, q.v, q.o[2], x, y);
  }

  function cardPose() {
    if (!card.o) return null;
    var out = [], uv = [[0,0],[1,0],[0,1],[1,1]];
    for (var i = 0; i < 4; i++) {
      var a = uv[i][0], b = uv[i][1];
      out.push([card.o[0] + card.u[0]*a + card.v[0]*b,
                card.o[1] + card.u[1]*a + card.v[1]*b,
                card.o[2] + (card.u[2]||0)*a + (card.v[2]||0)*b + lifts()[i]]);
    }
    /* CF2: `mode` was slip / north / r47 and there is one motion now, so
       it is the constant the harness can still assert on.
       CF3: + the card's own move (`st`), which face is up, and whether
       it is out of the picture. */
    return { mode: 'slip', corners: out, slipOff: slip.off, st: cf.st, side: cf.side || 0,
             turning: !!cf.turn, peek: cf.pk || 0,
             faceUp: card.faceUp !== false, gone: !!card.gone,
             o: card.o.slice(), u: card.u.slice(), v: card.v.slice(),
             flat: flat ? { o: flat.o.slice(), u: flat.u.slice(), v: flat.v.slice() } : null,
             /* CF3 (b): the hidden quad as last laid (null: none), in the
                card's own (a, b), and the leaf and eye it was cut against */
             clip: occNow ? occNow.map(function (q) { return q.slice(); }) : null,
             leaf: leafNow(),
             eye: (window.RoomObjects && RoomObjects.eye) ? RoomObjects.eye(S) : null };
  }
  if (!D.cardPose) D.cardPose = cardPose;

  grabDom();
  place();
  return { vao: vao, card: card, dom: dom, off: NO_CARD, pCard: pCard,
           cardPose: cardPose, hit: hit, hitSecond: hitSecond, hitCard: hitCard, peek: peek, peeking: peeking, peekUp: peekUp,
           peekTo: peekTo, peekSettle: peekSettle, peekDir: peekDir, peekInfo: peekInfo, peekNudge: peekNudge, restBox: restBox,
           place: place, layoutDom: layoutDom, sun: sunOf,
           shadowFigures:shadowFigures, draw: draw };
};


/* ==== FU2 (2026-09-27): THE NOTE UNDER THE MAP IS GONE ================
   X2's second card, lying on the oak under the sheet with N1's plan of the
   six glyphs on it, ringed and re-inked as each egg landed (restored by
   NC1). FU1 put each glyph in the margin of its own hint on the file
   card's back, and the card turns over beside the fold, so the plan was a
   second thing marked off for the same six. Tommy: "it feels like the
   card with the glyphs is almost dead now?" ... "yes, delete it." So
   `D.note`, `D.noteClearOf`, the engine's drawNote/needNote/noteQuad and
   the #fold-note markup are deleted, and marks.json's `plan` with them.
   The fold still strikes its mark (engine.js); the glyph drawings are the
   hint list's now (base.njk `.dc-g`), which progress.js reads for the
   found row on the sheet. ==== */

D.attach = function (H) {
  var gl = H.gl, S = H.S, C = window.RoomField.C;
  var EXAG = H.EXAG, MAXT = C.MAX_TILT;
  var NO_CARD = Q.get('card') === '0';
  var STEPS = Math.max(6, +(Q.get('picksteps') || 34));

  /* R12: the paper, made before frame 1. A caller that is not the
     engine gets one made here. */
  var PAPER = H.card || D.card(H);
  var card = PAPER.card, dom = PAPER.dom, vao = PAPER.vao;

  /* ---- the record, on the main thread ---------------------------- */
  var POSTS = readJSON('survey-posts') || [];
  var EVD = readJSON('survey-events') || {};
  var EVENTS = EVD.events || {}, BANK = EVD.bankHolidays || {};

  /* ================================================================
     THE FIELD, ON THE CPU. The pick needs the same surface the vertex
     shader displaces the grid to — `fieldAt(m)` (JG1) times
     `mouldMask`, the exaggeration and the relief — and nothing else in
     the engine reads the field back. These two are the GLSL of
     FIELD_GLSL and FRONT_GLSL, line for line.
     ================================================================ */
  function bedAt(mx, my) {
    var f = S.fld;
    /* JG1: the vertex shader's own read — field.js fieldAtJS is FIELD_GLSL's
       fieldAt (Catmull-Rom, the margin in whole cells, clamped to the
       array), so the pick, the card and the keel stand on the mesh. */
    var b = RoomField.fieldAtJS(f, mx, my);
    /* ==== R46: AND WEST OF JANUARY 2022 THE FLOOR IS NOT THE ARRAY.
       Past the margin the texture is CLAMP_TO_EDGE and the last column is
       the record's own floor, so the two lines above read -COAST at every
       point from 130 units west of the line to 1986 — X5 OPEN 5, the lead
       calling 21 fathoms at every cast. With ?west=bed the ground out
       there is an ANALYTIC field (field.js SEABED_GLSL, mirrored in
       seabedJS), so this is the same surface the vertex shader displaces
       the grid to and the same one `Room.bedAt` has always promised.
       seabedJS is 0 without the switch and 0 east of the line, so the
       pick, the day card and the harbour are what they were. ==== */
    /* X14 (g): and the voyage's own ground — the fairway's banks and the
       foul ground round each mark — is part of the same seabed now
       (field.js voyVec / voyGroundJS), so the lead, the grounding rule and
       the depth tints all read ONE surface. 0 east of the record's west
       line, so the pick, the day card and the harbour are unmoved. */
    return b + RoomField.seabedJS(f, mx, my) + RoomField.voyGroundJS(f, mx, my);
  }
  /* ==== AL2 (2026-09-26): THE PAGE'S GROUND IS THE DRAWN GROUND. JG2 left
     the note: this mask still tapered over the RECORD's own rows, though
     R35 moved the GPU's skirt out to the margin's edge and R41 released it
     west of the surveyed start — so the pick, the day card and the view's
     aim (engine.js aimLift) put the ground at 0 a few dozen units past the
     record's north/south line where the drawn ground stands up (at
     (1400, 800) 0 against 15 units). These two are now engine.js's GLSL
     mouldMask (FRONT_CORE, the analytic branch the bake is drawn from) and
     mouldMaskT (FRONT_TAIL), term for term, with the same uniforms worked
     out the way setCommon works them out: uMould is the margin's rect
     (margin.run out west, north and south), uWestSea the open-sea run
     (off under ?seamat=0), uScarp the cut and its run, and terraW is on
     unless ?downs=0. ==== */
  var WEST_SEA = Q.get('seamat') !== '0';
  var TERRA_ON = (function (q) { return q === null || (+q || 0) > 0; })(Q.get('downs'));
  function mouldMask(sx, sy) {
    var f = S.fld, fr = f.front, mg = f.margin || {}, MRUN = mg.run || 0;
    var ws = WEST_SEA ? (mg.openSea || 0) : 0;
    var wW = ws > 0 ? smst(0, ws, Math.max(fr.a0 - sx, 0)) : 0;
    var m = (smst(-26, 0, sx - (fr.west - MRUN)) * (1 - wW) + wW)
          * smst(-26, 0, fr.east - sx)
          * (smst(-26, 0, Math.min(sy - (fr.c0 - MRUN), (fr.c1 + MRUN) - sy)) * (1 - wW) + wW);
    if (m <= 0) return 0;
    if (fr.all || fr.none) return m;
    var rowEdge = C.py + (fr.turned ? fr.row + 1 : fr.row) * C.ch;
    /* AU2: the row edge is a 26-unit slope, not a step (engine.js mouldMask) */
    var into = fr.turned ? sy - rowEdge : rowEdge - sy;
    var sr = into <= -26 ? 0 : into >= 26 ? 1 : (function (t) { return t * t * (3 - 2 * t); })((into + 26) / 52);
    var e = clamp((sx - (fr.e1 - 40)) / 40, 0, 1);
    var cut = C.CONJECTURE_KEEP + (1 - C.CONJECTURE_KEEP) * (1 - e);
    m *= cut + (1 - cut) * sr;
    /* R35: the mask's cut is the backstop at the SCARP's foot now — the
       field falls over the scarp itself (engine.js FRONT_GLSL). */
    var sc = mg.scarp || 0;
    m *= 1 - smst(fr.e1 + sc, fr.e1 + sc + 0.8, sx);
    return m;
  }
  /* R40's mouldMaskT: east of the cut the mould is the north-south skirt */
  function mouldMaskT(sx, sy) {
    var f = S.fld, fr = f.front, mg = f.margin || {}, MRUN = mg.run || 0, sc = mg.scarp || 0;
    var cut = fr.none ? fr.west : (fr.all ? fr.east : fr.e1);
    var tw = TERRA_ON && sc > 0 ? smst(cut, cut + sc, sx) : 0;
    if (tw <= 0) return mouldMask(sx, sy);
    var mNS = smst(-26, 0, Math.min(sy - (fr.c0 - MRUN), (fr.c1 + MRUN) - sy));
    if (tw >= 1) return mNS;
    return mouldMask(sx, sy) * (1 - tw) + mNS * tw;
  }
  function reliefOf(t) { var k = clamp(t / MAXT, 0, 1); return 1 - (1 - k) * (1 - k); }
  /* ==== FR2 (2026-09-21): AND THE GROUND THE PARTY HAS PACED. Tommy:
     "you walk in one direction, say left, and the ground raises up. I
     believe the hitbox for everything is still unraised … you have to
     hover where the person would have been before it was raised up to
     see the arrows." It was. The ground and the sprites both rise through
     field.js paceVec (the seven pads the engine hands them in uPace), but
     this height field — the one the pick marches against and `toScreen`
     projects through — read the bed alone, so under a tilt the figure and
     its chevrons stood a pad's height above the surface the hand was
     tested on. This is paceVec's own value term, number for number: the
     pad is a cell half-wide, its edge an eleventh of that either side,
     and the same cubic pad edge (field.js padE). The engine's D.pace is
     the array the uniform is uploaded from, so the two can not drift. */
  function padE(d, a, b) {
    if (d <= a) return 1;
    if (d >= b) return 0;
    var t = (d - a) / (b - a);
    return 1 - t * t * (3 - 2 * t);
  }
  function paceAt(mx, my) {
    var Dm = S.doom;
    if (!Dm || !Dm.on || !Dm.any) return 0;
    var kx = C.cw / 2, ky = C.ch / 2, rx = 0.11 * kx, ry = 0.11 * ky, p = Dm.pace, a = 0, i;
    for (i = 0; i < 7; i++) {
      var h = p[i * 4 + 2];
      if (h <= 0) continue;
      var ex = padE(Math.abs(mx - p[i * 4]), kx - rx, kx + rx);
      if (ex <= 0) continue;
      var ey = padE(Math.abs(my - p[i * 4 + 1]), ky - ry, ky + ry);
      if (ey <= 0) continue;
      a += h * ex * ey;
    }
    return a;
  }
  function heightAt(mx, my) {
    return (bedAt(mx, my) + paceAt(mx, my)) * mouldMaskT(mx, my) * EXAG * reliefOf(S.tilt);
  }
  D.heightAt = heightAt;

  /* ================================================================
     THE RIGID BODY, ON THE CPU — mapToSheet, and its inverse. The
     GLSL is XFORM_GLSL; every number below comes from setCommon().
     ================================================================ */
  function frame() {
    var L = S.layout;
    var kk = S.portal.w / S.view.w / L.k;
    var rel = reliefOf(S.tilt);
    var vx0 = S.view.x + S.view.w * 0.5, vy0 = S.view.y + S.view.h * 0.5;
    /* R44 (b): the one aim-lift, engine.js's aimLift — the same figure the
       frame is drawn with, R33 (d) included. The two lines it replaces are
       kept as the fallback for a handle that predates it. */
    var lift = S.aimLift ? S.aimLift(S.view, rel) * kk : heightAt(vx0, vy0) * kk;
    return {
      kk: kk, ct: Math.cos(S.tilt), st: Math.sin(S.tilt),
      cy: Math.cos(S.yaw), sy: Math.sin(S.yaw), lift: lift,
      cx: (S.portal.l - L.ox) / L.k + (S.portal.w / L.k) * 0.5,
      cyy: (S.portal.t - L.oy) / L.k + (S.portal.h / L.k) * 0.5,
      vx: S.view.x + S.view.w * 0.5, vy: S.view.y + S.view.h * 0.5
    };
  }
  /* map -> sheet (x, y, up), with a height in map units */
  function mapToSheet(mx, my, h, F) {
    F = F || frame();
    var dx = (mx - F.vx) * F.kk, dy = (my - F.vy) * F.kk;
    var rx = dx * F.cy - dy * F.sy, ry = dx * F.sy + dy * F.cy;
    var z = h * F.kk;
    return [F.cx + rx, F.cyy + (ry * F.ct - z * F.st + F.lift * F.st), ry * F.st + z * F.ct];
  }
  D.mapToSheet = mapToSheet;
  /* sheet (x, y, up) -> map, and the height it implies */
  function sheetToMap(px, py, pz, F) {
    var rx = px - F.cx;
    var A = py - F.cyy - F.lift * F.st, B = pz;
    var ry = A * F.ct + B * F.st, z = -A * F.st + B * F.ct;
    var dx = rx * F.cy + ry * F.sy, dy = -rx * F.sy + ry * F.cy;
    return [F.vx + dx / F.kk, F.vy + dy / F.kk, z / F.kk];
  }

  /* ================================================================
     THE PICK

     tilt 0: the ground has no height (reliefOf(0) = 0) and the desk
     pose maps the sheet plane 1:1 to css pixels (E1 §h proved the
     perspective and the orthographic identical there), so screen ->
     sheet -> map is one affine inverse and nothing is marched.

     tilted: the ray is unprojected once, carried into the map's own
     rigid-body frame — where the surface is a plain height field —
     and marched against the FIELD'S LATTICE. No readback, no pick
     pass, nothing read off the GPU at all.
     ================================================================ */
  var picks = { n: 0, marched: 0, steps: 0, ms: 0 };

  /* cx, cy are CANVAS-relative css pixels; the engine takes the host's
     rect once per gesture rather than per move (a forced layout a move
     is exactly what v32 A measured away). */
  function mapAt(cx, cyp, nSteps) {
    var L = S.layout, F = frame();
    var t0 = performance.now();
    picks.n++;
    var out;
    if (S.tilt < 1e-4 && S.cam.pitch < 1e-4 && Math.abs(S.cam.k - 1) < 1e-6 && !S.cam.room && (S.frameK || 1) === 1) {   /* MM1 */
      /* ARITHMETIC. p is the sheet point straight off the css pixel. */
      var px = (cx - L.ox) / L.k, py = (cyp - L.oy) / L.k;
      var m = sheetToMap(px, py, 0, F);
      out = { x: m[0], y: m[1], steps: 0 };
    } else {
      out = march(cx, cyp, F, nSteps || STEPS);
    }
    picks.ms += performance.now() - t0;
    return out;
  }

  /* the ray, in the map's own frame */
  function rayOf(cx, cyp, F) {
    var L = S.layout;
    var inv = invert(S.VP);
    if (!inv) return null;
    var nx = cx / L.cw * 2 - 1, ny = 1 - cyp / L.ch * 2;
    var a = unproject(inv, nx, ny, -1), b = unproject(inv, nx, ny, 1);
    if (!a || !b) return null;
    /* world -> sheet */
    function toSheet(w) {
      return [(w[0] - L.ox) / L.k, (-w[1] - L.oy) / L.k, w[2] / L.k];
    }
    var A = toSheet(a), B = toSheet(b);
    /* sheet -> the map frame (rx, ry, z), which is a rotation plus a
       translation, so a direction drops the translation */
    function pt(p) {
      var rx = p[0] - F.cx, Aa = p[1] - F.cyy - F.lift * F.st, Bb = p[2];
      return [rx, Aa * F.ct + Bb * F.st, -Aa * F.st + Bb * F.ct];
    }
    var P = pt(A), Qq = pt(B);
    return { o: P, d: [Qq[0] - P[0], Qq[1] - P[1], Qq[2] - P[2]] };
  }

  /* the height of the surface at a point of the (rx, ry) plane, in the
     same units z is measured in */
  function surfAt(rx, ry, F) {
    var dx = rx * F.cy + ry * F.sy, dy = -rx * F.sy + ry * F.cy;
    var mx = F.vx + dx / F.kk, my = F.vy + dy / F.kk;
    return { z: heightAt(mx, my) * F.kk, x: mx, y: my };
  }

  function march(cx, cyp, F, N) {
    var R = rayOf(cx, cyp, F);
    if (!R) return null;
    var rel = reliefOf(S.tilt);
    /* FR2: the slab's ceiling takes the paced pads too, or a march that
       starts under a raised day would begin inside the ground */
    var paceHi = (S.doom && S.doom.on && S.doom.any) ? S.doom.paceH : 0;
    var zHi = (S.fld.maxHeight + paceHi) * EXAG * rel * F.kk + 1e-3;
    var zLo = -(S.fld.COAST + 0.2) * EXAG * rel * F.kk - 1e-3;
    /* the slab of world the ground can be in; outside it there is
       nothing to hit, so the march never leaves it */
    var t0 = 0, t1 = 1;
    if (Math.abs(R.d[2]) > 1e-9) {
      var ta = (zHi - R.o[2]) / R.d[2], tb = (zLo - R.o[2]) / R.d[2];
      t0 = Math.max(0, Math.min(ta, tb));
      t1 = Math.min(1, Math.max(ta, tb));
    }
    if (!(t1 > t0)) return null;
    var prev = null, pt = null, i;
    for (i = 0; i <= N; i++) {
      var t = t0 + (t1 - t0) * (i / N);
      var rx = R.o[0] + R.d[0] * t, ry = R.o[1] + R.d[1] * t, z = R.o[2] + R.d[2] * t;
      var s = surfAt(rx, ry, F);
      var f = z - s.z;
      if (prev !== null && prev.f > 0 && f <= 0) {
        /* the crossing is inside this cell of the march; the field is
           bilinear, so bisect it down */
        var lo = prev.t, hi = t, fl = prev.f;
        for (var k = 0; k < 16; k++) {
          var mid = (lo + hi) * 0.5;
          var mrx = R.o[0] + R.d[0] * mid, mry = R.o[1] + R.d[1] * mid;
          var mz = R.o[2] + R.d[2] * mid;
          var ms = surfAt(mrx, mry, F);
          if (mz - ms.z > 0) { lo = mid; fl = mz - ms.z; } else hi = mid;
        }
        var fs = surfAt(R.o[0] + R.d[0] * hi, R.o[1] + R.d[1] * hi, F);
        picks.marched++; picks.steps += i;
        return { x: fs.x, y: fs.y, steps: i };
      }
      prev = { t: t, f: f };
      pt = s;
    }
    /* the ray never came down through the ground: it is over the sea or
       past the record, and the last sample is the honest answer */
    picks.marched++; picks.steps += N;
    return pt ? { x: pt.x, y: pt.y, steps: N, miss: true } : null;
  }

  /* ---- 4x4 inverse, for the one unproject a tilted pick needs ----- */
  function invert(m) {
    var inv = new Array(16), i;
    inv[0] = m[5]*m[10]*m[15] - m[5]*m[11]*m[14] - m[9]*m[6]*m[15] + m[9]*m[7]*m[14] + m[13]*m[6]*m[11] - m[13]*m[7]*m[10];
    inv[4] = -m[4]*m[10]*m[15] + m[4]*m[11]*m[14] + m[8]*m[6]*m[15] - m[8]*m[7]*m[14] - m[12]*m[6]*m[11] + m[12]*m[7]*m[10];
    inv[8] = m[4]*m[9]*m[15] - m[4]*m[11]*m[13] - m[8]*m[5]*m[15] + m[8]*m[7]*m[13] + m[12]*m[5]*m[11] - m[12]*m[7]*m[9];
    inv[12] = -m[4]*m[9]*m[14] + m[4]*m[10]*m[13] + m[8]*m[5]*m[14] - m[8]*m[6]*m[13] - m[12]*m[5]*m[10] + m[12]*m[6]*m[9];
    inv[1] = -m[1]*m[10]*m[15] + m[1]*m[11]*m[14] + m[9]*m[2]*m[15] - m[9]*m[3]*m[14] - m[13]*m[2]*m[11] + m[13]*m[3]*m[10];
    inv[5] = m[0]*m[10]*m[15] - m[0]*m[11]*m[14] - m[8]*m[2]*m[15] + m[8]*m[3]*m[14] + m[12]*m[2]*m[11] - m[12]*m[3]*m[10];
    inv[9] = -m[0]*m[9]*m[15] + m[0]*m[11]*m[13] + m[8]*m[1]*m[15] - m[8]*m[3]*m[13] - m[12]*m[1]*m[11] + m[12]*m[3]*m[9];
    inv[13] = m[0]*m[9]*m[14] - m[0]*m[10]*m[13] - m[8]*m[1]*m[14] + m[8]*m[2]*m[13] + m[12]*m[1]*m[10] - m[12]*m[2]*m[9];
    inv[2] = m[1]*m[6]*m[15] - m[1]*m[7]*m[14] - m[5]*m[2]*m[15] + m[5]*m[3]*m[14] + m[13]*m[2]*m[7] - m[13]*m[3]*m[6];
    inv[6] = -m[0]*m[6]*m[15] + m[0]*m[7]*m[14] + m[4]*m[2]*m[15] - m[4]*m[3]*m[14] - m[12]*m[2]*m[7] + m[12]*m[3]*m[6];
    inv[10] = m[0]*m[5]*m[15] - m[0]*m[7]*m[13] - m[4]*m[1]*m[15] + m[4]*m[3]*m[13] + m[12]*m[1]*m[7] - m[12]*m[3]*m[5];
    inv[14] = -m[0]*m[5]*m[14] + m[0]*m[6]*m[13] + m[4]*m[1]*m[14] - m[4]*m[2]*m[13] - m[12]*m[1]*m[6] + m[12]*m[2]*m[5];
    inv[3] = -m[1]*m[6]*m[11] + m[1]*m[7]*m[10] + m[5]*m[2]*m[11] - m[5]*m[3]*m[10] - m[9]*m[2]*m[7] + m[9]*m[3]*m[6];
    inv[7] = m[0]*m[6]*m[11] - m[0]*m[7]*m[10] - m[4]*m[2]*m[11] + m[4]*m[3]*m[10] + m[8]*m[2]*m[7] - m[8]*m[3]*m[6];
    inv[11] = -m[0]*m[5]*m[11] + m[0]*m[7]*m[9] + m[4]*m[1]*m[11] - m[4]*m[3]*m[9] - m[8]*m[1]*m[7] + m[8]*m[3]*m[5];
    inv[15] = m[0]*m[5]*m[10] - m[0]*m[6]*m[9] - m[4]*m[1]*m[10] + m[4]*m[2]*m[9] + m[8]*m[1]*m[6] - m[8]*m[2]*m[5];
    var det = m[0]*inv[0] + m[1]*inv[4] + m[2]*inv[8] + m[3]*inv[12];
    if (!det) return null;
    det = 1 / det;
    for (i = 0; i < 16; i++) inv[i] *= det;
    return inv;
  }
  function unproject(inv, x, y, z) {
    var w = inv[3]*x + inv[7]*y + inv[11]*z + inv[15];
    if (!w) return null;
    return [(inv[0]*x + inv[4]*y + inv[8]*z + inv[12]) / w,
            (inv[1]*x + inv[5]*y + inv[9]*z + inv[13]) / w,
            (inv[2]*x + inv[6]*y + inv[10]*z + inv[14]) / w];
  }

  /* ================================================================
     THE DAY A MAP POINT IS ON

     The week is read off the along axis and the row off the across
     axis — and the DAY is then the one whose own cell that row is, the
     plough's turn included. `rowOf(w, d) = (w & 1) ? 6 - d : d` is its
     own inverse, so this is the same arithmetic `cellRect` draws the
     cell with and the two cannot disagree (the v32 A fault).
     ================================================================ */
  function dayAtMap(mx, my) {
    var f = S.fld, dys = f.days;
    if (!dys) return null;
    var w = Math.floor((mx - C.px) / C.cw);
    var r = Math.floor((my - C.py) / C.ch) - C.EDGE_ROWS;
    if (r < 0 || r > 6) return null;
    var d = (w & 1) ? 6 - r : r;
    var wi = w - f.baseW;
    if (wi < 0 || wi >= f.nWeeks) return null;
    if (!dys.inr[wi * 7 + d]) return null;
    var date = isoOf(dys.epochMs + (w * 7 + d) * DAY_MS);
    if (date > f.today) return null;
    return date;
  }
  D.dayAtMap = dayAtMap;
  /* EH1 (2026-09-26): the hand north or south of the plate holds the day at
     that column's edge. Tommy: "If you go beyond Sunday, 5th of July, that
     whole area should register as Sunday, 5th of July, still, not today's
     date". The hover's alone — dayAtMap stays exact for everything that
     asks whether a point IS surveyed (the terminal, WebMCP). */
  function dayAtEdge(mx, my) {
    var r = Math.floor((my - C.py) / C.ch) - C.EDGE_ROWS;
    if (r >= 0 && r <= 6) return null;
    return dayAtMap(mx, C.py + (Math.min(6, Math.max(0, r)) + C.EDGE_ROWS + 0.5) * C.ch);
  }

  /* where a day's cell is, in map units — the middle of it */
  function cellOf(date) {
    var f = S.fld, dys = f.days;
    if (!dys || !date) return null;
    var n = Math.round((Date.parse(date + 'T00:00:00Z') - dys.epochMs) / DAY_MS);
    if (!isFinite(n)) return null;
    var w = Math.floor(n / 7), d = n - w * 7;
    var wi = w - f.baseW;
    if (wi < 0 || wi >= f.nWeeks) return null;
    var r = (w & 1) ? 6 - d : d;
    return [C.px + (w + 0.5) * C.cw, C.py + (r + C.EDGE_ROWS + 0.5) * C.ch];
  }
  D.cellOf = cellOf;

  /* ================================================================
     WHAT THE CARD SAYS. On the sheet every one of these came off the
     day's own `.sv-hit` rectangle (survey-map.js:4240); here they come
     off the record itself, by the same rules.
     ================================================================ */
  function countOf(date) {
    var f = S.fld, dys = f.days;
    var n = Math.round((Date.parse(date + 'T00:00:00Z') - dys.epochMs) / DAY_MS);
    var w = Math.floor(n / 7), d = n - w * 7, wi = w - f.baseW;
    if (wi < 0 || wi >= f.nWeeks) return 0;
    return dys.count[wi * 7 + d] || 0;
  }
  function dayLine(date) {
    var dt = new Date(date + 'T00:00:00Z');
    return DAY_NAMES[(dt.getUTCDay() + 6) % 7] + ' ' + dt.getUTCDate() + ' ' +
           MONTHS[dt.getUTCMonth()] + ' ' + dt.getUTCFullYear();
  }
  function shortDate(date) {
    var dt = new Date(date + 'T00:00:00Z');
    return DAY_NAMES[(dt.getUTCDay() + 6) % 7].slice(0, 3) + ' ' + dt.getUTCDate() + ' ' +
           MONTHS[dt.getUTCMonth()].slice(0, 3);
  }
  function figLine(date) {
    var c = countOf(date), ev = EVENTS[date];
    /* BN1 (2026-09-30): a bank holiday says what it is whether or not it
       was worked. Tommy: "Do all bank holidays say what they are on their
       file card?" — they did not: the name came only with nought, so 12 of
       41 (New Year's Day 2026, 32 marks) read as any other day. */
    var bh = !ev ? BANK[date] : null;
    if (bh && c > 0) return c.toLocaleString() + ' mark' + (c === 1 ? '' : 's') + ' · ' + bh;
    if (bh) return bh + ' — no marks recorded';
    if (ev && c > 0) return c.toLocaleString() + ' mark' + (c === 1 ? '' : 's') + ' · ' + ev.name;
    if (ev) return ev.name;
    if (c === 0) return 'No marks recorded';
    return c.toLocaleString() + ' mark' + (c === 1 ? '' : 's');
  }
  /* LV1 (2026-09-26): the level line is gone — "Above the level of a
     directed day, by 69" and its kin. Tommy: "Let's say you're glancing
     your mouse over different days. Do you really want to have to read
     this pointless bit of text? ... you can kind of see the level from the
     map. That's the whole point, right? Let's kill the text for now".
     A visual in its place is parked on ROADMAP.md's list. */
  function markOn(date) {
    for (var i = 0; i < POSTS.length; i++) if (POSTS[i].date === date) return POSTS[i];
    return null;
  }
  D.markOn = markOn;

  /* ---- THE CARD'S TYPE — the half that is the record's. The stock is
     `D.card`'s, drawn before the field landed (R12); here is the
     typing. ---- */
  /* R12: the element itself is the paper's; these are the nodes the
     RECORD fills, and the control that opens an essay. */
  function grabDom() {
    if (!dom.el) return;
    dom.state = document.getElementById('dc-state');
    dom.date = document.getElementById('dc-date');
    dom.fig = document.getElementById('dc-fig');
    dom.level = document.getElementById('dc-level');
    dom.settle = document.getElementById('dc-settle');
    dom.btn = document.getElementById('dc-essay');
    dom.btnT = document.getElementById('dc-essay-t');
    dom.btnGo = dom.btn && dom.btn.querySelector('.dc-essay-go');
    /* ==== X6 (c): ONCE, AND NOT ONCE PER RECORD. `D.attach` runs again
       on every `Room.load`, and `grabDom` with it — so the essay button
       collected one more click handler per handle loaded, and after twenty
       loads one click on the card opened the book twenty-one times. The button outlives the module (it is in the
       page's own markup, not built here), so the guard is on the button.
       Found by the dispose pass: the GL objects were the leak that was
       looked for and this was standing beside them. ==== */
    if (dom.btn && !dom.btn.__x6wired) {
      dom.btn.__x6wired = 1;
      dom.btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (dom.btn.__present) {
          if (window.Room && Room.cardDismiss) Room.cardDismiss('button');
          else api.dismiss('button');
          return;
        }
        if (!dom.btn.__date) return;
        /* N2: the day was driven through the card with a pin first and
           then the book was opened at it. There is no pin: the control
           opens the book, which is the whole of what it was ever for. */
        if (window.RoomBooks) RoomBooks.openByDate(dom.btn.__date, { fly: false });
      });
    }
  }

  /* ================================================================
     THE STATE — what is being read, and the hold

     `over`   the day the pointer is actually on, this instant.
     `held`   `over`, outliving it by HOLD_MS — the day the HAND is on.

     ==== N2: AND THE HOLD REACHES THE CARD ==========================
     There is no lock on the day any more (see the head of this file), so
     `held` is the whole of what the card reads — and the one thing the
     600 ms hold could not do on its own was let the hand ARRIVE. A
     journal day puts a "read the post" control on the card; the reach
     from the cell to that button is a travel across the paper, and at
     the far end of it the hold had expired and taken the button with it.

     So the hold is REFUSED while the hand is on the card itself
     (`onCard`, below: the engine hands over the client point it has
     already read for the pick, and the card's own DOM rect answers).
     The 600 ms is what covers the corridor in between; the card covers
     the destination. Leave both and the card goes back to rest.

     The card's DOM is `pointer-events: none` but for that one button, so
     the pointer over the card is still a pointer on the glass and the
     engine's own move handler is what asks. ==== */
  var over = null, held = null, presented = null, onCard = 0;
  var holdT = null, drawn = null, state = null;
  /* CW1: how many times the card has been re-typed, so that "the DOM is
     touched only when what it would say has changed" is a number and not
     a claim. Read through `Room.dayStats()`. */
  var paints = 0;

  /* ================================================================
     V1 (5) — THE PUNCH CARD, and the two lines that are not about a day

     Tommy, 2026-09-18: *"Maybe the whole time we're on the voyage, we
     should have the card on the right display the different events. They
     wouldn't show them before they're discovered, but we could show the
     names because the names are relatively cryptic. We just wouldn't show
     where they are, and as you find one, he gets a nice pop-up card (the
     one that comes up and gives you more information about it). On the
     thing on the right, our punch card, you cross through the one you
     found. That way, you could clearly see if you've missed one as
     well."* And, of losing her: *"if you scroll away from the boat's
     mid-voyage, it can be quite tricky to find it again."* And, of the
     way in: *"it's very unclear that you need to scroll back to the start
     of the map and click on the port ... I wonder if we should show a
     constant message if you scroll before 2022."*

     So the ONE instrument this sheet already has answers all three, and
     nothing new appears anywhere. The card is never empty (E6) and what
     it is at rest depends on what the reader is doing:

       a day under the hand      -> that day, exactly as it always was.
                                    The hover wins over the punch card
                                    while the pointer is on a day, and
                                    the punch card comes back at rest.
       a mark just charted       -> the discovery card, untouched.
       she is under way          -> THE PUNCH CARD: every mark's NAME, in
                                    the file's own order, one to a line,
                                    struck through when it is charted.
                                    The names only: no date, no position,
                                    no note. The note is what the pop-up
                                    carries when the mark is found, and
                                    the position is the whole game. There
                                    is no "3 of 7" anywhere — the strokes
                                    ARE the count, which is what a punch
                                    card is. An unnamed mark (V1 dropped
                                    two names) prints as one em dash, so
                                    the list stays as long as the list.
       she is under way and OFF  -> and under the list, one line saying
       THE SCREEN                   which way she lies, and the card takes
                                    a pointer: clicking it pans the chart
                                    to her, through the engine's own
                                    `Room.centreOn`.
       she is moored and the     -> the rest line carries the way in.
       sea is under the pointer

     Both lines are PLACEHOLDERS in voyage.json (`hint.moored`,
     `hint.away`) and Tommy writes them; empty either and it is not typed.
     The nodes are built once, here, and not in base.njk, because they are
     the voyage's and the page without a voyage must not carry them.

     ==== CW1 (2026-09-19): WEST OF THE RECORD THE DAY IS GONE ==========
     Tommy: *"Did we ever change the punch card so when you go west of the
     beginning of 2022, it gives you the hint about the voyage? I feel
     like I see a small grey line at the top, but I would rather it just
     replaced all the nonsense about the current day. In fact, why don't
     we merge the card that shows the list of voyage items you've crossed
     off with the hint?"*

     What stood: west of the record and moored, the card kept the whole
     of the current day — Friday 11 September 2026, the count, the level,
     the settle line — and added the hint on `.dc-state`, the 9.2 px grey
     row above the red head rule. That row is one line high with
     `overflow: hidden`, so a hint of any length was CLIPPED: the small
     grey line at the top, and the day underneath it.

     Now: the moment the view (or the hand) is west of January 2022 the
     card is the VOYAGE'S CARD, and it carries no day at all — no date,
     no count, no level, no settle. IT IS ONE CARD. Tommy: *"I reckon we
     keep it all the same west of the record, with only slight
     variations ... Where's the record in underway? I think it should
     just be the same card details."* So every state prints the same
     four things, and ONLY THE TOP LINE CHANGES:

       the TOP LINE, where the day's lines were, under the head rule:
         moored      `hint.moored`   — the way in
         under way   `hint.underway` — she is out there
         arrived     `hint.arrived`  — the arrival, with the passage time,
                     and it STAYS: the passage is remembered the way the
                     charted marks are (`tl-survey-passage`, a sibling of
                     voyage.js's `tl-survey-charted`), so a reader who
                     comes back weeks later is met by the end of the
                     voyage rather than the invitation to start it.
       the SEVEN MARKS, one to a line: unticked at her moorings, struck
         as they are charted, all struck at the berth. V1's list,
         unchanged but for its leading (see below).
       the AWAY LINE, when she is drawn nowhere on the window: where she
         lies, and a click on the card pans to her. V1's, unchanged.
       LONDON, at the foot: the hour the sky is at and the wind the boat
         is sailing in, in plain words. `londonLine` below.

     AND THE CARD SAYS NOTHING THE GAME SAYS. An earlier cut of this
     card carried the last line `say` had said — the beat, the irons, the
     leadsman's soundings, the bank. Tommy: *"the wind and the stuff like
     that is getting far too complicated! You shouldn't need to be a
     professional sailor to play my mini game"*, then *"I don't think we
     need the 'she will not go on'. Just drop it!"* A mark found is
     already the strike through the list and the card in the middle of
     the window, which he keeps as it is. So the log stays in the log,
     voyage.js is not touched by this card at all, and the three
     placeholders above are every word the reader gets.

     THE LEADING. Three lines went on to a 143 px card (the top line is
     now always typed, and London is new). Measured at the harbour view
     (card 269 x 143 css, `--dck` 0.8406, the red head rule at 29.3 and
     the content box ending at 136.3): the seven marks alone were 70 px
     of it. Their leading is 10.5 --dck units instead of V1's 11.4 — the
     FACE AND THE SIZES ARE UNTOUCHED, it is the leading only. The away
     line's leading and the two foot margins came down by the same kind of
     hair for the same reason: the worst case this card can reach is a
     two-line top, seven marks, the away line AND London together, and it
     has to land inside the stock. The numbers are in REPORT-room-CW1.md.

     East of the record nothing changed. While she is at her moorings and
     the view is on the record, the card is the day's card, to the byte.
     A presented card still wins everywhere, because it is something the
     reader did on purpose.
     ================================================================ */
  var punch = null, punchLines = [], punchAway = null, punchKey = null;
  var punchTop = null, punchLondon = null;                      /* CW1 */
  var awayNow = '', topNow = '', londonNow = '', lastPtrX = null, lastPtrY = null;
  var terraOn = false;              /* D2 (2): is the card terra's just now? */
  var terraKey = '';                /* and which day of it: the hint follows the hand */
  var doomOn = '';                  /* D2 (5): and is it the pit's — which text? */
  var PTS = ['N','NNE','NE','ENE','E','ESE','SE','SSE',
             'S','SSW','SW','WSW','W','WNW','NW','NNW'];
  function voy() {
    var V = root.Room && root.Room.voyage;
    return (V && V.state && V.charted) ? V : null;
  }
  function buildPunch() {
    if (punch || !dom.el) return;
    punch = document.createElement('div');
    punch.id = 'dc-punch';
    punch.className = 'dc-punch';
    /* the card's own grid, the card's own type: only the sizes are set
       here, and they are the card's own --dck units like every other line
       of it, so the list holds its proportion at every window */
    /* THE HEAD RULE IS THE CANVAS'S, NOT THE CSS'S: day.js draws a red
       rule across the card's head at a fixed height and the DATE's
       baseline is set to sit on it. Measured on the first cut of this
       card (room-rev10/V1-punch-strip.png): with no margin the list's
       first line landed on that rule and the rule read as a strike
       through a mark that had not been charted, which is the one thing
       this card must never say. The list starts below the rule. */
    punch.style.cssText = 'display:none;grid-column:1;margin-top:calc(19px * var(--dck));';
    /* CW1: THE TOP LINE, WHERE THE DAY'S LINES WERE. Under the head
       rule, in the card's own reading type, because that is the space
       the date and the day's three lines had and this is what stands in
       their place. It is the one thing on this card that changes with
       the state: the way in, she is out there, or the arrival. */
    punchTop = document.createElement('p');
    punchTop.className = 'dc-punch-top';
    punchTop.style.cssText = 'font-size:calc(10.2px * var(--dck));' +
      'line-height:calc(11.8px * var(--dck));color:#4a4034;' +
      'margin-bottom:calc(3px * var(--dck));display:none;';
    punch.appendChild(punchTop);
    /* PC1: THE NAMES IN TWO COLUMNS, down the first and then the second.
       BL1 made the berth an eighth line, and eight names under a two-line
       arrival line, with the away line and London's, ran past the foot
       of the card (Tommy, 2026-09-28: "why is the voyage text longer than
       the card"): ~155 units of type on 143 of stock. Four rows hold it
       with room to spare. */
    var punchCols = document.createElement('div');
    punchCols.className = 'dc-punch-cols';
    punchCols.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;' +
      'grid-template-rows:repeat(4,auto);grid-auto-flow:column;column-gap:calc(8px * var(--dck));';
    punch.appendChild(punchCols);
    for (var i = 0; i < 24; i++) {
      var p = document.createElement('p');
      p.className = 'dc-punch-l';
      p.setAttribute('data-struck', '0');
      p.style.cssText = 'font-size:calc(9.6px * var(--dck));' +
        'line-height:calc(10.5px * var(--dck));color:#4a4034;' +
        'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
        'text-decoration-thickness:1px;display:none;';
      punchCols.appendChild(p);
      punchLines.push(p);
    }
    punchAway = document.createElement('p');
    punchAway.className = 'dc-punch-away';
    punchAway.style.cssText = 'font-size:calc(9px * var(--dck));' +
      'line-height:calc(10.6px * var(--dck));color:#96322a;letter-spacing:.05em;' +
      'margin-top:calc(2px * var(--dck));white-space:nowrap;overflow:hidden;' +
      'text-overflow:ellipsis;display:none;';
    punch.appendChild(punchAway);
    /* ==== CW1: LONDON, AT THE FOOT. Tommy, 2026-09-19: *"I don't mind
       putting something about the wind in there. It's quite a nice nod
       to be able to say the wind and something about London and the
       current time."* The sky over this room is the real sky over London
       at the hour of viewing (sky.js) and the wind the boat sails in is
       that same wind, so the card can simply say so — in plain words, no
       barbs and no points of sail.

       It is the LAST thing in the block and in normal flow under it, and
       not a grid row of the card's own pinned to the foot: pinned, it
       stood under the list in two states out of three and OVERLAPPED the
       away line in the third (measured, card 269 x 143: the list, a said
       line and the away line run to 131 and the pinned foot row sat at
       127). In the flow it is always the last line and never collides.
       It never wraps either — a foot line that took a second line would
       push the seventh mark off the stock. ==== */
    punchLondon = document.createElement('p');
    punchLondon.className = 'dc-punch-london';
    punchLondon.style.cssText = 'font-size:calc(9px * var(--dck));' +
      'line-height:calc(10.6px * var(--dck));color:#96322a;letter-spacing:.05em;' +
      'margin-top:calc(2px * var(--dck));white-space:nowrap;overflow:hidden;' +
      'text-overflow:ellipsis;display:none;';
    punch.appendChild(punchLondon);
    dom.el.insertBefore(punch, dom.btn || null);
    /* THE CARD TAKES A POINTER only while it is saying where she is —
       otherwise it is `pointer-events: none` like the rest of the desk's
       typing, and the essay tab keeps its own. */
    if (!dom.el.__v1click) {
      dom.el.__v1click = 1;
      dom.el.addEventListener('click', function (e) {
        if (!awayNow) return;
        var V = voy(); if (!V) return;
        var s = V.state();
        if (!root.Room || !root.Room.centreOn || s.x === undefined) return;
        e.preventDefault(); e.stopPropagation();
        root.Room.centreOn(s.x, s.y);   /* the engine's own pan, not a new one */
      });
    }
  }
  /* is she drawn anywhere on the window? the same projection the marks
     are picked with, against the map window's own rectangle */
  function boatOffScreen(vs) {
    if (!root.Room || !root.Room.toScreen) return false;
    var p = root.Room.toScreen(vs.x, vs.y);
    if (!p) return true;
    var P = S.portalScr || S.portal;            /* MP2: on the glass */
    return p[0] < P.l || p[0] > P.l + P.w || p[1] < P.t || p[1] > P.t + P.h;
  }
  function bearingTo(vs) {
    var cx = S.view.x + S.view.w / 2, cy = S.view.y + S.view.h / 2;
    var a = Math.atan2(vs.x - cx, -(vs.y - cy)) * 180 / Math.PI;
    return PTS[Math.round(((a % 360) + 360) % 360 / 22.5) % 16];
  }
  /* west of the record there is no day to name: `dayAtMap` answers null
     out there, which is exactly the state this line is for */
  /* PC1: she is at rest — at her moorings, or gone out at the berth */
  function voyRest(vs) { return vs.state === 'moored' || vs.state === 'arrived'; }
  function sheetFolded() { var O = root.RoomObjects; return !!(O && O.foldT > 0.5); }
  function westOfRecord() {
    var f = S.fld && S.fld.front;
    if (!f) return false;
    if (lastPtrX !== null && lastPtrX < f.west) return true;
    return (S.view.x + S.view.w / 2) < f.west;
  }
  /* ==== CW1: THE LONDON LINE. The clock is `Sky.at()` — the instant the
     world is at, so `?sky=` moves it and the harness can prove it — read
     on a London wall clock, and the wind is `Sky.wind()`: km/h and the
     bearing it blows FROM, to the same sixteen points the rest of this
     file names. A calm under 3 km/h is a calm and says so.

     Under way, one plain word more about how it bears on her, off the
     angle between her head and the bearing it blows from: within four
     points of dead ahead it is ahead, within four points of dead astern
     it is behind her, and anything else is on her beam. The pronoun rule
     holds — she is a boat at sea; nothing here is about the berth.

     Every word of it is a PLACEHOLDER in voyage.json (`hint.london*`),
     like every other word out here. ==== */
  var LONDON_CALM = 3;
  function londonLine(vs) {
    var Sk = root.Sky;
    if (!Sk || !Sk.wind || !Sk.at) return '';
    var w = Sk.wind() || { speed: 0, dir: 270 };
    var calm = !(w.speed >= LONDON_CALM);
    var t = hintOf(calm ? 'london_calm' : 'london');
    if (!t) return '';
    var line = t.replace('{clock}', clockText(Sk.at()))
                .replace('{point}', PTS[Math.round(((w.dir % 360) + 360) % 360 / 22.5) % 16])
                /* VW1 (Tommy, 2026-09-27: "should it be in miles ph?"): told in
                   mph; Sky.wind() and the calm test stay km/h */
                .replace('{speed}', Math.round(w.speed / 1.609344));
    if (!calm && vs && vs.state === 'sailing') {
      var off = Math.abs(((vs.hdg - w.dir) % 360 + 540) % 360 - 180);
      line += hintOf(off <= 45 ? 'london_ahead'
                   : off >= 135 ? 'london_astern' : 'london_beam');
    }
    return line;
  }
  function clockText(ms) {
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London',
        hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(ms));
    } catch (e) { return new Date(ms).toISOString().slice(11, 16); }
  }
  /* ==== V6 (4): UNDER A STRANGER'S HANDLE THE CARD CARRIES ONE LINE.
     The voyage is charted on one record and no other — the harbour is on
     this record's own first landfall and 12 April 1986 is a birthday that
     is not the stranger's — so voyage.js turns the whole egg off while a
     foreign subject is loaded, and the card west of the record turns with
     it: no marks, no away line, no London, just `hint.foreign`, which
     says where the voyage is and that `handle reset` brings it back. It
     is a PLACEHOLDER in voyage.json like every other word out here. ==== */
  function voyOff() {
    var V = root.Room && root.Room.voyage;
    try { return !!(V && V.off && V.off()); } catch (e) { return false; }
  }
  function hintOf(k) {
    var V = root.Room && root.Room.voyage;
    var h = (V && V.hint) ? V.hint() : null;
    return (h && h[k]) || '';
  }
  /* ================================================================
     CW1 — THE TOP LINE, AND THE PASSAGE THAT IS REMEMBERED

     Three placeholders in voyage.json, one per state, and nothing else:
     `hint.moored` while she lies at the harbour, `hint.underway` while
     she is out, `hint.arrived` once she is fast — the last with the
     passage time in it.

     AND THE PASSAGE OUTLIVES THE PAGE. Tommy: the arrival line *"becomes
     the permanent top line west of the record on every later visit (the
     passage is remembered as the marks are)"*. voyage.js remembers what
     has been charted in `tl-survey-charted` and what has been seen in
     `tl-survey-unveiled`; this is the third of that family and it is
     kept the same way, guarded the same way (localStorage throws outright
     in a third-party frame and in some private windows), and holds the
     one thing the arrival line needs that `V.state()` cannot answer after
     a reload: how long the passage took. While she is UNDER WAY the
     under-way line wins even with a passage in the store — a card that
     said she was made fast while she was sailing would be lying.
     ================================================================ */
  var PKEY = 'tl-survey-passage';
  /* RS1 (2026-09-28, Tommy: "shouldn't we extract the room state to a
     single blob to make it easy to persist, restore, etc.?"): it is
     `voyage.passage` in the room's blob (state.js) now, beside its
     siblings; an old reader's `tl-survey-passage` is taken in once and
     removed (RoomState.adopt). */
  var RSTATE = root.RoomState || null;
  var passage = (function () {
    /* and it answers the harness's one lever the way its two siblings do:
       `?charted=n` SETS the survey, so a run that walks 0 -> 7 does not
       carry the last picture's passage into the next one (voyage.js does
       exactly this to `charted` and `unveiled`; state.js has cleared the
       blob on it) */
    if (/^\d+$/.test(Q.get('charted') || '')) {
      try { localStorage.removeItem(PKEY); } catch (e) { } return '';
    }
    if (!RSTATE) { try { return localStorage.getItem(PKEY) || ''; } catch (e) { return ''; } }
    var v = RSTATE.adopt('voyage.passage', PKEY);
    return typeof v === 'string' ? v : '';
  })();
  function passageOf(vs) {
    if (vs && vs.state === 'arrived') {
      var sec = vs.elapsed || 0;
      /* voyage.js's own `clock()`, to the second, so the card and the log
         read the same passage */
      var t = Math.floor(sec / 60) + 'm ' + ('0' + Math.floor(sec % 60)).slice(-2) + 's';
      if (t !== passage) {
        passage = t;
        if (RSTATE) RSTATE.set('voyage.passage', t);   /* RS1: this session only if storage throws */
      }
    }
    return passage;
  }
  /* ==== D1 (5): AND ONE LINE TOWARD THE CONJECTURE. The voyage's card
     carries a hint west (`hint.moored`); the front's card carries the
     same shape of hint EAST, because the pit out in Terra Incognita is
     two hundred weeks past the neat line and nothing on the sheet says
     which way to look for it. It is on the card of the day the survey
     was carried to — the eastmost day there IS a card for, the record
     ending at the front — and it is the surveyor's log and not a
     caption: lower case, terse, about the GROUND. ==== */
  var EAST_LINE = RoomCopy.t('card.east_line');
  /* ==== D2 (2): AND IT IS TERRA'S OWN CARD THAT SAYS IT, not the day's.
     Tommy, 2026-09-19: "a different card once you're in Terra Incognita,
     rather than the current day, which hints towards where the
     singularity is." So the front's card goes back to being the front's
     card — the day the survey was carried to and nothing else — and the
     line east belongs to the ground the hand is actually over. ==== */
  var TERRA_HEAD = RoomCopy.t('card.terra_head');
  /* ==== TF1 (Tommy, 2026-09-23): "I can't see someone else's sheet hint
     on someone else's GitHub profile when we look into the Terra
     incognito on the file card?" The east line is the pit's, and the pit
     is his (PF1 put the pacing down on a stranger's sheet); the card
     west already turns to `hint.foreign` while a stranger's record is
     up, so the card east turns the same way. The head stays terra's — it
     is about the ground, and the ground is still unsurveyed. ==== */
  var TERRA_FOREIGN = RoomCopy.t('card.terra_foreign');
  /* ==== D2 (5): AND THE PIT HAS A CARD OF ITS OWN. Tommy, 2026-09-19:
     "we should have a special file card text value when you hover the
     singularity, one for the default unhappy path and one for the happy
     path." Read the way terra's is — the hand, else the view's centre —
     and only while that point is on the funnel's own DISC (`S.doom.cx`,
     `.cy`, `.r`, engine.js), which is a place and not a day, so the head
     carries no date and the key does not move with the hand across it.
     Two texts. UNHAPPY: nothing has been walked to it and no bearing
     taken (acts one and two). HAPPY: the party has aligned on it (act
     three), or did on an earlier visit, which `Room.progress` remembers
     across reloads and the pit's own state does not. */
  var DOOM_HEAD = RoomCopy.t('pit.head');
  /* Tommy (2026-09-20): "The cards 'a hollow in the conjecture' — can we
     put a date that says mid-2030 on it?" The hollow is not a day, so it
     takes no day line; it takes the season the conjecture puts it in. */
  var DOOM_WHEN = RoomCopy.t('pit.when');
  var DOOM_LINE = RoomCopy.t('pit.line');
  var DOOM_LINE_2 = RoomCopy.t('pit.line_2');
  var DOOM_HEAD_DONE = RoomCopy.t('pit.head_done');
  var DOOM_LINE_DONE = RoomCopy.t('pit.line_done');
  var DOOM_LINE_DONE_2 = RoomCopy.t('pit.line_done_2');
  /* '' off the disc, 'doom0' on it before the bearing, 'doom1' after */
  function doomKey() {
    var D = S.doom;
    if (!D || !D.on || !D.r) return '';
    var mx = lastPtrX === null ? S.view.x + S.view.w / 2 : lastPtrX;
    var my = lastPtrY === null ? S.view.y + S.view.h / 2 : lastPtrY;
    var dx = mx - D.cx, dy = my - D.cy;
    if (dx * dx + dy * dy > D.r * D.r) return '';
    var done = D.act === 3;
    if (!done) {
      try { done = !!(root.Room && Room.progress && Room.progress.has && Room.progress.has('doom')); }
      catch (e) { /* no progress: the pit's own state is all there is */ }
    }
    return done ? 'doom1' : 'doom0';
  }
  /* ==== FROM TERRA INCOGNITA ONWARDS. Tommy, 2026-09-19: "give a file
     card change from terra incognita onwards that gives a hint". The
     threshold is the NEAT LINE and nothing later: east of it the card is
     terra's, and it stays terra's all the way out to the pit and past
     it, because there is no second threshold to cross. It is read the
     way westOfRecord reads its own side — the HAND when there is one on
     the sheet, and the view's own centre when there is not, so a pan out
     east with the hand off the glass turns the card over too. ==== */
  /* THE HINT BEGINS WHERE THE SURVEY ENDS. Tommy (2026-09-20): "I don't
     think the note to the east about the singularity is appearing when it
     should… as soon as the left of your screen contains stuff that isn't
     terra incognita, it then changes to 18th of September 2026, which is
     the date at the frontier… it only gives you the hint when you fall to
     incognita." Measured: `front.east` stands 620 map units (fifteen
     weeks) east of the last surveyed column, so a hand anywhere in that
     band got the frontier's own day, and only past it the hint. The
     reader's line is the record's: the column after the last surveyed
     day is unsurveyed ground, and the hand there gets terra's card. */
  function handEast() {
    var f = S.fld, dys = f && f.days;
    if (!dys || !dys.epochMs || !dys.surveyedTo) return false;
    /* EG1: a phone has no hand hovering, so only a finger that has touched
       the glass is read: terra's card and the pit's come on a tap there,
       never from the view alone (nothing covers the map at rest) */
    if (PHONE && lastPtrX === null) return false;
    var mx = lastPtrX === null ? S.view.x + S.view.w / 2 : lastPtrX;
    var w = Math.floor((mx - C.px) / C.cw);
    var wTo = Math.floor((Date.parse(dys.surveyedTo + 'T00:00:00Z') - dys.epochMs) / DAY_MS / 7);
    /* TF1 (2026-10-07): AND NOT ON THE FIRST SCREEN. Tommy: "You should have to do at least a little bit of exploring
       beyond the stuff that's already on the initial screen. Otherwise, you'd find it almost immediately." The sheet
       opens on the front's own year, so the future's card starts past the end of that year, and never nearer the
       front than four weeks (a front in late December) */
    var yEnd = Date.parse(dys.surveyedTo.slice(0, 4) + '-12-31T00:00:00Z');
    wTo = Math.max(wTo + 4, Math.floor((yEnd - dys.epochMs) / DAY_MS / 7));
    /* EG1: and on the phone the tap must be ON the land, in the record's
       seven rows: a tap on the desk above the plate is the tap that puts
       the card away, not terra's (phone suite C4) */
    if (PHONE && w > wTo && !terraDate()) return false;
    return w > wTo;
  }
  /* the day that WOULD be under the hand if the survey had got there:
     the record's own lattice, carried on east — one week a column, one
     day a row, which is the only reading of a point out there that the
     sheet's own ruling supports. */
  function terraDate() {
    var f = S.fld;
    if (!f || !f.days || !f.days.epochMs) return null;
    /* the point being read: the hand, or the middle of the view when the
       hand is off the glass */
    var mx = lastPtrX === null ? S.view.x + S.view.w / 2 : lastPtrX;
    var my = lastPtrY === null ? S.view.y + S.view.h / 2 : lastPtrY;
    var w = Math.floor((mx - C.px) / C.cw);
    var r = Math.floor((my - C.py) / C.ch) - C.EDGE_ROWS;
    if (r < 0 || r > 6) return null;
    var d = (w & 1) ? 6 - r : r;       /* dayAtMap's own reading, carried on */
    var t = f.days.epochMs + (w * 7 + d) * DAY_MS;
    if (!isFinite(t)) return null;
    return isoOf(t);
  }
  function topOf(vs) {
    if (!vs) return '';
    if (voyOff()) return hintOf('foreign');   /* V6 (4): the way back */
    var p = passageOf(vs);
    if (vs.state === 'arrived' || (vs.state === 'moored' && p))
      return (hintOf('arrived') || '').replace('{passage}', p);
    return hintOf(vs.state === 'moored' ? 'moored' : 'underway');
  }
  /* what the card would say about the voyage this instant, as one string,
     so the DOM is touched only when it changes */
  function punchKeyOf(vs) {
    if (!vs) return '';
    var m = voy().charted().marks, k = '', i;
    for (i = 0; i < m.length; i++) k += m[i].charted ? '1' : '0';
    var bth = voy().charted().berth;
    if (bth) k += bth.made ? 'B' : 'b';
    return (voyOff() ? 'F|' : '') + vs.state + '|' + k + '|' + awayOf(vs) +
           '|' + topOf(vs) + '|' + londonLine(vs);
  }
  function awayOf(vs) {
    /* CW1: a boat at her moorings is where the reader was told she is —
       the way-in line says so — and the away line is about LOSING her */
    /* PC1 (2026-09-28): nor once she has ARRIVED — she has gone out at the
       berth as a dot and there is nothing out there to find. Tommy, from
       production after the voyage: "i don't even know why this card is
       showing ... for some reason it won't flip" — the away line made the
       whole card a button (pointer-events on, the click taken to pan to
       her), so the press never reached the canvas to turn it over. */
    if (vs.state === 'moored' || vs.state === 'arrived' || !boatOffScreen(vs)) return '';
    var t = hintOf('away');
    return t ? t.replace('{bearing}', bearingTo(vs)) : '';
  }
  function drawPunch(vs) {
    buildPunch();
    if (!punch) return;
    var ch = voy().charted(), m = ch.marks.slice(), i;
    /* BL1 (Tommy, 2026-09-27): "the berth should also be an item on the
       file card you cross off, along with the existing 7" — the last line,
       under the seven, struck when she has gone out past the legs */
    if (ch.berth) m.push({ name: ch.berth.name, charted: ch.berth.made });
    /* V6 (4): a stranger's record has no survey to strike off */
    var off = voyOff();
    for (i = 0; i < punchLines.length; i++) {
      var p = punchLines[i], f = off ? null : m[i];
      if (!f) { if (p.style.display !== 'none') p.style.display = 'none'; continue; }
      /* THE NAMES ONLY, and an em dash where a name was dropped */
      var txt = f.name || '—';
      if (p.__t !== txt) { p.__t = txt; p.textContent = txt; }
      p.style.display = '';
      var struck = f.charted ? '1' : '0';
      if (p.getAttribute('data-struck') !== struck) {
        p.setAttribute('data-struck', struck);
        /* a rule through the line, as a pencil strikes a list */
        p.style.textDecoration = f.charted ? 'line-through' : '';
        p.style.color = f.charted ? '#6b6052' : '#4a4034';
      }
    }
    /* CW1: the one line that changes with the state, above the list */
    if (punchTop.__t !== topNow) { punchTop.__t = topNow; punchTop.textContent = topNow; }
    punchTop.style.display = topNow ? '' : 'none';
    if (punchLondon.__t !== londonNow) { punchLondon.__t = londonNow; punchLondon.textContent = londonNow; }
    /* VL1: on the phone under way, one foot line: where she is while she is off the screen, else London */
    var oneFoot = PHONE && dom.el.getAttribute('data-two') === 'voy' && !!awayNow;
    punchLondon.style.display = (londonNow && !off && !oneFoot) ? '' : 'none';
    punchAway.textContent = off ? '' : awayNow;
    punchAway.style.display = (awayNow && !off) ? '' : 'none';
    dom.el.style.pointerEvents = (awayNow && !off) ? 'auto' : '';
    dom.el.style.cursor = (awayNow && !off) ? 'pointer' : '';
  }
  function showPunch(on) {
    buildPunch();
    if (!punch) return;
    if (punch.__on === on) return;
    punch.__on = on;
    punch.style.display = on ? '' : 'none';
    [dom.date, dom.fig, dom.level, dom.settle].forEach(function (n) {
      if (n) n.style.display = on ? 'none' : '';
    });
    if (on && dom.btn) dom.btn.hidden = true;
    if (!on) {
      dom.el.style.pointerEvents = '';
      dom.el.style.cursor = '';
    }
    drawn = null;                       /* the day's own lines are stale */
  }
  function set(node, txt) {
    if (!node || node.__t === txt) return;
    node.__t = txt; node.textContent = txt;
  }
  /* V1 (5): THE CARD FOLLOWS THE BOAT. The voyage asks for a frame every
     time she moves and the engine draws, so this runs with `drawCard`:
     it works out what the card WOULD say and re-types it only when that
     has changed, so nothing touches the DOM on a frame where nothing
     has. `paint()` asks for one more frame and the next tick agrees with
     itself, so it settles in one. */
  /* the terra card's key: whether the hand is east, whether it is on the
     pit's disc, and WHICH DAY the lattice gives it there. Measured before
     this (2026-09-20): a hand walked from 2600 to 3300 map units east and
     the card stood on the same day, because only the first two were
     compared. Asked by every frame that animates and by every pointer move
     that finds no surveyed day under the hand (`show(null)`). */
  function terraSync() {
    if (presented || held) return false;
    var te = handEast();
    /* D2 (5): and the pit's disc is a threshold inside terra, crossed by
       the hand or by the bearing being taken. `paint` only re-types the
       DOM when its key moves. */
    var dk = te ? doomKey() : '';
    var tk = te ? (dk || terraDate() || '') : '';
    if (te !== terraOn || dk !== doomOn || tk !== terraKey) {
      terraOn = te; doomOn = dk; terraKey = tk; paint(); return true;
    }
    return false;
  }
  /* ==== NL1 (2026-09-21) — AND THE FOLD IS NOT A THRESHOLD THIS CARD
     READS ANY MORE. CR1 made it one: the memorandum was shown only while
     the sheet was folded (Tommy, 2026-09-20: "it should only show when
     the map is folded. when unfolded it should show the current date's
     details"), so this card had to be told the corner had been turned —
     `foldedNow` off RoomObjects' own `foldT`, and `foldSync` as the
     knock on every voyage tick.

     The memorandum is gone (see THE MEMORANDUM LEAVES THIS CARD,
     below): the hints are
     the note's list now, one at a time, and the day card is the day's
     record again folded or not. So the threshold, the remembered answer
     and the knock are all deleted — nothing on this card depends on the
     state of the paper, and voyTick has one fewer question to ask sixty
     times a second. The pit's card, the conjecture's and the voyage's
     take their own areas exactly as before.
     NC1 (2026-09-22): READ AS HISTORY — the threshold is back, with
     the list (THE MEMORANDUM, below). ==== */
  /* ==== NC1 (2026-09-22) — THE MEMORANDUM: THE RULED LIST, ON THIS CARD
     Tommy, 2026-09-22: "If I had to guess what had happened, it would be
     that we had two notes: one was full of glyphs, and one just had the
     next hint. I ask you to change the next hint card to actually be a
     list of all the hints that get struck through as they're done. They
     appear in the right order, but they only appear as you've done them,
     or the next one appears, which is what we've got now. But that was
     meant to be on the file cards, not the hidden cards. We're meant to
     keep our map of glyphs on the other cards, the hidden one."

     So NL1's list (2026-09-21, which put it on the note under the map)
     comes HERE, and N2's memorandum comes back in its shape. On a FOLDED
     sheet, at rest — CR1's gate: nothing under the hand, nothing
     presented, not out on the conjecture or the pit, not the voyage's
     card — this card is the surveyor's ruled list of his six errands, in
     `Room.progress.list()`'s own order (marks.json `names`, the order of
     finding):

        a FOUND errand   its line written and struck through in the
                         revision red (X2b's pen rule)
        the NEXT errand  its line written out in full
        every one after  a blank rule. No words, nothing.

     and the closing line (`card.hint_done`) only once all six are struck.
     FU1 (2026-09-27): READ THE THREE ROWS ABOVE AS HISTORY. Tommy: "the
     hint card actually showing all hints at once and them crossing
     through as you find them". Every line is written from the start and
     struck when found; there is no NEXT and no blank rule. Each line has
     its mark's drawing in the margin now (styles.css `.dc-g`).
     The small grey row over the head rule says what the card is
     (`card.hint_head`), as N2's did. THE LIST CARRIES NO DRAWINGS: the
     drawings are the note's plan (D.note), and one card carrying both a
     list and the glyphs, both marked off, is what Tommy called weird.
     Unfolded, or with a day under the hand, the card is the day's record
     exactly as it was, and the list is not displayed at all.

     THE CARD IS NOT RE-CUT FOR IT. The list is ruled to the stock this
     card already is: the day card's feint ruling runs from the red head
     rule at 0.205 of its height to 0.945 in five spaces, and while it
     carries the list the same span is ruled in N — one space to each ROW
     of type the list needs (NC1 (2), memoFit below) — through NL1 (3)'s
     `card.rule` uniform. The DOM list is laid over exactly that span
     (styles.css `.dc-list`: top 20.5%, height 74%) at a line-height of
     one pitch, so every row stands on the stock's own rule, the card is
     the same piece of index stock folded or not, and nothing about how it
     reads unfolded changes.

     IT RE-READS THE MARKS WHEN THEY CHANGE, on `Room.progress.onChange`,
     so an egg found with the card in view strikes its line and writes
     the next one out there and then. progress.js loads after this file,
     so the subscription is taken the first time the list is shown.

     THE FOLD IS A THRESHOLD THE CARD READS again (CR1's `foldedNow` /
     `foldSync`, which NL1 (2) deleted): `foldT` off RoomObjects past the
     same 0.0005 crumb of a turn desk-books.js takes its crease on, and
     `foldSync` at the top of voyTick because nothing else on the desk
     tells this card the corner has been turned. ==== */
  var DAY_RULE = card.rule.slice();
  /* NC1 (2): the list's ruling is N spaces over the same span, one ROW of
     type a space — N is measured (memoFit, below); 9 until it can be */
  var SPAN0 = 0.205, SPAN = 0.74, ROWS_FALLBACK = 9;
  var LIST_RULE = [SPAN0, SPAN / ROWS_FALLBACK, ROWS_FALLBACK + 1, SPAN0];
  /* ==== CF3 (2026-09-23): THE MEMORANDUM IS THE CARD'S BACK ============
     Tommy: "Yes, have the memorandum on the back." So the fold is not a
     threshold this card reads again, and this time for good: `foldedNow`,
     `foldedOn` and `foldSync` (and voyTick's knock) are deleted. The list
     and its head row are a second DOM face, #day-card-back, laid by
     D.card's layoutDom on the quad's mirror and shown only when the back
     is the face toward the eye; the card turns over when the fold flops
     it off the leaf, and comes back face up when it is dealt home. The
     front is always the day's five lines on the day's ruling (DAY_RULE,
     `card.rule`, which no longer switches); the back is always the list
     on LIST_RULE (`card.ruleB`, which the stock's GL pass rules the back
     face with). Everything below — the strike, the rows, the knock on
     `Room.progress.onChange` — is NC1's, on the back's list. ==== */
  var memoShown = false;
  card.ruleB = LIST_RULE;
  /* mulberry32, the same tiny generator the rings are drawn with. A
     strike is a different stroke from a ring, so the shape is its own;
     the arithmetic is four lines and not worth a module. */
  function seeded(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  /* THE VIEWBOX IS A FULL ROW'S OWN SHAPE (100 x 5.4 against ~266 x 13.4
     css px at --dck 1), so on a row of type that runs the width of the
     card the pen is square: the wobble is in the same units either way
     and a 0.5-unit nib comes out a shade over a css pixel. A SHORT row —
     the tail of a wrapped line — squeezes the same stroke into less
     width, which shortens the wander and leaves the weight alone, because
     the weight of a near-horizontal rule is the VERTICAL scale and that
     does not change. A quicker stroke on a shorter word is what a hand
     does anyway. */
  function strikeD(i) {
    var q = seeded(0x5A7 + i * 613);
    var x0 = 0.4 + q() * 1.2, x1 = 99.6 - q() * 1.2;
    var y0 = 2.62 + (q() - 0.5) * 0.5, y1 = 2.62 + (q() - 0.5) * 0.5;
    var a1 = 0.14 + q() * 0.20, p1 = q() * 6.2832;
    var a2 = 0.05 + q() * 0.09, p2 = q() * 6.2832;
    var N = 20, d = '', k, t, x, y;
    for (k = 0; k <= N; k++) {
      t = k / N;
      x = x0 + (x1 - x0) * t;
      y = y0 + (y1 - y0) * t + a1 * Math.sin(t * 5.3 + p1) + a2 * Math.sin(t * 12.7 + p2);
      d += (k ? 'L' : 'M') + (Math.round(x * 100) / 100) + ' ' + (Math.round(y * 100) / 100);
    }
    return d;
  }
  /* the revision red is read off the stylesheet, once, so there is not a
     second copy of it in here (see progress.js: every correction on this
     sheet is the same ink) */
  function redInk() {
    var c = '';
    try {
      c = getComputedStyle(document.documentElement).getPropertyValue('--accent');
    } catch (e) { /* no view */ }
    c = (c || '').trim();
    return /^#[0-9a-fA-F]{3,8}$/.test(c) ? c : '#96322a';
  }
  function strikeUrl(i, ink) {
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 5.4"' +
              ' preserveAspectRatio="none"><path d="' + strikeD(i) + '" fill="none"' +
              ' stroke="' + ink + '" stroke-width=".5" stroke-linecap="round"' +
              ' stroke-linejoin="round" opacity=".92"/></svg>';
    return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")';
  }
  var memoEl = null, memoLines = [], memoDressed = 0, memoSub = 0;
  function memoDress() {
    var i, span;
    if (memoDressed) return;
    memoDressed = 1;
    memoEl = document.getElementById('dc-list');
    if (!memoEl) return;
    /* the six lines in marks.json's own order — which is the order
       base.njk printed them in and the order `Room.progress.list()`
       answers in, so the i-th line wears the i-th hand's strike and
       always will */
    var all = memoEl.querySelectorAll('.dc-line[data-mark]');
    for (i = 0; i < all.length; i++) memoLines.push(all[i]);
    var ink = redInk();
    for (i = 0; i < memoLines.length; i++) {
      span = memoLines[i].querySelector('.dc-t');
      if (span) span.style.backgroundImage = strikeUrl(i, ink);
    }
    memoSync();
    /* nothing draws itself on the way in: a strike already earned is ink
       that was already on the card. Only one landing while the reader is
       looking travels. */
    var el = memoEl;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-settled'); });
    });
  }
  function memoSync() {
    var P = window.Room && Room.progress, i, m, has, next = -1;
    if (!memoEl) return;
    /* FU1: every line is written; a found one is struck. NC1's `is-next`
       (the one line written after the struck ones) went with the blanks. */
    for (i = 0; i < memoLines.length; i++) {
      m = memoLines[i].getAttribute('data-mark');
      has = !!(m && P && P.has(m));
      memoLines[i].classList.toggle('is-struck', has);
      if (!has && next < 0) next = i;
    }
    memoEl.classList.toggle('is-done', next < 0 && memoLines.length > 0);
    if (!memoSub && P && P.onChange) {
      memoSub = 1;
      P.onChange(function () { memoSync(); if (memoShown) H.invalidate(); });
    }
  }
  /* ==== NC1 (2) — ONE ROW OF TYPE TO ONE RULED SPACE, ALWAYS ==========
     The first cut ruled the span in seven equal spaces, one a LINE, and
     squeezed a wrapped hint's two rows into one space at a leading under
     the type's own size: the pit's hint rode up under the line above it
     and neither of its rows stood on a rule (nc1-c-folded-all.png). So
     the leading IS the pitch. Each line is measured for how many rows it
     wraps to (its box over its own line-height — the wrap is the width's
     and the face's, and both are in `--dck`, so the count is the same at
     every window), N is their sum including the closing line's, and the
     span from the red head rule (0.205) to the stock's last rule (0.945)
     is ruled in N spaces: the shader through `card.rule`, the DOM by
     `--dcp`, the pitch in px, which is the list's line-height. A hint
     that wraps takes as many spaces as it has rows, and every row, the
     blank ones included, stands on its own printed rule. Measured again
     when the web font settles and whenever the card is re-placed; N is
     ROWS_FALLBACK until the list has been laid out at all. At 9.2 px the
     nine rows the six hints and the closing line need today come out at
     a pitch of ~13.9 --dck units, so the type keeps its size. ==== */
  var memoRows = 0, memoW = 0;
  function memoFit() {
    if (!memoEl || memoEl.hidden || !card.w || !card.h) return;
    if (!memoRows || memoW !== (card.dw || card.w)) {
      var all = memoEl.querySelectorAll('.dc-line'), i, lh, n = 0, r;
      for (i = 0; i < all.length; i++) {
        lh = parseFloat(getComputedStyle(all[i]).lineHeight);
        if (!(lh > 0) || !all[i].offsetHeight) { n = 0; break; }
        r = Math.max(1, Math.round(all[i].offsetHeight / lh));
        all[i].setAttribute('data-rows', r);
        n += r;
      }
      memoRows = n; memoW = card.dw || card.w;
    }
    var N = memoRows || ROWS_FALLBACK;
    memoEl.style.setProperty('--dcp', ((card.dh || card.h) * SPAN / N).toFixed(3) + 'px');
    LIST_RULE = [SPAN0, SPAN / N, N + 1, SPAN0];
    card.ruleB = LIST_RULE;              /* CF3: the back's ruling; the front keeps the day's */
  }
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(function () {
      memoRows = 0;
      if (memoShown) { memoFit(); H.invalidate(); }
    });
  }
  /* the list shown or not, and the stock ruled to match: N spaces while
     it carries the list, its own five otherwise */
  function memoOn(on) {
    memoShown = !!on;
    card.rule = DAY_RULE;                /* CF3: the front is always the day's */
    card.ruleB = LIST_RULE;
    if (on) memoDress();
    if (memoEl) {
      if (memoEl.hidden === on) memoEl.hidden = !on;
      if (on) { memoSync(); memoFit(); }
    }
  }
  /* CF3: the card calls this as its back comes up (D.card backShow) */
  dom.onBack = function () { memoOn(true); };
  function voyTick() {
    /* EG3: on the phone whether the card is on the desk at all can change
       without a paint (the sheet folding under it); asked every frame,
       and it touches nothing unless its key moved */
    if (PHONE && !NO_CARD) phoneCard();
    if (NO_CARD || presented || held) return;
    /* CF3: `foldSync` stood here. The card's own face says what is read
       now (D.card's pose), so there is nothing about the paper to ask. */
    /* EG1: the phone has no voyage (yet), and terra and the pit are not
       the voyage's: they are read before it is asked for */
    if (PHONE && terraSync()) return;
    var V = voy(); if (!V) return;
    var vs = V.state();
    /* CW1: the day's card belongs to the record. She is moored and the
       view is on the record -> the day, as it always was. Anything else
       -- under way anywhere, or moored with the unsurveyed sea under the
       view or the hand -- is the voyage's card. */
    /* D2 (2): the front is a threshold the VIEW can cross on its own */
    if (terraSync()) return;
    /* PC1: ARRIVED is at rest too, like moored — the voyage is over, and
       the card on the record (or over Terra Incognita) is the day's again,
       west of it the voyage's with the arrival line. And the FOLDED sheet
       is not the voyage's at all: the card under it lands face up with
       the day's own text (FU1). */
    if (sheetFolded() || (voyRest(vs) && !westOfRecord())) {
      if (punch && punch.__on) { paint(); return; }
      /* NC1: the row reads the memorandum's head while the list is up —
         compared against the RIGHT line, or a folded sheet would
         repaint (and redraw) this card on every tick */
      if (dom.state && dom.state.__t !== RoomCopy.t('card.state_front')) paint();
      return;
    }
    awayNow = awayOf(vs);
    topNow = topOf(vs);
    londonNow = londonLine(vs);
    if (punchKeyOf(vs) !== punchKey) paint();
  }
  /* ==== NL1 (2026-09-21) — THE MEMORANDUM LEAVES THIS CARD ==========
     N2 gave the resting card the next unfound egg's line, and MG1 gave
     it that mark's drawing in the margin; CR1 then held both back until
     the sheet was folded. All of it goes. Tommy, 2026-09-21: the hints
     are "a list of all the hints in order but it only shows the next
     hint + any that have already been completed (struck through)" — and
     that list is the NOTE under the folded map (D.note, above), which is
     the one card on this desk anything is marked off on. Tommy,
     2026-09-20: "it's weird to have a list + the glyphs with both
     getting marked off."

     So `nextHint`, the `memo` branch of `paint`, the `data-memo`
     attribute and the mark's drawing (#dc-memo) are deleted outright,
     and this card is what it was before N2: the day's own record at
     rest, folded or not. The card's own subscription to progress went
     with them — nothing printed here depends on what has been found —
     and the pit's card, the conjecture's and the voyage's are untouched.
     Gone from copy.json with the branch: `card.hint_head`,
     `card.hint.<name>` and `card.hint_done`, whose words are the note's
     now (`note.head`, `note.hint.*`, `note.done`).
     NC1 (2026-09-22): READ AS HISTORY. NL1 misread Tommy; the list is
     back on this card on the folded sheet (THE MEMORANDUM, above) and
     the keys are `card.hint_head`, `card.hint.*`, `card.hint_done`
     again. ==== */
  /* PH10: on the phone the card is there only for a day the finger asked
     for (PAPER.place, above), on the side of the screen away from it */
  var phoneKey = null;
  /* UC1 (2026-10-04): THE CARD COMES BACK FROM THE DESK. Tommy: "file card
     disappears during unfold and only reappears upon touching a day on the
     map". The card that lay beside the folded packet is put away for the
     unfold's travel (FJ1) and comes back to its corner as the sheet lands,
     reading what it read on the desk, until a tap puts it away as it puts a
     tapped day's away (engine.js, a tap off the record or off the plate). */
  var deskCard = false;
  /* TC1: THE TWO-CARD ONES. On the phone on its side the pit's card and the sea's take two cards at the reading
     size: the pit's head and first line on the front and its second line (the clue) behind; the sea's way in and
     London on the front and the list of the places to chart behind. `cardPage` is which is on top; a new card
     always comes up on its front. The words are shown and hidden by styles.css (TC1), so paint is untouched. */
  var cardPage = 0, twoKey = '';
  /* VL1: the voyage's card under way carries the eight places and a foot line, and at 1.6 it ran 20 css px past
     the stock: it is set at 1.45, still well over the 6 px it was before TC1 */
  function typeK() { return dom.el && dom.el.getAttribute('data-two') === 'voy' ? 1.45 : 1.6; }
  function twoCard() {
    if (!PHONE || !dom.el) return;
    var c = PAPER.card, L = S.layout, kind = '';
    if (L && L.side && !c.phoneAway && !sheetFolded() && !presented && !held) {
      if (doomOn) kind = 'pit';
      else if (punch && punch.__on) { var Vk = voy(), vk = Vk ? Vk.state() : null; kind = vk && !voyRest(vk) ? 'voy' : 'sea'; }   /* VL1 */
    }
    /* OC1 (2026-10-07): THE SEA'S IS ONE CARD. Tommy: "Outside of the voyage, all you need is one card with a
       hint, right?" At rest it is the way in and London; the list of places is the voyage's, and is not on it. */
    var two = kind === 'pit';
    var k = kind + '|' + doomOn;
    if (k !== twoKey) { twoKey = k; cardPage = 0; }
    if (c.second !== two) { c.second = two; if (H.invalidate) H.invalidate(); }
    if ((dom.el.getAttribute('data-two') || '') !== kind) {
      dom.el.setAttribute('data-two', kind);
      if (PAPER.card.dw) dom.el.style.setProperty('--dck', (PAPER.card.dw / 320 * typeK()).toFixed(4));   /* VL1 */
    }
    var pg = String(cardPage);
    if (dom.el.getAttribute('data-page') !== pg) dom.el.setAttribute('data-page', pg);
  }
  function phoneCard() {
    /* EG1: terra's card and the pit's are cards a tap asked for, as a day's is */
    /* EG3: and the card beside the folded packet (FU1), whose back holds the
       hunt's hints, is on the desk as it is at a desk */
    /* CS1: while the sheet moves the card is on the desk, as at a desk (the
       fold moves it: foldSlip) */
    var O0 = root.RoomObjects, folding = !!(O0 && O0.foldOn && O0.foldT > 0);
    if (sheetFolded()) deskCard = true;                                                  /* UC1 */
    /* SC1 (2026-10-07): AND THE SEA'S. Tommy: "I'm sure we've got a hint card somewhere that hints you towards
       the voyage ... we got it on desktop and not mobile? ... I asked you to port from desktop to mobile." At a
       desk the hand over the open sea west of the record, the boat at rest, turns the card to the voyage's
       ("the record starts in 2022. what came before lies out to sea", paint's `onPunch`); the phone painted it
       and never brought it up. A finger's tap on that sea is the phone's hand there: ON the plate, in the
       record's seven rows, as terra's (a tap on the paper above it reads a point too, and puts the card away) */
    var V0 = voy(), vs0 = V0 ? V0.state() : null, fr0 = S.fld && S.fld.front;
    var r0 = lastPtrY === null ? -1 : Math.floor((lastPtrY - C.py) / C.ch) - C.EDGE_ROWS;
    var seaOn = lastPtrX !== null && !!fr0 && lastPtrX < fr0.west && r0 >= 0 && r0 <= 6 && !!vs0 && voyRest(vs0);
    /* VL1 (2026-10-07): AND WHILE SHE SAILS the card is up in its corner with the places to chart. Tommy: "it should
       always show in the corner" (the phone had no card at all under way, so the list was never seen) */
    var sailOn = !!vs0 && !voyRest(vs0);
    var c = PAPER.card, away = !folding && !held && !presented && !terraOn && !doomOn && !seaOn && !sailOn && !sheetFolded() && !deskCard, at = null, L = S.layout;
    if (c.phoneAway && !away && folding) c.phoneDealIn = true;                             /* CS1 */
    var d = presented ? presented.date : held;
    if (d && L && root.Room && Room.toScreen) {
      var cl = cellOf(d), p = cl ? Room.toScreen(cl[0], cl[1]) : null;
      if (p) at = [p[0], p[1]];
    }
    /* CC1: the card moves only for a day under its corner, so only a day
       near the top right is placed again as it moves */
    var near = at && at[0] > L.cw - 320 && at[1] < 240;
    var key = away + '|' + sheetFolded() + '|' + folding + '|' + (near ? (at[0] >> 3) + ',' + (at[1] >> 3) : '-');
    c.phoneAway = away;
    if (key === phoneKey) { twoCard(); return; }   /* TC1 */
    phoneKey = key; c.phoneAt = at;
    PAPER.place(); PAPER.layoutDom();
    twoCard();
    if (H.invalidate) H.invalidate();
  }
  function paint() {
    /* THE CARD IS NEVER EMPTY. At rest it reads the day the survey was
       carried to — the front — so it teaches what it is by being what it
       is. Blank chrome is not a thing a printed sheet has.

       V1 (5): unless she has cast off, in which case at rest it is the
       PUNCH CARD. A day under the hand still wins. */
    paints++;
    if (PHONE) phoneCard();
    var V5 = voy();
    var vs5 = V5 ? V5.state() : null;
    /* CW1: under way anywhere, or west of the record in any state */
    var onPunch = !!(vs5 && !sheetFolded() && (!voyRest(vs5) || westOfRecord()) &&
                     !presented && !held);   /* PC1: arrived is at rest; never folded */
    if (onPunch) {
      awayNow = awayOf(vs5);
      topNow = topOf(vs5);
      londonNow = londonLine(vs5);
      punchKey = punchKeyOf(vs5);
    } else { awayNow = ''; topNow = ''; londonNow = ''; punchKey = ''; }
    showPunch(onPunch);
    if (PHONE) twoCard();   /* TC1 */
    if (onPunch) {
      /* NC1: the voyage's card carries no list. CF3: it is the FRONT that
         carries the punch; the list is the back's and stays there. */
      drawPunch(vs5);
      set(dom.state, '');
      if (state !== 'punch' && dom.el) {
        state = 'punch';
        dom.el.setAttribute('data-state', 'punch');
      }
      H.invalidate();
      return;
    }
    var date = held || S.fld.days.surveyedTo || S.fld.today;
    /* D2 (2): the hand out on the conjecture, with nothing held, gets
       TERRA'S card and not the front's */
    var east = !presented && !held && handEast();
    /* D2 (5): on the pit's disc the card is the pit's, whichever lattice
       row the hand is on — the disc reaches past the seven rows, and a
       hollow is not a day. `doomOn` is what voyTick compares against. */
    var doomK = east ? doomKey() : '';
    var terra = east ? (doomK || terraDate()) : null;
    terraOn = !!terra; doomOn = doomK; terraKey = terra || '';
    if (PHONE) twoCard();   /* TC1 */
    /* NL1: and with nothing under the hand and nothing out east, the
       card is the DAY'S OWN RECORD, folded or not — N2's memorandum
       stood here and the note's list carries the hints now. */
    /* NC1 (2026-09-22): AND ON A FOLDED SHEET IT IS THE MEMORANDUM — the
       ruled list of the six (THE MEMORANDUM, above), behind CR1's gate:
       nothing under the hand, nothing presented, nothing out east, and
       the paper in eight. Tommy: "that was meant to be on the file
       cards, not the hidden cards." */
    /* CF3: never on the front now: the memorandum is the card's back */
    var memo = false;
    var st = presented ? 'present' : (held ? 'hover' : 'rest');
    var drawKey = presented ? ['present', presented.date, presented.title,
      presented.story, presented.detail].join('|')
      : (terra ? 'terra|' + (S.foreign ? 'F|' : '') + terra : (memo ? 'memo' : date));
    if (drawKey !== drawn) {
      drawn = drawKey;
      if (presented) {
        set(dom.date, dayLine(presented.date));
        set(dom.fig, presented.title || '');
        set(dom.level, presented.story || '');
        set(dom.settle, presented.detail || '');
        if (dom.btn) {
          set(dom.btnT, RoomCopy.t('card.button_put_down'));
          set(dom.btnGo, '');
          dom.btn.hidden = false; dom.btn.__date = null; dom.btn.__present = 1;
        }
      } else if (doomK) {
        /* D2 (5): the pit's card — a head and two lines, no date, no
           figure; the second line rides on the level's row */
        set(dom.date, (doomK === 'doom1' ? DOOM_HEAD_DONE : DOOM_HEAD) + ' \u2014 ' + DOOM_WHEN);
        set(dom.fig, '');
        var dl1 = doomK === 'doom1' ? DOOM_LINE_DONE : DOOM_LINE, dl2 = doomK === 'doom1' ? DOOM_LINE_DONE_2 : DOOM_LINE_2;
        /* TC1: on the phone the pit's card is two cards and its first line is two sentences: the first stays on the
           front under the head, the second goes behind with the clue (the words are the same) */
        var dcut = PHONE ? dl1.indexOf('. ') : -1;
        if (dcut > 0) { dl2 = dl1.slice(dcut + 2) + ' ' + dl2; dl1 = dl1.slice(0, dcut + 1); }
        set(dom.level, dl1);
        set(dom.settle, dl2);
        if (dom.btn && !dom.btn.hidden) { dom.btn.hidden = true; dom.btn.__date = null; }
      } else if (terra) {
        /* D2 (2): the sheet's own voice about ground it has not been on —
           what it is, and one line pointing
           east. No figure and no level: there is nothing counted here. */
        set(dom.date, TERRA_HEAD);   /* no date after it: Tommy, 2026-09-26, "why even show the exact date after?" */
        set(dom.fig, '');
        set(dom.level, '');
        set(dom.settle, S.foreign ? TERRA_FOREIGN : EAST_LINE);   /* TF1 */
        if (dom.btn && !dom.btn.hidden) { dom.btn.hidden = true; dom.btn.__date = null; }
      } else if (memo) {
        /* NC1: the list is the whole of it — the day's rows are emptied
           and the list laid over the stock's ruling (memoOn, below) */
        set(dom.date, '');
        set(dom.fig, '');
        set(dom.level, '');
        set(dom.settle, '');
        if (dom.btn && !dom.btn.hidden) { dom.btn.hidden = true; dom.btn.__date = null; }
      } else {
        set(dom.date, dayLine(date));
        set(dom.fig, figLine(date));
        set(dom.level, '');                    /* LV1 */
        /* SL1 (2026-10-07): NO SETTLED LINE. Tommy, of "Village, settled 11 May to 7 August 2026 · thirteen weeks
           of directed days": "I kind of hate it anyway ... Kill it". It named a building the flat sheet does not draw
           (ST1) in the code's own words, and it was the line that overran the card on the phone */
        set(dom.settle, '');
        var p = markOn(date);
        if (dom.btn) {
          dom.btn.__present = 0;
          /* RP1 (2026-10-07): no 'read the post'. Tommy: "let's drop 'read the post'": the bench mark itself opens
             the essay (BK2), and the line was what overran the card on the phone; the essay's name stays and is still the door */
          set(dom.btnGo, '');
          if (p) {
            set(dom.btnT, p.numeral + ' — ' + p.title);   /* RP2: the numeral and the title (Tommy: "Drop benchmark") */
            dom.btn.hidden = false; dom.btn.__date = p.date;
          } else if (!dom.btn.hidden) { dom.btn.hidden = true; dom.btn.__date = null; }
        }
      }
    }
    /* ==== R24 (b): NO WORDS ABOUT THE STATE. Tommy: "we should drop
       keyboard keys like 'esc to release' and the word 'pinned',
       remember this is physical, not a computer!" What is left on this
       line is a sentence about the DAY and not about the interface.
       CW1: V1 (5) put the voyage's way in on THIS line — the 9.2 px grey
       row above the head rule — and left the day printed underneath it.
       The way in is on the voyage's card now, under the rule, where the
       day's lines were.
       NL1: N2 put the memorandum's own head here and took it away
       again; what is on this line is the survey front's line once more,
       in every state but a card in the hand. */
    /* NC1: and on the folded sheet's memorandum this row is its head,
       the one row on this card allowed to say what the card IS */
    var note = presented || held ? ''
      : RoomCopy.t(memo ? 'card.hint_head' : 'card.state_front');
    memoOn(true);                       /* CF3: the back always carries the list */
    if (st !== state && dom.el) {
      state = st;
      dom.el.setAttribute('data-state', st);
    }
    set(dom.state, note);
    H.invalidate();
  }

  /* ==== BM1 (2026-09-29): THE HAND ON A BENCH MARK READS ITS DAY. Tommy:
     "the hover for the blog post is quite 'low' vs the icon?" It was: the
     plate is an upright card built in css px ABOVE its anchor (marks.js
     SPR_BODY, BOX.bm), and on a raised sheet the anchor stands on the
     water's datum where the day is under the sea, so the ray through the
     drawn arrow went on to the seabed and read a day a fortnight on
     (#176,347,489,53,-104: the arrow at (542, 456) read 2026-01-25, its
     own day answered only from y 470 down). So the pointer is tested
     against every bench mark's DRAWN symbol first — the anchor projected
     at the height the sprite program stands it at (max(ground, 0), the
     datum over the sea), the broad arrow and its numeral in the plate's
     own art units at BOX.bm's 0.95 — and inside one it is that mark's
     day. Everywhere else the pick is the ground's, as it was. ==== */
  var BM_K = 0.95;                      /* marks.js BOX.bm.k: css px per art unit */
  function benchAt(cx, cyp) {
    var M = S.marks && S.fld && S.fld.marks;   /* S.marks is null under ?marks=0: nothing drawn */
    if (!M || !M.plates) return null;
    var L = S.layout, F = null, V = S.VP, best = null, bd = Infinity;
    for (var i = 0; i < M.plates.length; i++) {
      var P = M.plates[i];
      if (P.sym !== 'bm') continue;
      F = F || frame();
      var p = mapToSheet(P.x, P.y, Math.max(heightAt(P.x, P.y), 0), F);
      var wx = L.ox + p[0] * L.k, wy = -(L.oy + p[1] * L.k), wz = p[2] * L.k;
      var cw = V[3]*wx + V[7]*wy + V[11]*wz + V[15];
      if (!(cw > 0)) continue;
      var sx = ((V[0]*wx + V[4]*wy + V[8]*wz + V[12]) / cw * 0.5 + 0.5) * L.cw;
      var sy = (0.5 - (V[1]*wx + V[5]*wy + V[9]*wz + V[13]) / cw * 0.5) * L.ch;
      /* the arrow and its cut span -8.5..8.5 by -9..4 with a 2.2 halo;
         the numeral is set from 10.5 at 8 px, about 5.5 a letter */
      var x1 = (P.text ? 11.5 + 5.5 * P.text.length : 11) * BM_K;
      var dx = cx - sx, dy = cyp - sy;
      if (dx < -11 * BM_K || dx > x1 || dy < -12.5 * BM_K || dy > 6.5 * BM_K) continue;
      var date = dayAtMap(P.x, P.y);
      if (!date) continue;
      var dd = dx * dx + (dy + 2.5 * BM_K) * (dy + 2.5 * BM_K);
      if (dd < bd) { bd = dd; best = { date: date, x: P.x, y: P.y }; }
    }
    return best;
  }

  /* ==== LH2 (2026-09-30): THE HAND ON A LANDMARK READS ITS DAY. Tommy, at
     #198,324,82,9,-28: "the actual card that says Sizewell B is out in the
     sea and not where the actual building is on the map? Is this due to us
     moving the building to nearby land?" It was: a holiday's day is sea,
     standingPlace (marks.js) walks its landmark to the nearest shore, and
     the pick read the ground under the dome — a day in the next week —
     while "Sizewell B" answered only over the water. So after the bench
     marks, every landmark that stands for a day (marks.js `date`) is
     tested where it is DRAWN: its building from footing to roof (a
     capsule round the projected axis, the reach wide) while the building
     is on the sheet, and its glyph in BOX.lm's art box (k 1.85 css px a
     unit) while its generation is. Inside one, it is that landmark's day. */
  var LM_K = 1.85, LM_BOX = [-15, -14, 15, 9];   /* marks.js BOX.lm */
  function projCss(mx, my, h, F) {
    var L = S.layout, V = S.VP;
    var p = mapToSheet(mx, my, h, F);
    var wx = L.ox + p[0] * L.k, wy = -(L.oy + p[1] * L.k), wz = p[2] * L.k;
    var cw = V[3]*wx + V[7]*wy + V[11]*wz + V[15];
    if (!(cw > 0)) return null;
    return [((V[0]*wx + V[4]*wy + V[8]*wz + V[12]) / cw * 0.5 + 0.5) * L.cw,
            (0.5 - (V[1]*wx + V[5]*wy + V[9]*wz + V[13]) / cw * 0.5) * L.ch];
  }
  function landmarkAt(cx, cyp) {
    var M = S.marks && S.fld && S.fld.marks;
    if (!M || !M.landmarks) return null;
    var fd = S.marks.fade ? S.marks.fade() : [1, 1];
    var rel = reliefOf(S.tilt), F = null, best = null, bd = Infinity;
    var pxPerMap = (S.portalScr || S.portal).w / S.view.w;   /* MP2 */
    for (var i = 0; i < M.landmarks.length; i++) {
      var Lm = M.landmarks[i];
      if (!Lm.date) continue;
      F = F || frame();
      var g = Math.max(heightAt(Lm.x, Lm.y), 0), d = Infinity;
      /* the building, as marks.js draws it: plan with the near generation,
         solid with the mid one (uAl there) */
      if (Lm.reach && fd[0] * (fd[1] + (1 - fd[1]) * rel) > 0.3) {
        var b0 = projCss(Lm.x, Lm.y, g, F), b1 = projCss(Lm.x, Lm.y, g + (Lm.tall || 0) * rel, F);
        if (b0 && b1) {
          var ax = b1[0] - b0[0], ay = b1[1] - b0[1], ll = ax * ax + ay * ay;
          var t = ll > 0 ? Math.max(0, Math.min(1, ((cx - b0[0]) * ax + (cyp - b0[1]) * ay) / ll)) : 0;
          var qx = cx - (b0[0] + ax * t), qy = cyp - (b0[1] + ay * t), rp = (Lm.reach + 0.5) * pxPerMap;
          var dq = qx * qx + qy * qy;
          if (dq <= rp * rp) d = dq;
        }
      }
      /* the glyph, in its generation, and not where its solid has taken it
         off the raised sheet (marks.js BI1, band + 10) */
      var bandOn = Lm.band === 0 ? 1 : Lm.band === 1 ? fd[0] : Lm.band === 3 ? 1 - fd[1] : fd[1];
      if (d === Infinity && bandOn > 0.5 && !(Lm.solid && rel > 0.25)) {
        var sp = projCss(Lm.x, Lm.y, g, F);
        if (sp) {
          var dx = cx - sp[0], dy = cyp - sp[1];
          if (dx >= LM_BOX[0] * LM_K && dx <= LM_BOX[2] * LM_K && dy >= LM_BOX[1] * LM_K && dy <= LM_BOX[3] * LM_K)
            d = dx * dx + (dy + 2.5 * LM_K) * (dy + 2.5 * LM_K);
        }
      }
      if (d < bd) { bd = d; best = { date: Lm.date, x: Lm.x, y: Lm.y }; }
    }
    return best;
  }

  var api = {
    /* the day under a css point of the canvas */
    dayAt: function (cx, cyp) {
      var m = mapAt(cx, cyp);
      lastPtrX = m ? m.x : null;        /* V1 (5): where the hand is, for westOfRecord */
      lastPtrY = m ? m.y : null;        /* D2 (2): and for Terra Incognita's own card */
      var bm = benchAt(cx, cyp);        /* BM1: the drawn mark first */
      if (bm) return { date: bm.date, x: bm.x, y: bm.y, steps: m ? m.steps : 0, bench: true };
      var lmk = landmarkAt(cx, cyp);    /* LH2: then a landmark that stands for a day */
      if (lmk) return { date: lmk.date, x: lmk.x, y: lmk.y, steps: m ? m.steps : 0, landmark: true };
      if (!m) return null;
      var date = dayAtMap(m.x, m.y) || dayAtEdge(m.x, m.y);
      return date ? { date: date, x: m.x, y: m.y, steps: m.steps } : null;
    },
    mapAt: mapAt,
    /* X5: THE FIELD ITSELF, at a map point, in the field's own units —
       the number `bedAt` above already reads for the pick, published so
       that a lead line can be cast into it. The COAST is at 0 and the
       open sea's floor at -COAST; nothing here scales, masks or
       exaggerates it, because a sounding is a depth and not a picture. */
    bedAt: bedAt,
    /* THE MARCH'S OWN ACCURACY, measured against itself at a step count
       no reader will ever pay for. There is no other ground truth when
       the sheet is raised: the cell a point falls in is exact, but WHICH
       point of the ground is under a pixel is the ray's answer, so the
       reference is the same ray marched 40x as finely. */
    probe: function (cx, cyp, n) { return mapAt(cx, cyp, n); },
    /* the forward map, for the round trip: a map point -> the css pixel
       it is drawn at, through the same VP the shaders use */
    toScreen: function (mx, my, hx, hy) {
      /* (hx, hy), as toClip's: the height read somewhere else — the pace
         arrows measure the next day at the surveyor's own height (PA1) */
      var L = S.layout, F = frame();
      var p = mapToSheet(mx, my, heightAt(hx === undefined ? mx : hx, hy === undefined ? my : hy), F);
      var wx = L.ox + p[0] * L.k, wy = -(L.oy + p[1] * L.k), wz = p[2] * L.k, m = S.VP;
      var cx = m[0]*wx + m[4]*wy + m[8]*wz + m[12];
      var cyy = m[1]*wx + m[5]*wy + m[9]*wz + m[13];
      var cw = m[3]*wx + m[7]*wy + m[11]*wz + m[15];
      if (!cw) return null;
      return [(cx / cw * 0.5 + 0.5) * L.cw, (0.5 - cyy / cw * 0.5) * L.ch];
    },
    /* AU3: the same forward map stopped on the PAPER, in the css frame
       S.portal is in — the window in the paper is cut there (plateClip),
       so a name is inside the window exactly when this is inside it */
    toSheet: function (mx, my) {
      var L = S.layout, F = frame();
      var p = mapToSheet(mx, my, heightAt(mx, my), F);
      return [L.ox + p[0] * L.k, L.oy + p[1] * L.k];
    },
    /* PH1 (2026-09-25): the same forward map, stopped at CLIP space and
       with a lift over the ground (map units of height, the unit heightAt
       answers in), for a line drawn in the air — the pit's line of sight —
       which has to be cut at the camera's own plane before it is divided */
    toClip: function (mx, my, lift, hx, hy) {
      /* (hx, hy): where the height is read, when it is not under the point
         itself — the line's far end hangs over the pit at its LIP's level */
      var L = S.layout, F = frame();
      var p = mapToSheet(mx, my, heightAt(hx === undefined ? mx : hx, hy === undefined ? my : hy) + (lift || 0), F);
      var wx = L.ox + p[0] * L.k, wy = -(L.oy + p[1] * L.k), wz = p[2] * L.k, m = S.VP;
      return [m[0]*wx + m[4]*wy + m[8]*wz + m[12], m[1]*wx + m[5]*wy + m[9]*wz + m[13],
              m[2]*wx + m[6]*wy + m[10]*wz + m[14], m[3]*wx + m[7]*wy + m[11]*wz + m[15]];
    },
    cellOf: cellOf,
    dayAtMap: dayAtMap,
    /* UC1: a tap off the record puts the card from the desk away */
    dropDesk: function () { if (deskCard) { deskCard = false; paint(); } },
    /* TC1: the card behind brought forward (a tap on what shows of it, engine.js); the one in front goes behind */
    turnCard: function () {
      if (!PHONE || !PAPER.card.second) return false;
      cardPage ^= 1; twoCard();
      if (H.invalidate) H.invalidate();
      return true;
    },
    show: function (date) {
      over = date || null;
      if (date) {
        deskCard = false;                       /* UC1: a day takes the card over */
        if (holdT) { clearTimeout(holdT); holdT = null; }
        if (date === held) return;
        held = date; paint();
        return;
      }
      /* N2: the hand is ON the card — the hold is refused outright, so
         the reach from a day to the card's own button keeps the day it
         set out from. */
      if (onCard && held) { if (holdT) { clearTimeout(holdT); holdT = null; } return; }
      /* TT1 (2026-10-07): on the phone a tap is a tap, with no hand reaching for the card, so the day lets go at once.
         Tommy: "when I click Terra Incognita, it takes a second or so" (it waited out the desk's HOLD_MS) */
      if (PHONE && held) { if (holdT) { clearTimeout(holdT); holdT = null; } held = null; if (!terraSync()) paint(); return; }
      if (!held || holdT) { if (!held) { terraSync(); if (PHONE) phoneCard(); } return; }   /* SC1: the sea's card goes too */
      holdT = setTimeout(function () { holdT = null; held = null; paint(); }, HOLD_MS);
    },
    /* N2: IS THE HAND ON THE CARD? The engine asks with the client point
       it has already read for the pick, on every move, before it asks
       for the day — so `show(null)` knows whether the pointer has left
       the day for the card or left them both. The card's DOM box is the
       answer (it is axis-aligned round a card askew by three degrees,
       which errs the generous way, which is the right way for a hand
       reaching for a button). A card that is not being drawn, is turned
       away from the eye, or is in the reader's hand as a discovery is
       not a thing to hold on to. */
    hand: function (px, py) {
      var el = dom.el, on = 0, r;
      if (el && !NO_CARD && !dom.__away && el.style.visibility !== 'hidden' &&
          el.getAttribute('data-discovery') !== '1') {
        r = el.getBoundingClientRect();
        if (r.width && r.height && px >= r.left && px <= r.right &&
            py >= r.top && py <= r.bottom) on = 1;
      }
      onCard = on;
      return !!on;
    },
    showing: function () { return over; },
    /* The voyage may lay one of its already-authored records on this
       physical card. It has priority over the hover, and its dismissal
       is its own. */
    present: function (record) {
      if (!record || !/^\d{4}-\d\d-\d\d$/.test(record.date || '')) return false;
      /* CS1: a card laid over one that is holding the boat carries that
         card's put-down with it, or the boat is held for good */
      if (presented && typeof presented.onDismiss === 'function' && typeof record.onDismiss !== 'function') {
        record = Object.assign({}, record, { onDismiss: presented.onDismiss });
      }
      presented = record; drawn = null;
      var foreground = record.discovery === true;
      document.documentElement.classList.toggle('room-card-present', foreground);
      if (dom.el) {
        if (foreground) {
          dom.el.setAttribute('data-discovery', '1');
          dom.el.setAttribute('role', 'dialog');
          dom.el.setAttribute('aria-modal', 'true');
          dom.el.setAttribute('aria-label', 'New chart discovery');
          /* AL1: a discovery may ask to be held to the corner of the glass
             instead of its middle — the pit's, so the ground it is about
             can be seen standing up beside it (styles.css) */
          if (record.at === 'corner') dom.el.setAttribute('data-discovery-at', 'corner');
          else dom.el.removeAttribute('data-discovery-at');
        } else {
          dom.el.removeAttribute('data-discovery-at');
          dom.el.removeAttribute('data-discovery');
          dom.el.removeAttribute('role');
          dom.el.removeAttribute('aria-modal');
          dom.el.removeAttribute('aria-label');
        }
      }
      paint();
      /* The card has one physical control. Put the keyboard hand on it
         just as the card arrives, without scrolling the room. */
      if (foreground && dom.btn && dom.btn.focus) {
        try { dom.btn.focus({ preventScroll: true }); } catch (e) { dom.btn.focus(); }
      }
      return true;
    },
    presented: function () { return presented; },
    dismiss: function (reason) {
      if (!presented) return false;
      var done = presented.onDismiss;
      presented = null; drawn = null;
      document.documentElement.classList.remove('room-card-present');
      if (dom.el) {
        dom.el.removeAttribute('data-discovery');
        dom.el.removeAttribute('data-discovery-at');
        dom.el.removeAttribute('role');
        dom.el.removeAttribute('aria-modal');
        dom.el.removeAttribute('aria-label');
      }
      if (dom.btn && dom.btn.blur) dom.btn.blur();
      paint();
      /* Only a hand putting the card down may restart a voyage. RESET,
         home and future programmatic teardown use `system` and do not
         masquerade as that hand. MO1 (2026-09-23): the terminal's `sheet
         card off` IS a hand on the button — typed by a reader, or by an
         agent over the link, which has no other way to put a card down —
         so `terminal` hands her back too. */
      /* CS1: the callback hears EVERY put-down, with its reason, and it
         is the voyage that decides which of them hand her back (a teardown
         says 'system'); otherwise a card put down any other way held the
         boat's loop for good. */
      if (typeof done === 'function') done(reason || 'system');
      return true;
    },
    place: function () { PAPER.place(); PAPER.layoutDom(); if (memoShown) memoFit(); },
    stats: function () {
      return { picks: picks.n, marched: picks.marched,
               avgSteps: picks.marched ? +(picks.steps / picks.marched).toFixed(1) : 0,
               msPerPick: picks.n ? +(picks.ms / picks.n).toFixed(4) : 0,
               steps: STEPS, hold: HOLD_MS, card: !NO_CARD, paints: paints };
    },
    resetStats: function () { picks = { n: 0, marched: 0, steps: 0, ms: 0 }; }
  };

  /* ================================================================
     THE DRAWING

     ONE hook. N2 took away the second — `drawGround`, which ran inside
     the window's scissor because the pencil cross and the pinned day's
     marking pin were ON the country. Neither is drawn any more (the
     cross at R24, the pin here), so nothing this module draws belongs
     under the scissor: the card, its cut edges and its shadow are
     objects lying over the whole desk.
     ================================================================ */
  var draws = 0;

  api.drawCard = function () {
    voyTick();                          /* V1 (5): the card follows the boat */
    if (!S.fld || !S.fld.days || NO_CARD) return;
    /* R12: the stock is `D.card`'s and draws itself */
    draws += PAPER.draw();
  };
  api.draws = function () { var n = draws; draws = 0; return n; };
  /* H (2026-09-19): the record can change under the card without a frame
     that draws it (the swap's frames are the reveal's), so the engine asks
     for the punch card to be re-struck outright when the subject flips;
     the key is cleared so `voyTick` cannot think nothing changed. */
  api.restrike = function () { punchKey = null; voyTick(); };

  grabDom();
  PAPER.place();
  paint();
  D.ready = true;
  return api;
};

function readJSON(id) {
  var n = document.getElementById(id);
  try { return n ? JSON.parse(n.textContent) : null; } catch (e) { return null; }
}

})(window);
